import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';

// Axios interceptor rimosso per evitare conflitti
import './App.css';
import LoginPage from './LoginPage';
import AnalyticsPage from './pages/AnalyticsPage';
import MenuPage from './pages/MenuPage';
import DDTPage from './pages/DDTPage';
import FBCostPage from './pages/FBCostPage';
import HACCPPage from './pages/HACCPPage';
import OrdiniPage from './pages/OrdiniPage';
import GestioneBollePage from './pages/GestioneBollePage';
import TeamPage from './pages/TeamPage';
import ResiFornitorePage from './pages/ResiFornitorePage';
import SDIConfigPage from './pages/SDIConfigPage';
import CassettoFiscalePage from './pages/CassettoFiscalePage';
import SaaSAdminPage from './pages/SaaSAdminPage';
import TenantApp from './pages/TenantApp';
import LandingPage from './pages/LandingPage';
import ControlPanelPage from './pages/ControlPanelPage';
import NotificationPrompt from './components/NotificationPrompt';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { AlertCircle, Euro, CreditCard, Coins, Receipt, TrendingUp, Calendar, Trash2, Smartphone, Target, CheckCircle, XCircle, Lock, Gift, LogOut, RotateCcw, Plus, Building2, ShoppingBag, Edit, MessageCircle, Clock, FileText, Save, ArrowLeft, Download, Upload, Home, BarChart3, Mail, Settings, Menu, X, PieChart, ShoppingCart, DollarSign, Loader2, FileSearch, Truck, Package, RefreshCw, ChevronDown, ChevronUp, ChevronRight, Eye, Filter, Search, Users, MapPin, ClipboardList, ShieldCheck, ListTodo, Store, Sparkles, AlertTriangle, Shield, Crown } from 'lucide-react';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

import LottoPubblicoPage from './pages/LottoPubblicoPage';
import CartellaIspettorePage from './pages/CartellaIspettorePage';
import RegistroAnomaliePage from './pages/RegistroAnomaliePage';
import PublicCartaViniPage from './pages/PublicCartaViniPage';
import { API } from './utils/api';

// Utility per verificare se il token è valido (versione semplificata per compatibilità)
const isTokenValid = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

// Funzione globale per debug da console (solo in development)
window.debugInitialCash = async () => {
  try {
    const token = localStorage.getItem('token');
    const API = `${window.location.protocol}//${window.location.host}/api`;
    
    console.log('🔍 Debug: Checking initial cash...');
    console.log('🔍 API URL:', API);
    console.log('🔍 Token available:', !!token);
    
    // Test with cache busting
    const cacheBuster = `?debug=${Date.now()}`;
    const response = await fetch(`${API}/initial-cash${cacheBuster}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    
    console.log('🔍 Response status:', response.status);
    const data = await response.json();
    console.log('🔍 Response data:', data);
    
    if (data.importo === 2060) {
      console.log('✅ API returns correct value: €2060');
    } else {
      console.log('❌ API returns wrong value:', data.importo);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Debug error:', error);
    return error;
  }
};

window.forceFixCash = async () => {
  try {
    const token = localStorage.getItem('token');
    const API = `${window.location.protocol}//${window.location.host}/api`;
    
    console.log('🔄 Forcing cash update...');
    const response = await fetch(`${API}/force-update-initial-cash`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    console.log('🔄 Force update result:', data);
    
    if (response.ok) {
      console.log('✅ Cash updated successfully, reloading page...');
      setTimeout(() => window.location.reload(), 1000);
    }
    
    return data;
  } catch (error) {
    console.error('❌ Force update error:', error);
    return error;
  }
};

console.log('🔧 Debug functions available:');
console.log('- window.debugInitialCash() - Check current API value');
console.log('- window.forceFixCash() - Force update to €2060');

// Settings Component per Administrator
const SettingsPage = ({ onBack }) => {
  const [settings, setSettings] = useState({
    initial_cash: 2060.0,
    whatsapp_number: '393453595804',
    company_name: 'Sistema Gestione Cassa',
    admin_email: 'admin@ristorante.com',
    // Nuovi dati societari
    ragione_sociale: '',
    partita_iva: '',
    codice_fiscale: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: '',
    telefono: '',
    pec: '',
    codice_sdi: ''
  });
  const [activeSettingsTab, setActiveSettingsTab] = useState('generale'); // generale, societari, team, integrazioni, backup
  const APP_VERSION = '1.00';
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [backupFile, setBackupFile] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [restoreMode, setRestoreMode] = useState('replace');
  
  // Stati per importazione email/Excel
  const [emailImportLoading, setEmailImportLoading] = useState(false);
  const [articoliFile, setArticoliFile] = useState(null);
  const [finanziarioFile, setFinanziarioFile] = useState(null);
  const [showImportResults, setShowImportResults] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // Stati per configurazione email IMAP
  const [emailConfig, setEmailConfig] = useState({
    email: '',
    password: '',
    enabled: false
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [syncingEmails, setSyncingEmails] = useState(false);

  // Stati per configurazione Aruba
  const [arubaConfig, setArubaConfig] = useState({
    username: '',
    password: '',
    configured: false
  });
  const [arubaConfigLoading, setArubaConfigLoading] = useState(false);
  const [testingAruba, setTestingAruba] = useState(false);

  // Stati per categorizzazione automatica
  const [categorizationScheduler, setCategorizationScheduler] = useState(null);
  const [categorizationStatus, setCategorizationStatus] = useState(null);

  // Stati per gestione file importati
  const [emailImports, setEmailImports] = useState([]);
  const [loadingImports, setLoadingImports] = useState(false);
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedMonths, setExpandedMonths] = useState({});
  const [deletingImport, setDeletingImport] = useState(null);

  // Carica impostazioni attuali
  useEffect(() => {
    loadSettings();
    loadEmailConfig();
    loadArubaConfig();
    loadCategorizationInfo();
    loadEmailImports();
  }, []);

  const loadEmailImports = async () => {
    try {
      setLoadingImports(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email-imports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmailImports(response.data || []);
    } catch (error) {
      console.error('Error loading email imports:', error);
    } finally {
      setLoadingImports(false);
    }
  };

  const deleteEmailImport = async (importId, reportDate) => {
    if (!window.confirm(`Sei sicuro di voler eliminare i dati del ${reportDate}?`)) {
      return;
    }
    try {
      setDeletingImport(importId);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/email-imports/${importId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEmailImports(prev => prev.filter(imp => imp.id !== importId));
    } catch (error) {
      console.error('Error deleting import:', error);
      alert('Errore durante l\'eliminazione');
    } finally {
      setDeletingImport(null);
    }
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const toggleMonth = (yearMonth) => {
    setExpandedMonths(prev => ({ ...prev, [yearMonth]: !prev[yearMonth] }));
  };

  // Raggruppa importazioni per anno e mese
  const groupedImports = emailImports.reduce((acc, imp) => {
    if (!imp.report_date) return acc;
    const [year, month] = imp.report_date.split('-');
    if (!acc[year]) acc[year] = {};
    if (!acc[year][month]) acc[year][month] = [];
    acc[year][month].push(imp);
    return acc;
  }, {});

  const loadCategorizationInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const [schedulerRes, statusRes] = await Promise.all([
        axios.get(`${API}/aruba/categorizzazione/scheduler-info`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API}/aruba/categorizzazione/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      setCategorizationScheduler(schedulerRes.data);
      setCategorizationStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading categorization info:', error);
    }
  };

  const loadArubaConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/aruba-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setArubaConfig(response.data);
    } catch (error) {
      console.error('Error loading Aruba config:', error);
    }
  };

  const saveArubaConfig = async () => {
    if (!arubaConfig.username) {
      toast.error('Username obbligatorio');
      return;
    }
    
    setArubaConfigLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/aruba-config`, arubaConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('✅ Configurazione Aruba salvata!');
      await loadArubaConfig();
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setArubaConfigLoading(false);
    }
  };

  const testArubaConnection = async () => {
    setTestingAruba(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/aruba-config/test`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('✅ ' + response.data.message);
      } else {
        toast.error('❌ ' + response.data.message);
      }
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingAruba(false);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [cashResponse, configResponse, datiSocietariResponse] = await Promise.all([
        axios.get(`${API}/initial-cash`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: { importo: 2060.0 } })),
        axios.get(`${API}/system-config`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${API}/gestione/dati-societari`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: {} }))
      ]);

      setSettings(prev => ({
        ...prev,
        initial_cash: cashResponse.data.importo || 2060.0,
        ...configResponse.data,
        // Dati societari
        ragione_sociale: datiSocietariResponse.data.ragione_sociale || '',
        partita_iva: datiSocietariResponse.data.partita_iva || '',
        codice_fiscale: datiSocietariResponse.data.codice_fiscale || '',
        indirizzo: datiSocietariResponse.data.indirizzo || '',
        cap: datiSocietariResponse.data.cap || '',
        citta: datiSocietariResponse.data.citta || '',
        provincia: datiSocietariResponse.data.provincia || '',
        telefono: datiSocietariResponse.data.telefono || '',
        pec: datiSocietariResponse.data.pec || '',
        codice_sdi: datiSocietariResponse.data.codice_sdi || ''
      }));
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Salva contanti iniziali
      await axios.post(`${API}/update-initial-cash`, {
        importo: parseFloat(settings.initial_cash)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Salva altre configurazioni
      await axios.post(`${API}/system-config`, {
        whatsapp_number: settings.whatsapp_number,
        company_name: settings.company_name,
        admin_email: settings.admin_email
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Salva dati societari
      await axios.put(`${API}/gestione/dati-societari`, {
        ragione_sociale: settings.ragione_sociale || '',
        partita_iva: settings.partita_iva || '',
        codice_fiscale: settings.codice_fiscale || '',
        indirizzo: settings.indirizzo || '',
        cap: settings.cap || '',
        citta: settings.citta || '',
        provincia: settings.provincia || '',
        telefono: settings.telefono || '',
        pec: settings.pec || '',
        codice_sdi: settings.codice_sdi || ''
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('🎉 Impostazioni salvate con successo!');
      
      // Ricarica la pagina per applicare le modifiche
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Errore nel salvare le impostazioni: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    const confirmReset = window.confirm(
      'Vuoi ripristinare tutte le impostazioni ai valori predefiniti?\n\n' +
      'Questa operazione:\n' +
      '• Ripristinerà i contanti a €2060\n' +
      '• Resetterà il numero WhatsApp\n' +
      '• Ripristinerà nome azienda e email\n\n' +
      'Vuoi continuare?'
    );

    if (confirmReset) {
      setSettings({
        initial_cash: 2060.0,
        whatsapp_number: '393453595804',
        company_name: 'Sistema Gestione Cassa',
        admin_email: 'admin@ristorante.com'
      });
    }
  };

  const downloadBackup = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/backup`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Crea un file JSON da scaricare
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      // Crea nome file con data/ora
      const now = new Date();
      const filename = `backup_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}.json`;
      
      // Crea link temporaneo e scarica
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('✅ Backup scaricato con successo!');

    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Errore nel creare il backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        toast.error('Per favore seleziona un file JSON valido');
        return;
      }
      setBackupFile(file);
      setShowRestoreDialog(true);
    }
  };

  const restoreBackup = async () => {
    if (!backupFile) {
      toast.error('Seleziona un file di backup');
      return;
    }

    if (!restorePassword) {
      toast.error('Inserisci la password amministratore');
      return;
    }

    // Conferma finale
    const confirmRestore = window.confirm(
      `⚠️ ATTENZIONE: Ripristino Backup\n\n` +
      `Modalità: ${restoreMode === 'replace' ? 'SOSTITUISCI TUTTO' : 'AGGIUNGI AI DATI ESISTENTI'}\n\n` +
      `${restoreMode === 'replace' ? 
        'TUTTI I DATI ATTUALI SARANNO CANCELLATI E SOSTITUITI CON QUELLI DEL BACKUP!' : 
        'I dati del backup saranno aggiunti a quelli esistenti.'}\n\n` +
      `Questa operazione NON PUÒ ESSERE ANNULLATA!\n\n` +
      `Vuoi continuare?`
    );

    if (!confirmRestore) {
      return;
    }

    setRestoreLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Leggi il file JSON
      const fileContent = await backupFile.text();
      const backupData = JSON.parse(fileContent);

      // Invia al server
      const response = await axios.post(`${API}/restore`, {
        backup_data: backupData,
        password: restorePassword,
        mode: restoreMode
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('🎉 Backup ripristinato con successo!');
      
      // Chiudi dialog e ricarica pagina
      setShowRestoreDialog(false);
      setBackupFile(null);
      setRestorePassword('');
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error restoring backup:', error);
      let errorMessage = 'Errore nel ripristinare il backup';
      
      if (error.response?.status === 403) {
        errorMessage = 'Password non corretta';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setRestoreLoading(false);
    }
  };

  // Funzioni per importazione email/Excel
  const handleArticoliFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
        toast.error('Seleziona un file Excel valido (.xls o .xlsx)');
        return;
      }
      setArticoliFile(file);
      toast.success(`File articoli selezionato: ${file.name}`);
    }
  };

  const handleFinanziarioFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xls') && !file.name.endsWith('.xlsx')) {
        toast.error('Seleziona un file Excel valido (.xls o .xlsx)');
        return;
      }
      setFinanziarioFile(file);
      toast.success(`File finanziario selezionato: ${file.name}`);
    }
  };

  const uploadAndAnalyzeExcel = async () => {
    if (!articoliFile || !finanziarioFile) {
      toast.error('Seleziona entrambi i file Excel prima di procedere');
      return;
    }

    const confirmUpload = window.confirm(
      '📧 IMPORTAZIONE DATI DA EMAIL\n\n' +
      'Stai per caricare e analizzare automaticamente i file Excel ricevuti via email:\n\n' +
      `• ${articoliFile.name}\n` +
      `• ${finanziarioFile.name}\n\n` +
      'L\'AI analizzerà automaticamente:\n' +
      '- Articoli venduti (quantità, importi)\n' +
      '- Gestione finanziaria (Altro, Fiscale, Fatture)\n\n' +
      'Vuoi procedere?'
    );

    if (!confirmUpload) {
      return;
    }

    setEmailImportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('articoli_file', articoliFile);
      formData.append('finanziario_file', finanziarioFile);

      const response = await axios.post(`${API}/upload-excel`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setImportResults(response.data);
        setShowImportResults(true);
        toast.success('🎉 File analizzati con successo dall\'AI!');
        
        // Reset file selection
        setArticoliFile(null);
        setFinanziarioFile(null);
      } else {
        toast.error('Errore nell\'analisi: ' + response.data.error);
      }

    } catch (error) {
      console.error('Error uploading/analyzing files:', error);
      toast.error('Errore nell\'upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmailImportLoading(false);
    }
  };

  // Funzioni per configurazione email IMAP
  const loadEmailConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setEmailConfig(response.data);
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const saveEmailConfig = async () => {
    if (!emailConfig.email) {
      toast.error('Inserisci l\'indirizzo email');
      return;
    }

    if (!emailConfig.password || emailConfig.password === '********') {
      toast.error('Inserisci la password per app Gmail');
      return;
    }

    setEmailConfigLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email-config`, emailConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('✅ Configurazione email salvata!');
        await loadEmailConfig();
      }
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error('Errore nel salvataggio: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const testEmailConnection = async () => {
    setTestingConnection(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email-config/test`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`✅ Connessione riuscita! ${response.data.total_messages} email nella casella`);
      } else {
        toast.error('❌ Connessione fallita: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Errore nel test: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingConnection(false);
    }
  };

  const syncEmailsNow = async () => {
    const confirmSync = window.confirm(
      '📧 SINCRONIZZAZIONE EMAIL\n\n' +
      'Stai per:\n' +
      '1. Connetterti alla casella Gmail configurata\n' +
      '2. Cercare nuove email\n' +
      '3. Scaricare e analizzare gli allegati\n\n' +
      'La sincronizzazione avverrà in BACKGROUND.\n' +
      'Puoi continuare a usare l\'app.\n\n' +
      'Vuoi procedere?'
    );

    if (!confirmSync) {
      return;
    }

    setSyncingEmails(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Avvia in background
      const response = await axios.post(`${API}/sync-emails`, {}, { headers });

      if (response.data.success) {
        toast.info('Sincronizzazione avviata in background...');
        
        // Polling per controllare lo stato
        const checkStatus = async () => {
          try {
            const statusRes = await axios.get(`${API}/sync-emails/status`, { headers });
            
            if (statusRes.data.is_running) {
              toast.loading(statusRes.data.progress || 'Elaborazione...', { id: 'email-sync-settings' });
              setTimeout(checkStatus, 2000);
            } else {
              toast.dismiss('email-sync-settings');
              setSyncingEmails(false);
              
              if (statusRes.data.result?.success) {
                const { articoli_imported, finanziario_imported, message } = statusRes.data.result;
                toast.success(`🎉 ${message || 'Sincronizzazione completata'}`);
              } else {
                toast.error(statusRes.data.result?.error || 'Errore durante la sincronizzazione');
              }
            }
          } catch (e) {
            console.error('Error checking status:', e);
            setSyncingEmails(false);
            toast.dismiss('email-sync-settings');
          }
        };
        
        setTimeout(checkStatus, 1000);
      } else {
        setSyncingEmails(false);
        toast.error(response.data.error || 'Errore durante l\'avvio');
      }
    } catch (error) {
      console.error('Error syncing emails:', error);
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
      setSyncingEmails(false);
    }
  };

  // Helper functions
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 p-6">
        <div className="w-full lg:max-w-4xl lg:mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p>Caricamento impostazioni...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-3 lg:p-6 min-h-[calc(100vh-200px)] lg:min-h-screen">
      <div className="w-full lg:max-w-4xl lg:mx-auto">
        {/* Header - hidden on mobile since we have navbar */}
        <div className="hidden lg:block bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                onClick={onBack}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Torna alla Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">⚙️ Impostazioni Sistema</h1>
                <p className="text-gray-600">Configura i parametri dell'applicazione</p>
              </div>
            </div>
            <div className="text-right">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                v{APP_VERSION}
              </span>
            </div>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 flex gap-2 flex-wrap">
          {[
            { id: 'generale', label: '⚙️ Generale', icon: Settings },
            { id: 'societari', label: '🏢 Dati Societari', icon: Building2 },
            { id: 'team', label: '👥 Team', icon: Users },
            { id: 'integrazioni', label: '🔗 Integrazioni', icon: Sparkles },
            { id: 'backup', label: '💾 Backup', icon: Download }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSettingsTab(tab.id)}
              className={`flex-1 min-w-[120px] px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeSettingsTab === tab.id
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB: Generale */}
        {activeSettingsTab === 'generale' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          
          {/* Impostazioni Finanziarie */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-800">
                <Euro className="h-5 w-5" />
                Impostazioni Finanziarie
              </CardTitle>
              <CardDescription>
                Configura i valori finanziari del sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="initial_cash">Contanti Disponibili Iniziali</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="initial_cash"
                    type="number"
                    step="0.01"
                    min="0"
                    value={settings.initial_cash}
                    onChange={(e) => setSettings(prev => ({ ...prev, initial_cash: e.target.value }))}
                    placeholder="2060.00"
                  />
                  <span className="text-sm text-gray-600">€</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Importo base per il calcolo dei contanti disponibili in cassa
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Impostazioni WhatsApp */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-600">
                <MessageCircle className="h-5 w-5" />
                Impostazioni WhatsApp
              </CardTitle>
              <CardDescription>
                Configura l'integrazione WhatsApp
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="whatsapp_number">Numero WhatsApp</Label>
                <Input
                  id="whatsapp_number"
                  type="text"
                  value={settings.whatsapp_number}
                  onChange={(e) => setSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                  placeholder="393453595804"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Numero di telefono per l'invio automatico dei report (senza +)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Impostazioni Azienda */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600">
                <Building2 className="h-5 w-5" />
                Informazioni Azienda
              </CardTitle>
              <CardDescription>
                Personalizza le informazioni dell'azienda
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company_name">Nome Azienda</Label>
                <Input
                  id="company_name"
                  type="text"
                  value={settings.company_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Sistema Gestione Cassa"
                />
              </div>
              <div>
                <Label htmlFor="admin_email">Email Amministratore</Label>
                <Input
                  id="admin_email"
                  type="email"
                  value={settings.admin_email}
                  onChange={(e) => setSettings(prev => ({ ...prev, admin_email: e.target.value }))}
                  placeholder="admin@ristorante.com"
                />
              </div>
            </CardContent>
          </Card>

          {/* Azioni Rapide */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-600">
                <Smartphone className="h-5 w-5" />
                Azioni Rapide
              </CardTitle>
              <CardDescription>
                Strumenti di manutenzione e ripristino
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={resetToDefaults}
                variant="outline"
                className="w-full"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Ripristina Valori Predefiniti
              </Button>
              
              <Button
                onClick={() => {
                  window.open('https://wa.me/' + settings.whatsapp_number + '?text=Test%20messaggio%20dal%20sistema', '_blank');
                }}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Testa WhatsApp
              </Button>

              <Button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await axios.post(`${API}/email-sync`, {}, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    toast.success(`🎉 ${response.data.message}`);
                  } catch (error) {
                    toast.error('Errore: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                <Mail className="h-4 w-4 mr-2" />
                Sincronizza Email Ora
              </Button>
            </CardContent>
          </Card>

          {/* Backup e Ripristino */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600">
                <FileText className="h-5 w-5" />
                Backup e Ripristino Dati
              </CardTitle>
              <CardDescription>
                Esporta e importa i dati del sistema per sicurezza
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Scarica Backup */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Download className="h-4 w-4 text-blue-600" />
                      Scarica Backup
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Esporta tutti i dati del sistema in un file JSON
                    </p>
                  </div>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li>• Tutte le transazioni</li>
                    <li>• Chiusure giornaliere</li>
                    <li>• Incassi del giorno</li>
                    <li>• Versamenti e spese extra</li>
                    <li>• Configurazioni sistema</li>
                  </ul>
                  <Button
                    onClick={downloadBackup}
                    disabled={backupLoading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {backupLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creazione backup...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Scarica Backup
                      </>
                    )}
                  </Button>
                </div>

                {/* Ripristina Backup */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Upload className="h-4 w-4 text-orange-600" />
                      Ripristina da Backup
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Importa i dati da un file di backup precedente
                    </p>
                  </div>
                  <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded">
                    ⚠️ Operazione irreversibile! Richiede password amministratore.
                  </div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleRestoreFileSelect}
                    className="hidden"
                    id="restore-file-input"
                  />
                  <Button
                    onClick={() => document.getElementById('restore-file-input').click()}
                    variant="outline"
                    className="w-full border-orange-600 text-orange-600 hover:bg-orange-50"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Carica File Backup
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configurazione Aruba Fatturazione Elettronica */}
          <Card className="lg:col-span-2 border-2 border-blue-200">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-50">
              <CardTitle className="flex items-center gap-2 text-blue-700">
                <FileText className="h-5 w-5" />
                🧾 Aruba Fatturazione Elettronica
              </CardTitle>
              <CardDescription>
                Configura le credenziali per scaricare le fatture passive
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Status */}
                <div className={`p-3 rounded-lg border ${arubaConfig.configured ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center gap-2">
                    {arubaConfig.configured ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">Configurato</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm font-medium text-yellow-800">Non configurato</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Form Aruba */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="aruba-username">Username Aruba</Label>
                    <Input
                      id="aruba-username"
                      type="text"
                      placeholder="ARUBA12345678901"
                      value={arubaConfig.username}
                      onChange={(e) => setArubaConfig({...arubaConfig, username: e.target.value})}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Username fornito da Aruba per la Fatturazione Elettronica
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="aruba-password">Password Aruba</Label>
                    <Input
                      id="aruba-password"
                      type="password"
                      placeholder="••••••••"
                      value={arubaConfig.password}
                      onChange={(e) => setArubaConfig({...arubaConfig, password: e.target.value})}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Lascia vuoto per mantenere la password attuale
                    </p>
                  </div>
                </div>

                {/* Pulsanti Azione */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t">
                  <Button
                    onClick={saveArubaConfig}
                    disabled={arubaConfigLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {arubaConfigLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Salva
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={testArubaConnection}
                    disabled={testingAruba || !arubaConfig.username}
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    {testingAruba ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Test...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Testa Connessione
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Categorizzazione Automatica Fatture */}
          <Card className="lg:col-span-2 border-2 border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50">
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <Clock className="h-5 w-5" />
                🤖 Categorizzazione Automatica AI
              </CardTitle>
              <CardDescription>
                Categorizzazione automatica delle fatture non categorizzate
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Status Scheduler */}
                <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">Scheduler Attivo</span>
                  </div>
                  
                  {categorizationScheduler && (
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Orario esecuzione</p>
                        <p className="font-semibold text-purple-700">{categorizationScheduler.orario}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Timezone</p>
                        <p className="font-semibold text-purple-700">{categorizationScheduler.timezone}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-500">Limite</p>
                        <p className="font-semibold text-purple-700">{categorizationScheduler.limite}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Status Categorizzazione Corrente */}
                {categorizationStatus && (
                  <div className={`p-4 rounded-lg border ${categorizationStatus.running ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        {categorizationStatus.running ? '⏳ Categorizzazione in corso...' : '✅ Nessuna categorizzazione in corso'}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={loadCategorizationInfo}
                        className="h-8 px-2"
                      >
                        <RefreshCw className="h-3 w-3" />
                      </Button>
                    </div>
                    {categorizationStatus.running && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Progresso: {categorizationStatus.progress}/{categorizationStatus.total}</span>
                          <span>Categorizzate: {categorizationStatus.categorizzate} | Errori: {categorizationStatus.errori}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${categorizationStatus.total > 0 ? (categorizationStatus.progress / categorizationStatus.total) * 100 : 0}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Info */}
                <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded-lg">
                  <p>📅 Ogni giorno alle ore <strong>04:00</strong> (ora italiana) il sistema categorizza automaticamente tutte le fatture non ancora categorizzate utilizzando l'intelligenza artificiale.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Gestione File Importati */}
          <Card className="lg:col-span-2 border-2 border-orange-200">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <FileText className="h-5 w-5" />
                📁 File Articoli e Finanziario Importati
              </CardTitle>
              <CardDescription>
                Gestisci i file analizzati - Visualizza e elimina per data
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              {loadingImports ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
                  <span className="ml-2 text-sm text-gray-500">Caricamento...</span>
                </div>
              ) : emailImports.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Nessun file importato</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {Object.keys(groupedImports).sort((a, b) => b - a).map(year => (
                    <div key={year} className="border rounded-lg overflow-hidden">
                      {/* Header Anno */}
                      <button
                        onClick={() => toggleYear(year)}
                        className="w-full flex items-center justify-between px-3 py-2 bg-orange-100 hover:bg-orange-200 transition-colors"
                      >
                        <span className="font-semibold text-orange-800">{year}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-orange-600">
                            {Object.values(groupedImports[year]).flat().length} giorni
                          </span>
                          {expandedYears[year] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </button>
                      
                      {/* Mesi dell'anno */}
                      {expandedYears[year] && (
                        <div className="bg-white">
                          {Object.keys(groupedImports[year]).sort((a, b) => b - a).map(month => {
                            const monthNames = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
                            const yearMonth = `${year}-${month}`;
                            return (
                              <div key={month} className="border-t">
                                {/* Header Mese */}
                                <button
                                  onClick={() => toggleMonth(yearMonth)}
                                  className="w-full flex items-center justify-between px-4 py-1.5 bg-orange-50 hover:bg-orange-100 transition-colors"
                                >
                                  <span className="text-sm font-medium text-orange-700">{monthNames[parseInt(month)]}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-orange-500">{groupedImports[year][month].length} giorni</span>
                                    {expandedMonths[yearMonth] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  </div>
                                </button>
                                
                                {/* Giorni del mese */}
                                {expandedMonths[yearMonth] && (
                                  <div className="bg-gray-50 divide-y divide-gray-100">
                                    {groupedImports[year][month].sort((a, b) => b.report_date.localeCompare(a.report_date)).map(imp => (
                                      <div key={imp.id} className="flex items-center justify-between px-4 py-1.5 hover:bg-gray-100">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-mono text-gray-600">
                                            {imp.report_date.split('-')[2]}/{month}
                                          </span>
                                          <span className="text-xs text-gray-400">
                                            {imp.articoli_filename ? '📊' : ''} {imp.finanziario_filename ? '💰' : ''}
                                          </span>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => deleteEmailImport(imp.id, imp.report_date)}
                                          disabled={deletingImport === imp.id}
                                          className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                          {deletingImport === imp.id ? (
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                          ) : (
                                            <Trash2 className="h-3 w-3" />
                                          )}
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Info */}
              <div className="mt-3 text-xs text-gray-500 flex items-center gap-2">
                <span>📊 = Articoli</span>
                <span>💰 = Finanziario</span>
                <span className="ml-auto">{emailImports.length} totali</span>
              </div>
            </CardContent>
          </Card>

          {/* Configurazione Email Gmail */}
          <Card className="lg:col-span-2 border-2 border-green-200">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="flex items-center gap-2 text-green-700">
                <MessageCircle className="h-5 w-5" />
                📬 Configurazione Email Gmail
              </CardTitle>
              <CardDescription>
                Configura l'accesso alla casella Gmail per sincronizzazione automatica
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Guida */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-900 mb-2">
                    📖 Come ottenere la Password per App:
                  </p>
                  <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
                    <li>Vai su <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/security</a></li>
                    <li>Attiva "Verifica in due passaggi" (se non attiva)</li>
                    <li>Vai su "Password per le app"</li>
                    <li>Seleziona App: "Mail", Dispositivo: "Altro"</li>
                    <li>Copia la password di 16 caratteri generata</li>
                  </ol>
                </div>

                {/* Form Email */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="gmail-email">Indirizzo Email Gmail</Label>
                    <Input
                      id="gmail-email"
                      type="email"
                      placeholder="ilristorantediportapia@gmail.com"
                      value={emailConfig.email}
                      onChange={(e) => setEmailConfig({...emailConfig, email: e.target.value})}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="gmail-password">Password per App Gmail</Label>
                    <Input
                      id="gmail-password"
                      type="password"
                      placeholder="xxxx xxxx xxxx xxxx"
                      value={emailConfig.password}
                      onChange={(e) => setEmailConfig({...emailConfig, password: e.target.value})}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Password di 16 caratteri generata da Google (NON la password normale)
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="email-enabled"
                      checked={emailConfig.enabled}
                      onChange={(e) => setEmailConfig({...emailConfig, enabled: e.target.checked})}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <Label htmlFor="email-enabled" className="cursor-pointer">
                      Abilita sincronizzazione automatica email
                    </Label>
                  </div>
                </div>

                {/* Pulsanti Azione */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-4 border-t">
                  <Button
                    onClick={saveEmailConfig}
                    disabled={emailConfigLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {emailConfigLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Salva Configurazione
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={testEmailConnection}
                    disabled={testingConnection || !emailConfig.email || !emailConfig.password}
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  >
                    {testingConnection ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                        Testando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Testa Connessione
                      </>
                    )}
                  </Button>

                  <Button
                    onClick={syncEmailsNow}
                    disabled={syncingEmails || !emailConfig.enabled}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {syncingEmails ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Sincronizzando...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Sincronizza Ora
                      </>
                    )}
                  </Button>
                </div>

                {!emailConfig.enabled && (
                  <p className="text-xs text-center text-orange-600 bg-orange-50 p-2 rounded">
                    ⚠️ Salva e abilita la configurazione per utilizzare la sincronizzazione
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Importazione Email/Excel con AI */}
          <Card className="lg:col-span-2 border-2 border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="flex items-center gap-2 text-purple-700">
                <MessageCircle className="h-5 w-5" />
                📧 Importazione Automatica Email (AI)
              </CardTitle>
              <CardDescription>
                Carica i file Excel ricevuti via email e l'AI li analizzerà automaticamente
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {/* Descrizione */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-sm text-purple-900 font-medium mb-2">
                    🤖 Come funziona l'analisi AI:
                  </p>
                  <ul className="text-xs text-purple-800 space-y-1 ml-4">
                    <li>• Carica i 2 file Excel ricevuti via email dalla cassa</li>
                    <li>• L'AI estrae automaticamente articoli venduti (quantità, importi)</li>
                    <li>• L'AI categorizza le transazioni finanziarie (Altro, Fiscale, Fatture)</li>
                    <li>• Ottieni dati strutturati pronti per l'importazione nel sistema</li>
                  </ul>
                </div>

                {/* Upload Files */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* File Stampa Articoli */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 hover:border-purple-500 transition-colors">
                    <div className="text-center space-y-2">
                      <FileText className="h-8 w-8 text-purple-600 mx-auto" />
                      <h4 className="font-semibold text-gray-900">Stampa Articoli</h4>
                      <p className="text-xs text-gray-600">
                        File con tutti gli articoli venduti
                      </p>
                      {articoliFile ? (
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <p className="text-xs font-medium text-green-800 truncate">
                            ✓ {articoliFile.name}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-red-600">Nessun file selezionato</p>
                      )}
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={handleArticoliFileSelect}
                        className="hidden"
                        id="articoli-file-input"
                      />
                      <Button
                        onClick={() => document.getElementById('articoli-file-input').click()}
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Seleziona File
                      </Button>
                    </div>
                  </div>

                  {/* File Stampa Finanziario */}
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-4 hover:border-purple-500 transition-colors">
                    <div className="text-center space-y-2">
                      <Euro className="h-8 w-8 text-purple-600 mx-auto" />
                      <h4 className="font-semibold text-gray-900">Stampa Finanziario</h4>
                      <p className="text-xs text-gray-600">
                        File con Altro, Fiscale, Fatture
                      </p>
                      {finanziarioFile ? (
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <p className="text-xs font-medium text-green-800 truncate">
                            ✓ {finanziarioFile.name}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-red-600">Nessun file selezionato</p>
                      )}
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={handleFinanziarioFileSelect}
                        className="hidden"
                        id="finanziario-file-input"
                      />
                      <Button
                        onClick={() => document.getElementById('finanziario-file-input').click()}
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Seleziona File
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Pulsante Analizza */}
                <div className="pt-4 border-t">
                  <Button
                    onClick={uploadAndAnalyzeExcel}
                    disabled={emailImportLoading || !articoliFile || !finanziarioFile}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3"
                  >
                    {emailImportLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Analisi AI in corso...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="h-5 w-5 mr-2" />
                        🤖 Analizza con AI
                      </>
                    )}
                  </Button>
                  {(!articoliFile || !finanziarioFile) && (
                    <p className="text-xs text-center text-gray-500 mt-2">
                      Seleziona entrambi i file per procedere
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        )}

        {/* TAB: Dati Societari */}
        {activeSettingsTab === 'societari' && (
          <div className="grid grid-cols-1 gap-4 lg:gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <Building2 className="h-5 w-5" />
                  Dati Aziendali
                </CardTitle>
                <CardDescription>
                  Informazioni sulla tua attività per documenti e fatturazione
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ragione_sociale">Ragione Sociale</Label>
                    <Input
                      id="ragione_sociale"
                      value={settings.ragione_sociale}
                      onChange={(e) => setSettings(prev => ({ ...prev, ragione_sociale: e.target.value }))}
                      placeholder="Es: Ristorante La Bella Napoli S.r.l."
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="partita_iva">Partita IVA</Label>
                    <Input
                      id="partita_iva"
                      value={settings.partita_iva}
                      onChange={(e) => setSettings(prev => ({ ...prev, partita_iva: e.target.value }))}
                      placeholder="12345678901"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                    <Input
                      id="codice_fiscale"
                      value={settings.codice_fiscale}
                      onChange={(e) => setSettings(prev => ({ ...prev, codice_fiscale: e.target.value }))}
                      placeholder="RSSMRA80A01H501A"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefono">Telefono</Label>
                    <Input
                      id="telefono"
                      value={settings.telefono}
                      onChange={(e) => setSettings(prev => ({ ...prev, telefono: e.target.value }))}
                      placeholder="+39 081 1234567"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-gray-700 mb-3">Indirizzo Sede Legale</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                      <Label htmlFor="indirizzo">Indirizzo</Label>
                      <Input
                        id="indirizzo"
                        value={settings.indirizzo}
                        onChange={(e) => setSettings(prev => ({ ...prev, indirizzo: e.target.value }))}
                        placeholder="Via Roma, 123"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cap">CAP</Label>
                      <Input
                        id="cap"
                        value={settings.cap}
                        onChange={(e) => setSettings(prev => ({ ...prev, cap: e.target.value }))}
                        placeholder="80100"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="provincia">Provincia</Label>
                      <Input
                        id="provincia"
                        value={settings.provincia}
                        onChange={(e) => setSettings(prev => ({ ...prev, provincia: e.target.value }))}
                        placeholder="NA"
                        maxLength={2}
                        className="mt-1"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="citta">Città</Label>
                      <Input
                        id="citta"
                        value={settings.citta}
                        onChange={(e) => setSettings(prev => ({ ...prev, citta: e.target.value }))}
                        placeholder="Napoli"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-gray-700 mb-3">Fatturazione Elettronica</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pec">PEC</Label>
                      <Input
                        id="pec"
                        type="email"
                        value={settings.pec}
                        onChange={(e) => setSettings(prev => ({ ...prev, pec: e.target.value }))}
                        placeholder="azienda@pec.it"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="codice_sdi">Codice SDI</Label>
                      <Input
                        id="codice_sdi"
                        value={settings.codice_sdi}
                        onChange={(e) => setSettings(prev => ({ ...prev, codice_sdi: e.target.value }))}
                        placeholder="SUBM70N"
                        maxLength={7}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">7 caratteri per Sistema di Interscambio</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB: Team */}
        {activeSettingsTab === 'team' && (
          <div className="bg-white/90 lg:bg-white backdrop-blur-sm lg:backdrop-blur-none rounded-xl shadow-lg overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
              <h3 className="font-bold text-lg text-blue-900 flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestione Team
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                Gestisci i membri del team e i loro permessi di accesso
              </p>
            </div>
            <div className="p-4">
              <TeamPage embedded={true} />
            </div>
          </div>
        )}

        {/* TAB: Integrazioni */}
        {activeSettingsTab === 'integrazioni' && (
          <div className="grid grid-cols-1 gap-4 lg:gap-6">
            {/* Configurazione Aruba */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-50">
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <FileText className="h-5 w-5" />
                  🧾 Aruba Fatturazione Elettronica
                </CardTitle>
                <CardDescription>
                  Configura le credenziali per scaricare le fatture passive automaticamente
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-gray-600 mb-4">
                  L'integrazione Aruba permette di importare automaticamente le fatture dal Sistema di Interscambio.
                  Vai al tab "Generale" per configurare le credenziali Aruba.
                </p>
                <Button onClick={() => setActiveSettingsTab('generale')} variant="outline">
                  Vai a Configurazione Aruba
                </Button>
              </CardContent>
            </Card>

            {/* WhatsApp Integration */}
            <Card className="border-2 border-green-200">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <MessageCircle className="h-5 w-5" />
                  WhatsApp Business
                </CardTitle>
                <CardDescription>
                  Invia notifiche e report via WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <p className="text-sm text-gray-600 mb-4">
                  Configura il numero WhatsApp per ricevere notifiche automatiche.
                  Vai al tab "Generale" per configurare il numero.
                </p>
                <Button onClick={() => setActiveSettingsTab('generale')} variant="outline">
                  Vai a Configurazione WhatsApp
                </Button>
              </CardContent>
            </Card>

            {/* Versione App */}
            <Card className="border-2 border-purple-200">
              <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <Sparkles className="h-5 w-5" />
                  Informazioni Sistema
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Versione Applicazione</p>
                    <p className="text-sm text-gray-500">Ultimo aggiornamento</p>
                  </div>
                  <div className="text-right">
                    <span className="px-4 py-2 bg-purple-600 text-white rounded-full text-lg font-bold">
                      v{APP_VERSION}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TAB: Backup */}
        {activeSettingsTab === 'backup' && (
          <div className="grid grid-cols-1 gap-4 lg:gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-600">
                  <Download className="h-5 w-5" />
                  Backup e Ripristino Dati
                </CardTitle>
                <CardDescription>
                  Esporta e importa i dati del sistema per sicurezza
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Scarica Backup */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Download className="h-4 w-4 text-blue-600" />
                        Scarica Backup
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Esporta tutti i dati del sistema in un file JSON
                      </p>
                    </div>
                    <Button
                      onClick={downloadBackup}
                      disabled={backupLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {backupLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generazione backup...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Scarica Backup
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Ripristina Backup */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Upload className="h-4 w-4 text-orange-600" />
                        Ripristina Backup
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Carica un file di backup precedente
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".json"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setBackupFile(file);
                          setShowRestoreDialog(true);
                        }
                      }}
                      className="hidden"
                      id="backup-file-input"
                    />
                    <Button
                      onClick={() => document.getElementById('backup-file-input')?.click()}
                      variant="outline"
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Seleziona File Backup
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pulsanti Azione */}
        <div className="bg-white/90 lg:bg-white backdrop-blur-sm lg:backdrop-blur-none rounded-xl shadow-lg p-6 mt-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Le modifiche alle impostazioni verranno applicate immediatamente
            </div>
            <div className="flex gap-3">
              <Button
                onClick={onBack}
                variant="outline"
              >
                Annulla
              </Button>
              <Button
                onClick={saveSettings}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Salva Impostazioni
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Dialog Ripristino Backup */}
        {showRestoreDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Upload className="h-5 w-5 text-orange-600" />
                  Ripristina Backup
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                  File selezionato: <span className="font-semibold">{backupFile?.name}</span>
                </p>
              </div>

              <div className="space-y-4">
                {/* Modalità di ripristino */}
                <div>
                  <Label>Modalità di Ripristino</Label>
                  <Select value={restoreMode} onValueChange={setRestoreMode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="replace">
                        <div className="flex flex-col items-start">
                          <span className="font-semibold">Sostituisci Tutto</span>
                          <span className="text-xs text-gray-500">Cancella i dati attuali e carica quelli del backup</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="merge">
                        <div className="flex flex-col items-start">
                          <span className="font-semibold">Aggiungi</span>
                          <span className="text-xs text-gray-500">Mantieni i dati esistenti e aggiungi quelli del backup</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="restore-password">Password Amministratore</Label>
                  <Input
                    id="restore-password"
                    type="password"
                    value={restorePassword}
                    onChange={(e) => setRestorePassword(e.target.value)}
                    placeholder="Inserisci la password"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Richiesta per sicurezza
                  </p>
                </div>

                {/* Avviso */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-800">
                    <strong>⚠️ Attenzione:</strong> {restoreMode === 'replace' 
                      ? 'Tutti i dati attuali saranno cancellati e sostituiti con quelli del backup.' 
                      : 'I dati del backup saranno aggiunti a quelli esistenti.'}
                  </p>
                </div>

                {/* Pulsanti */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => {
                      setShowRestoreDialog(false);
                      setBackupFile(null);
                      setRestorePassword('');
                    }}
                    variant="outline"
                    className="flex-1"
                    disabled={restoreLoading}
                  >
                    Annulla
                  </Button>
                  <Button
                    onClick={restoreBackup}
                    disabled={restoreLoading || !restorePassword}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                  >
                    {restoreLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Ripristino...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Ripristina
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dialog Risultati Analisi AI */}
        {showImportResults && importResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
              <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-pink-50">
                <h2 className="text-2xl font-bold text-purple-900 flex items-center gap-2">
                  🤖 Risultati Analisi AI
                </h2>
                <p className="text-sm text-purple-700 mt-1">
                  I dati sono stati estratti e analizzati automaticamente dall'intelligenza artificiale
                </p>
              </div>

              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Stampa Articoli */}
                {importResults.data?.articoli?.success && (
                  <div className="border rounded-lg p-4 bg-blue-50">
                    <h3 className="font-bold text-lg text-blue-900 mb-3 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      📦 Articoli Venduti
                    </h3>
                    {importResults.data.articoli.data?.articoli && (
                      <>
                        <div className="bg-white rounded p-3 mb-3">
                          <p className="text-sm font-semibold text-gray-700">
                            Totale Articoli: {importResults.data.articoli.data.totale_articoli}
                          </p>
                          <p className="text-sm font-semibold text-gray-700">
                            Totale Importo: €{importResults.data.articoli.data.totale_importo?.toFixed(2)}
                          </p>
                        </div>
                        <div className="bg-white rounded p-3 max-h-60 overflow-y-auto">
                          <table className="w-full text-xs">
                            <thead className="border-b">
                              <tr>
                                <th className="text-left p-2">Codice</th>
                                <th className="text-left p-2">Descrizione</th>
                                <th className="text-right p-2">Qta</th>
                                <th className="text-right p-2">Importo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {importResults.data.articoli.data.articoli.slice(0, 10).map((art, idx) => (
                                <tr key={idx} className="border-b hover:bg-gray-50">
                                  <td className="p-2">{art.codice}</td>
                                  <td className="p-2">{art.descrizione}</td>
                                  <td className="text-right p-2">{art.quantita}</td>
                                  <td className="text-right p-2">€{art.importo?.toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {importResults.data.articoli.data.articoli.length > 10 && (
                            <p className="text-xs text-gray-500 text-center mt-2">
                              ... e altri {importResults.data.articoli.data.articoli.length - 10} articoli
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Stampa Finanziario */}
                {importResults.data?.finanziario?.success && (
                  <div className="border rounded-lg p-4 bg-green-50">
                    <h3 className="font-bold text-lg text-green-900 mb-3 flex items-center gap-2">
                      <Euro className="h-5 w-5" />
                      💰 Gestione Finanziaria
                    </h3>
                    {importResults.data.finanziario.data && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* Altro */}
                        <div className="bg-white rounded p-3 border-l-4 border-red-500">
                          <p className="text-xs font-semibold text-gray-600 mb-1">ALTRO</p>
                          <p className="text-sm font-bold text-gray-900">
                            €{importResults.data.finanziario.data.nero?.importo?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {importResults.data.finanziario.data.nero?.quantita} transazioni
                          </p>
                        </div>

                        {/* Fiscale Scontrino */}
                        <div className="bg-white rounded p-3 border-l-4 border-blue-500">
                          <p className="text-xs font-semibold text-gray-600 mb-1">FISCALE (Scontrino)</p>
                          <p className="text-sm font-bold text-gray-900">
                            €{importResults.data.finanziario.data.fiscale_scontrino?.importo?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {importResults.data.finanziario.data.fiscale_scontrino?.quantita} scontrini
                          </p>
                        </div>

                        {/* Fatture */}
                        <div className="bg-white rounded p-3 border-l-4 border-green-500">
                          <p className="text-xs font-semibold text-gray-600 mb-1">FATTURE</p>
                          <p className="text-sm font-bold text-gray-900">
                            €{importResults.data.finanziario.data.fatture?.importo?.toFixed(2)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {importResults.data.finanziario.data.fatture?.quantita} fatture
                          </p>
                        </div>

                        {/* Totale */}
                        <div className="bg-purple-100 rounded p-3 border-l-4 border-purple-600 md:col-span-3">
                          <p className="text-xs font-semibold text-purple-900 mb-1">TOTALE GENERALE</p>
                          <p className="text-xl font-bold text-purple-900">
                            €{importResults.data.finanziario.data.totale_generale?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer con azioni */}
              <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                <Button
                  onClick={() => {
                    setShowImportResults(false);
                    setImportResults(null);
                  }}
                  variant="outline"
                >
                  Chiudi
                </Button>
                <Button
                  onClick={() => {
                    setShowImportResults(false);
                    setImportResults(null);
                    toast.success('Dati pronti per essere applicati al sistema');
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Conferma e Chiudi
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
// AnalyticsPage importato da ./pages/AnalyticsPage.jsx

// Importazioni Email Page Component
const ImportazioniPage = () => {
  const [emailConfig, setEmailConfig] = useState({ 
    email: '', 
    password: '', 
    enabled: false,
    sender_filter: 'ilristorantediportapia@gmail.com',
    subject_articoli: 'Chiusure Stampa Articoli',
    subject_finanziario: 'Chiusure Stampa Finanziario Periodo',
    sync_interval_seconds: 21600  // 6 ore default
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [syncingEmails, setSyncingEmails] = useState(false);
  const [articoliFile, setArticoliFile] = useState(null);
  const [finanziarioFile, setFinanziarioFile] = useState(null);
  const [emailImportLoading, setEmailImportLoading] = useState(false);
  const [showImportResults, setShowImportResults] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [imports, setImports] = useState([]);
  const [loadingImports, setLoadingImports] = useState(false);

  // Opzioni intervallo sincronizzazione
  const syncIntervalOptions = [
    { value: 30, label: 'Ogni 30 secondi' },
    { value: 60, label: 'Ogni minuto' },
    { value: 300, label: 'Ogni 5 minuti' },
    { value: 900, label: 'Ogni 15 minuti' },
    { value: 1800, label: 'Ogni 30 minuti' },
    { value: 3600, label: 'Ogni ora' },
    { value: 7200, label: 'Ogni 2 ore' },
    { value: 14400, label: 'Ogni 4 ore' },
    { value: 21600, label: 'Ogni 6 ore' },
    { value: 43200, label: 'Ogni 12 ore' },
    { value: 86400, label: 'Ogni 24 ore' },
  ];

  useEffect(() => {
    loadEmailConfig();
    loadImports();
  }, []);

  const loadEmailConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Gestisci retrocompatibilità con sync_interval_hours
      const config = response.data;
      if (!config.sync_interval_seconds && config.sync_interval_hours) {
        config.sync_interval_seconds = config.sync_interval_hours * 3600;
      }
      setEmailConfig(config);
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const loadImports = async () => {
    setLoadingImports(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email-imports`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setImports(response.data);
    } catch (error) {
      console.error('Error loading imports:', error);
    } finally {
      setLoadingImports(false);
    }
  };

  const deleteImport = async (importId) => {
    const confirmDelete = window.confirm(
      '🗑️ ELIMINAZIONE IMPORTAZIONE\n\n' +
      'Sei sicuro di voler eliminare questa importazione?\n\n' +
      'Questa operazione NON PUÒ ESSERE ANNULLATA!\n\n' +
      'Vuoi continuare?'
    );

    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/email-imports/${importId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('✅ Importazione eliminata!');
      await loadImports(); // Ricarica la lista
    } catch (error) {
      console.error('Error deleting import:', error);
      toast.error('Errore nell\'eliminazione: ' + (error.response?.data?.detail || error.message));
    }
  };

  const saveEmailConfig = async () => {
    if (!emailConfig.email || !emailConfig.password || emailConfig.password === '********') {
      toast.error('Inserisci email e password validi');
      return;
    }

    setEmailConfigLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/email-config`, emailConfig, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('✅ Configurazione salvata!');
      await loadEmailConfig();
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const testEmailConnection = async () => {
    setTestingConnection(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email-config/test`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success(`✅ Connessione riuscita! ${response.data.total_messages} email trovate`);
      } else {
        toast.error('❌ ' + response.data.message);
      }
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTestingConnection(false);
    }
  };

  const syncEmailsNow = async () => {
    setSyncingEmails(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/email-sync`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`🎉 ${response.data.message}`);
      await loadImports();
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncingEmails(false);
    }
  };

  const uploadAndAnalyzeExcel = async () => {
    if (!articoliFile || !finanziarioFile) {
      toast.error('Seleziona entrambi i file');
      return;
    }

    setEmailImportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('articoli_file', articoliFile);
      formData.append('finanziario_file', finanziarioFile);

      const response = await axios.post(`${API}/upload-excel`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setImportResults(response.data);
        setShowImportResults(true);
        toast.success('🎉 Analisi completata!');
        setArticoliFile(null);
        setFinanziarioFile(null);
        await loadImports();
      }
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEmailImportLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Configurazione Email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600">
            <MessageCircle className="h-5 w-5" />
            Configurazione Email Gmail
          </CardTitle>
          <CardDescription>
            Connetti la casella Gmail per sincronizzazione automatica
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Email Gmail</Label>
              <Input
                type="email"
                placeholder="email@gmail.com"
                value={emailConfig.email}
                onChange={(e) => setEmailConfig({...emailConfig, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Password per App</Label>
              <Input
                type="password"
                placeholder="Password 16 caratteri"
                value={emailConfig.password}
                onChange={(e) => setEmailConfig({...emailConfig, password: e.target.value})}
              />
            </div>
          </div>

          {/* Filtri Avanzati */}
          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Filtri Email Automatici
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Mittente Email</Label>
                <Input
                  placeholder="email@mittente.com"
                  value={emailConfig.sender_filter}
                  onChange={(e) => setEmailConfig({...emailConfig, sender_filter: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Filtra email da questo mittente</p>
              </div>
              <div>
                <Label>Oggetto File Articoli</Label>
                <Input
                  placeholder="Chiusure Stampa Articoli"
                  value={emailConfig.subject_articoli}
                  onChange={(e) => setEmailConfig({...emailConfig, subject_articoli: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Oggetto email articoli</p>
              </div>
              <div>
                <Label>Oggetto File Finanziario</Label>
                <Input
                  placeholder="Chiusure Stampa Finanziario"
                  value={emailConfig.subject_finanziario}
                  onChange={(e) => setEmailConfig({...emailConfig, subject_finanziario: e.target.value})}
                />
                <p className="text-xs text-gray-500 mt-1">Oggetto email finanziario</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="email-enabled"
              checked={emailConfig.enabled}
              onChange={(e) => setEmailConfig({...emailConfig, enabled: e.target.checked})}
              className="h-4 w-4 rounded"
            />
            <Label htmlFor="email-enabled" className="cursor-pointer">Abilita sincronizzazione automatica</Label>
          </div>

          {emailConfig.enabled && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-purple-50 rounded-lg">
              <Clock className="h-5 w-5 text-purple-600" />
              <Label className="text-sm font-medium">Intervallo sincronizzazione:</Label>
              <select
                value={emailConfig.sync_interval_seconds || 21600}
                onChange={(e) => setEmailConfig({...emailConfig, sync_interval_seconds: parseInt(e.target.value)})}
                className="border rounded-md px-3 py-1.5 text-sm bg-white"
              >
                {syncIntervalOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {emailConfig.scheduler_next_run && (
                <span className="text-xs text-green-600 ml-2">
                  ✅ Prossima: {emailConfig.scheduler_next_run}
                </span>
              )}
            </div>
          )}

          {emailConfig.enabled && emailConfig.scheduler_active && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-700">
                <strong>Scheduler attivo</strong> - Prossima sincronizzazione: {emailConfig.scheduler_next_run || 'In attesa...'}
              </span>
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={saveEmailConfig} disabled={emailConfigLoading} className="bg-green-600">
              <Save className="h-4 w-4 mr-2" />
              Salva Configurazione
            </Button>
            <Button onClick={testEmailConnection} disabled={testingConnection} variant="outline">
              <CheckCircle className="h-4 w-4 mr-2" />
              Testa Connessione
            </Button>
            <Button onClick={syncEmailsNow} disabled={syncingEmails || !emailConfig.enabled} className="bg-purple-600">
              <MessageCircle className="h-4 w-4 mr-2" />
              Sincronizza Ora
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload Manuale */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-600">
            <Upload className="h-5 w-5" />
            Upload Manuale File Excel
          </CardTitle>
          <CardDescription>
            Carica i file Excel e analizzali con AI
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium mb-2">Stampa Articoli</p>
              {articoliFile ? (
                <p className="text-xs text-green-600">✓ {articoliFile.name}</p>
              ) : (
                <p className="text-xs text-gray-500">Nessun file</p>
              )}
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setArticoliFile(e.target.files[0])}
                className="hidden"
                id="articoli-upload"
              />
              <Button
                onClick={() => document.getElementById('articoli-upload').click()}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                Seleziona
              </Button>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <Euro className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm font-medium mb-2">Stampa Finanziario</p>
              {finanziarioFile ? (
                <p className="text-xs text-green-600">✓ {finanziarioFile.name}</p>
              ) : (
                <p className="text-xs text-gray-500">Nessun file</p>
              )}
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={(e) => setFinanziarioFile(e.target.files[0])}
                className="hidden"
                id="finanziario-upload"
              />
              <Button
                onClick={() => document.getElementById('finanziario-upload').click()}
                variant="outline"
                size="sm"
                className="mt-2"
              >
                Seleziona
              </Button>
            </div>
          </div>

          <Button
            onClick={uploadAndAnalyzeExcel}
            disabled={emailImportLoading || !articoliFile || !finanziarioFile}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
          >
            {emailImportLoading ? 'Analisi in corso...' : '🤖 Analizza con AI'}
          </Button>
        </CardContent>
      </Card>

      {/* Storico Importazioni */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-600">
            <Clock className="h-5 w-5" />
            Storico Importazioni
          </CardTitle>
          <CardDescription>
            Ultime importazioni analizzate
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingImports ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
            </div>
          ) : imports.length > 0 ? (
            <div className="space-y-3">
              {imports.slice(0, 5).map((imp) => (
                <div key={imp.id} className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        {imp.report_date ? (
                          <>
                            <span className="text-orange-600">Report del {new Date(imp.report_date + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                          </>
                        ) : (
                          new Date(imp.timestamp).toLocaleString('it-IT')
                        )}
                      </p>
                      {imp.report_date && (
                        <p className="text-xs text-gray-400">
                          Importato: {new Date(imp.timestamp).toLocaleString('it-IT')}
                        </p>
                      )}
                      <p className="text-xs text-gray-500">
                        {imp.articoli_filename} • {imp.finanziario_filename}
                      </p>
                      {imp.analysis_result?.articoli?.data && (
                        <div className="text-sm text-gray-600 mt-1">
                          <span>Articoli: {imp.analysis_result.articoli.data.totale_articoli}</span>
                          <span className="mx-2">•</span>
                          <span>€{imp.analysis_result.articoli.data.totale_importo?.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        {imp.status}
                      </span>
                      <Button
                        onClick={() => deleteImport(imp.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:bg-red-50 border-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Nessuna importazione trovata</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Risultati */}
      {showImportResults && importResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-pink-50">
              <h2 className="text-2xl font-bold text-purple-900">🤖 Risultati Analisi AI</h2>
            </div>
            <div className="p-6">
              {importResults.data?.finanziario?.success && (
                <div className="mb-6">
                  <h3 className="font-bold text-lg mb-3">💰 Gestione Finanziaria</h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-red-50 p-3 rounded border-l-4 border-red-500">
                      <p className="text-xs font-medium text-gray-600">NERO</p>
                      <p className="text-lg font-bold">€{importResults.data.finanziario.data.nero?.importo?.toFixed(2)}</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                      <p className="text-xs font-medium text-gray-600">FISCALE</p>
                      <p className="text-lg font-bold">€{importResults.data.finanziario.data.fiscale_scontrino?.importo?.toFixed(2)}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded border-l-4 border-green-500">
                      <p className="text-xs font-medium text-gray-600">FATTURE</p>
                      <p className="text-lg font-bold">€{importResults.data.finanziario.data.fatture?.importo?.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}
              <Button onClick={() => setShowImportResults(false)} className="w-full">
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Error Boundary per catturare crash React
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
    // Se è un errore di autenticazione, reset PWA-friendly
    if (error.message && error.message.includes('401')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      // Invece di reload, forza re-render
      this.setState({ hasError: true, authError: true });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg">
            <h2 className="text-xl font-bold text-red-600 mb-4">
              {this.state.authError ? 'Sessione Scaduta' : 'Oops! Qualcosa è andato storto'}
            </h2>
            <p className="text-gray-600 mb-4">
              {this.state.authError ? 'La tua sessione è scaduta. Rieffettua il login.' : 'L\'applicazione ha riscontrato un errore.'}
            </p>
            <button 
              onClick={() => this.setState({ hasError: false, authError: false })} 
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {this.state.authError ? 'Torna al Login' : 'Riprova'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook personalizzato per debouncing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Header component with logout
const AppHeader = ({ user, onLogout, onShowSettings }) => {
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logout effettuato');
    onLogout();
  };

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_ristocassa/artifacts/j7p7bjoh_Tavola%20disegno%2038.png" 
            alt="Logo Azienda" 
            className="h-8 w-8 object-contain rounded bg-black p-1"
          />
          <div>
            <h1 className="text-lg font-bold text-gray-900">Gestione Cassa</h1>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            <span className="font-medium">Benvenuto, {user.nome_completo || user.username}</span>
            <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
              {(user?.role === 'administrator' || user?.is_super_admin === true) ? 'Admin' : 'Operatore'}
            </span>
          </div>
          {(user?.role === 'administrator' || user?.is_super_admin === true) && onShowSettings && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onShowSettings}
              className="text-gray-600 hover:text-blue-600"
            >
              ⚙️ Impostazioni
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-gray-600 hover:text-red-600"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Esci
          </Button>
        </div>
      </div>
    </div>
  );
};

const formatEuro = (amount) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('it-IT');
};

// Helper function to get current date in Rome timezone
const getTodayInRomeTimezone = () => {
  const now = new Date();
  // More reliable way using Intl.DateTimeFormat
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now);
};

// Helper function to get yesterday's date in Rome timezone
const getYesterdayInRomeTimezone = () => {
  const now = new Date();
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now);
  
  // Get yesterday by subtracting 1 day from today in Rome timezone
  const todayDate = new Date(today + 'T12:00:00Z'); // Noon UTC to avoid timezone issues
  todayDate.setDate(todayDate.getDate() - 1);
  return todayDate.toISOString().split('T')[0];
};

// Transaction types constants
const TransactionType = {
  POS: 'pos',
  CONTANTI: 'contanti',
  SATISPAY: 'satispay',
  BUONI_DAY: 'buoni_day',
  BUONI_EDENRED: 'buoni_edenred',
  SPESE: 'spese'
};

// Batch Transaction Manager - Multiple transactions system
const NewTransactionManager = ({ selectedDate, isLocked, onTransactionSaved }) => {
  const [batchTransactions, setBatchTransactions] = useState([]);
  const [currentTx, setCurrentTx] = useState({ tipo: 'pos', importo: '', descrizione: '' });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  if (isLocked) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-red-800">🔒 Giorno Bloccato</CardTitle></CardHeader>
        <CardContent><p className="text-red-600">Non puoi registrare transazioni</p></CardContent>
      </Card>
    );
  }

  const addTransaction = () => {
    if (!currentTx.importo || parseFloat(currentTx.importo) <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    if (currentTx.tipo === 'spese' && !currentTx.descrizione.trim()) {
      toast.error('Descrizione obbligatoria per le spese');
      return;
    }

    const newTransaction = {
      id: Date.now(),
      tipo: currentTx.tipo,
      importo: parseFloat(currentTx.importo),
      descrizione: currentTx.descrizione || '',
      modalita_pagamento_spesa: currentTx.tipo === 'spese' ? 'contanti' : null
    };

    setBatchTransactions(prev => [...prev, newTransaction]);
    setCurrentTx({ tipo: 'pos', importo: '', descrizione: '' });
    setShowForm(false);
    toast.success('Transazione aggiunta alla lista!');
  };

  const removeTransaction = (id) => {
    setBatchTransactions(prev => prev.filter(tx => tx.id !== id));
    toast.success('Transazione rimossa');
  };

  const saveAllTransactions = async () => {
    if (batchTransactions.length === 0) {
      toast.error('Nessuna transazione da salvare');
      return;
    }

    setSaving(true);
    try {
      // Salva tutte le transazioni una per volta
      for (const tx of batchTransactions) {
        await axios.post(`${API}/transactions`, {
          tipo: tx.tipo,
          importo: tx.importo,
          descrizione: tx.descrizione,
          modalita_pagamento_spesa: tx.modalita_pagamento_spesa,
          data: selectedDate
        });
      }

      toast.success(`${batchTransactions.length} transazioni salvate con successo!`);
      setBatchTransactions([]);
      
      if (onTransactionSaved) onTransactionSaved();
    } catch (error) {
      toast.error('Errore nel salvare le transazioni');
    } finally {
      setSaving(false);
    }
  };

  const getTypeIcon = (tipo) => {
    const icons = {
      pos: '💳',
      contanti: '💰',
      satispay: '📱',
      buoni_day: '🎟',
      buoni_edenred: '🎫',
      spese: '💸'
    };
    return icons[tipo] || '💳';
  };

  const getTypeLabel = (tipo) => {
    const labels = {
      pos: 'POS',
      contanti: 'Contanti',
      satispay: 'Satispay',
      buoni_day: 'Buoni Day',
      buoni_edenred: 'Buoni Edenred',
      spese: 'Spese'
    };
    return labels[tipo] || 'POS';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle 
          className="text-green-800 cursor-pointer hover:text-green-600 transition-colors"
          onClick={() => setShowForm(!showForm)}
        >
          ➕ Nuova Transazione {batchTransactions.length > 0 && `(${batchTransactions.length})`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* Form per aggiungere transazione */}
        {showForm && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-3">
            <select 
              value={currentTx.tipo} 
              onChange={(e) => setCurrentTx(prev => ({...prev, tipo: e.target.value}))}
              className="w-full p-2 border rounded"
            >
              <option value="pos">💳 POS</option>
              <option value="contanti">💰 Contanti</option>
              <option value="satispay">📱 Satispay</option>
              <option value="buoni_day">🎟 Buoni Day</option>
              <option value="buoni_edenred">🎫 Buoni Edenred</option>
              <option value="spese">💸 Spese</option>
            </select>

            <input
              type="number"
              step="0.01"
              placeholder="Importo €"
              value={currentTx.importo}
              onChange={(e) => setCurrentTx(prev => ({...prev, importo: e.target.value}))}
              className="w-full p-2 border rounded"
            />

            <input
              type="text"
              placeholder={currentTx.tipo === 'spese' ? 'Descrizione (obbligatoria)' : 'Descrizione (opzionale)'}
              value={currentTx.descrizione}
              onChange={(e) => setCurrentTx(prev => ({...prev, descrizione: e.target.value}))}
              className="w-full p-2 border rounded"
            />

            <div className="flex gap-2">
              <button
                onClick={addTransaction}
                className="flex-1 bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
              >
                ➕ Aggiungi alla Lista
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 bg-gray-300 text-gray-700 p-2 rounded hover:bg-gray-400"
              >
                Annulla
              </button>
            </div>
          </div>
        )}

        {/* Lista transazioni in batch */}
        {batchTransactions.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-gray-800">Transazioni da salvare:</h4>
            {batchTransactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                <div className="flex items-center gap-2">
                  <span>{getTypeIcon(tx.tipo)}</span>
                  <span className="font-medium">{getTypeLabel(tx.tipo)}</span>
                  <span className="font-bold">€{tx.importo.toFixed(2)}</span>
                  {tx.descrizione && <span className="text-gray-600">- {tx.descrizione}</span>}
                </div>
                <button
                  onClick={() => removeTransaction(tx.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Pulsanti azione */}
        <div className="flex gap-2">
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex-1 bg-green-600 text-white p-2 rounded hover:bg-green-700"
            >
              ➕ Aggiungi Transazione
            </button>
          )}
          
          {batchTransactions.length > 0 && (
            <button
              onClick={saveAllTransactions}
              disabled={saving}
              className="flex-1 bg-orange-600 text-white p-2 rounded hover:bg-orange-700 disabled:opacity-50"
            >
              {saving ? '⏳ Salvando...' : `💾 Salva Tutte (${batchTransactions.length})`}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// COMPONENTE PLACEHOLDER PER SOSTITUIRE IL PROBLEMATICO
const SimpleBatchForm = ({ selectedDate, isLocked, onBatchSaved }) => {
  return (
    <NewTransactionManager 
      selectedDate={selectedDate}
      isLocked={isLocked}
      onTransactionSaved={onBatchSaved}
    />
  );
};

// Componente per il breakdown con card espandibili
const BreakdownCards = ({ selectedDate }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({ pos: false, contanti: false, spese: false });

  useEffect(() => {
    loadBreakdown();
  }, [selectedDate]);

  const loadBreakdown = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/breakdown?data=${selectedDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setBreakdown(response.data);
      }
    } catch (error) {
      console.error('Error loading breakdown:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (type) => {
    setExpanded(prev => ({ ...prev, [type]: !prev[type] }));
  };

  if (loading || !breakdown) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {/* Card Contanti */}
      <Card 
        className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200 cursor-pointer hover:shadow-lg transition-all"
        onClick={() => toggleExpand('contanti')}
      >
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-600" />
            Contanti
          </CardTitle>
          <div className="text-xl font-bold text-amber-900">{formatEuro(breakdown.contanti?.totale)}</div>
        </CardHeader>
        {expanded.contanti && breakdown.contanti?.dettagli?.length > 0 && (
          <CardContent className="pt-0 border-t border-amber-200">
            <div className="space-y-1 mt-2">
              {breakdown.contanti.dettagli.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-amber-700">{item.descrizione}</span>
                  <span className="font-medium text-amber-900">{formatEuro(item.importo)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Card POS Suddivisi */}
      <Card 
        className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 cursor-pointer hover:shadow-lg transition-all"
        onClick={() => toggleExpand('pos')}
      >
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-600" />
            POS Totale
          </CardTitle>
          <div className="text-xl font-bold text-emerald-900">{formatEuro(breakdown.pos?.totale)}</div>
        </CardHeader>
        {expanded.pos && breakdown.pos?.dettagli?.length > 0 && (
          <CardContent className="pt-0 border-t border-emerald-200">
            <div className="space-y-1 mt-2">
              {breakdown.pos.dettagli.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-emerald-700">{item.tipo}</span>
                  <span className="font-medium text-emerald-900">{formatEuro(item.importo)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Card Spese */}
      <Card 
        className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200 cursor-pointer hover:shadow-lg transition-all"
        onClick={() => toggleExpand('spese')}
      >
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-semibold text-red-800 flex items-center gap-2">
            <Receipt className="h-5 w-5 text-red-600" />
            Spese
          </CardTitle>
          <div className="text-xl font-bold text-red-900">{formatEuro(breakdown.spese?.totale)}</div>
        </CardHeader>
        {expanded.spese && breakdown.spese?.dettagli?.length > 0 && (
          <CardContent className="pt-0 border-t border-red-200">
            <div className="space-y-1 mt-2">
              {breakdown.spese.dettagli.map((item, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-red-700">{item.descrizione}</span>
                  <span className="font-medium text-red-900">{formatEuro(item.importo)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

const DashboardSummary = React.memo(({ summary }) => {
  if (!summary) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8 lg:mb-10">
      <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-amber-50 lg:to-yellow-50 border border-amber-500/30 lg:border-amber-200 rounded-2xl lg:rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex flex-row items-center justify-between mb-1">
          <span className="text-xs font-semibold text-amber-400 lg:text-amber-800">Contanti</span>
          <Coins className="h-4 w-4 text-amber-400 lg:text-amber-600" />
        </div>
        <div className="text-base sm:text-lg lg:text-xl font-bold text-amber-400 lg:text-amber-900">{formatEuro(summary.totale_contanti)}</div>
      </div>

      <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-red-50 lg:to-rose-50 border border-red-500/30 lg:border-red-200 rounded-2xl lg:rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex flex-row items-center justify-between mb-1">
          <span className="text-xs font-semibold text-red-400 lg:text-red-800">Spese</span>
          <AlertCircle className="h-4 w-4 text-red-400 lg:text-red-600" />
        </div>
        <div className="text-base sm:text-lg lg:text-xl font-bold text-red-400 lg:text-red-900">{formatEuro(summary.totale_spese)}</div>
      </div>

      <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-emerald-50 lg:to-teal-50 border border-emerald-500/30 lg:border-emerald-200 rounded-2xl lg:rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex flex-row items-center justify-between mb-1">
          <span className="text-xs font-semibold text-emerald-400 lg:text-emerald-800">POS</span>
          <CreditCard className="h-4 w-4 text-emerald-400 lg:text-emerald-600" />
        </div>
        <div className="text-base sm:text-lg lg:text-xl font-bold text-emerald-400 lg:text-emerald-900">{formatEuro(summary.totale_pos)}</div>
      </div>

      <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-cyan-50 lg:to-blue-50 border border-cyan-500/30 lg:border-cyan-200 rounded-2xl lg:rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex flex-row items-center justify-between mb-1">
          <span className="text-xs font-semibold text-cyan-400 lg:text-cyan-800">Satispay</span>
          <Smartphone className="h-4 w-4 text-cyan-400 lg:text-cyan-600" />
        </div>
        <div className="text-base sm:text-lg lg:text-xl font-bold text-cyan-400 lg:text-cyan-900">{formatEuro(summary.totale_satispay)}</div>
      </div>

      <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-green-50 lg:to-lime-50 border border-green-500/30 lg:border-green-200 rounded-2xl lg:rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex flex-row items-center justify-between mb-1">
          <span className="text-xs font-semibold text-green-400 lg:text-green-800">Buoni Day</span>
          <Gift className="h-4 w-4 text-green-400 lg:text-green-600" />
        </div>
        <div className="text-base sm:text-lg lg:text-xl font-bold text-green-400 lg:text-green-900">{formatEuro(summary.totale_buoni_day)}</div>
      </div>

      <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-lime-50 lg:to-emerald-50 border border-lime-500/30 lg:border-lime-200 rounded-2xl lg:rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow">
        <div className="flex flex-row items-center justify-between mb-1">
          <span className="text-xs font-semibold text-lime-400 lg:text-lime-800">Edenred</span>
          <Gift className="h-4 w-4 text-lime-400 lg:text-lime-600" />
        </div>
        <div className="text-base sm:text-lg lg:text-xl font-bold text-lime-400 lg:text-lime-900">{formatEuro(summary.totale_buoni_edenred)}</div>
      </div>

      <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-purple-50 lg:to-violet-50 border border-purple-500/30 lg:border-purple-200 rounded-2xl lg:rounded-xl p-3 lg:p-4 shadow-sm hover:shadow-md transition-shadow col-span-2 sm:col-span-1">
        <div className="flex flex-row items-center justify-between mb-1">
          <span className="text-xs font-semibold text-purple-400 lg:text-purple-800">Tot. Incassi</span>
          <Euro className="h-4 w-4 text-purple-400 lg:text-purple-600" />
        </div>
        <div className="text-base sm:text-lg lg:text-xl font-bold text-purple-400 lg:text-purple-900">{formatEuro(summary.totale_incassi)}</div>
      </div>
    </div>
  );
});

// Rimuovo la barra di ricerca per l'Operatore
const TransactionsList = ({ transactions, onDeleteTransaction, onEditTransaction, selectedDate, userRole }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearch, setIsGlobalSearch] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Global search for expenses when 3+ characters (only for administrators)
  const performGlobalSearch = async (query) => {
    if (userRole !== 'administrator') return; // No search for operators
    
    if (query.length >= 3) {
      setSearchLoading(true);
      try {
        const response = await axios.get(`${API}/search/expenses`, {
          params: { query }
        });
        setGlobalSearchResults(response.data);
        setIsGlobalSearch(true);
      } catch (error) {
        console.error('Error searching expenses:', error);
        setGlobalSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    } else {
      setIsGlobalSearch(false);
      setGlobalSearchResults([]);
    }
  };

  useEffect(() => {
    if (userRole !== 'administrator') return; // No search for operators
    
    const timer = setTimeout(() => {
      performGlobalSearch(searchTerm);
    }, 500); // Debounce search

    return () => clearTimeout(timer);
  }, [searchTerm, userRole]);

  // Functions for transaction editing
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setEditForm({
      tipo: transaction.tipo,
      importo: transaction.importo,
      descrizione: transaction.descrizione || '',
      modalita_pagamento_spesa: transaction.modalita_pagamento_spesa || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(`${API}/transactions/${editingTransaction.id}`, editForm);
      
      if (response.status === 200) {
        toast.success('Transazione modificata con successo');
        setEditingTransaction(null);
        setEditForm({});
        
        // Call parent callback to refresh transactions
        if (onEditTransaction) {
          onEditTransaction();
        }
      }
    } catch (error) {
      console.error('Error updating transaction:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore nella modifica della transazione');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingTransaction(null);
    setEditForm({});
  };

  const displayTransactions = isGlobalSearch ? globalSearchResults : transactions;
  const getTransactionIcon = (tipo) => {
    switch (tipo) {
      case 'pos': return <CreditCard className="h-4 w-4" />;
      case 'contanti': return <Coins className="h-4 w-4" />;
      case 'satispay': return <Smartphone className="h-4 w-4" />;
      case 'buoni_day': return <Gift className="h-4 w-4" />;
      case 'buoni_edenred': return <Gift className="h-4 w-4" />;
      case 'spese': return <AlertCircle className="h-4 w-4" />;
      case 'spese_extra': return <ShoppingBag className="h-4 w-4" />;
      default: return <Euro className="h-4 w-4" />;
    }
  };

  const getTransactionBadge = (tipo) => {
    switch (tipo) {
      case 'pos': return <Badge variant="default" className="bg-emerald-100 text-emerald-800">POS</Badge>;
      case 'contanti': return <Badge variant="default" className="bg-amber-100 text-amber-800">Contanti</Badge>;
      case 'satispay': return <Badge variant="default" className="bg-cyan-100 text-cyan-800">Satispay</Badge>;
      case 'buoni_day': return <Badge variant="default" className="bg-green-100 text-green-800">Buoni Day</Badge>;
      case 'buoni_edenred': return <Badge variant="default" className="bg-lime-100 text-lime-800">Buoni Edenred</Badge>;
      case 'spese': return <Badge variant="destructive">Spese</Badge>;
      case 'spese_extra': return <Badge variant="destructive" className="bg-pink-100 text-pink-800">Spese Extra</Badge>;
      default: return <Badge variant="outline">{tipo}</Badge>;
    }
  };

  return (
    <div>
      {/* Modal di modifica transazione */}
      {editingTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Modifica Transazione</h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-tipo">Tipo</Label>
                <Select value={editForm.tipo} onValueChange={(value) => setEditForm({...editForm, tipo: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pos">POS</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>  
                    <SelectItem value="satispay">Satispay</SelectItem>
                    <SelectItem value="buoni_day">Buoni Day</SelectItem>
                    <SelectItem value="buoni_edenred">Buoni Edenred</SelectItem>
                    <SelectItem value="spese">Spese</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-importo">Importo (€)</Label>
                <Input
                  id="edit-importo"
                  type="number"
                  step="0.01"
                  value={editForm.importo}
                  onChange={(e) => setEditForm({...editForm, importo: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div>
                <Label htmlFor="edit-descrizione">Descrizione</Label>
                <Input
                  id="edit-descrizione"
                  type="text"
                  value={editForm.descrizione}
                  onChange={(e) => setEditForm({...editForm, descrizione: e.target.value})}
                  placeholder={editForm.tipo === 'spese' ? 'Descrizione obbligatoria per le spese' : 'Descrizione (opzionale)'}
                />
              </div>

              {editForm.tipo === 'spese' && (
                <div>
                  <Label htmlFor="edit-modalita">Modalità Pagamento Spesa</Label>
                  <Select value={editForm.modalita_pagamento_spesa} onValueChange={(value) => setEditForm({...editForm, modalita_pagamento_spesa: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona modalità" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pos">POS</SelectItem>
                      <SelectItem value="contanti">Contanti</SelectItem>
                      <SelectItem value="satispay">Satispay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <Button onClick={handleSaveEdit} className="flex-1">
                Salva
              </Button>
              <Button onClick={handleCancelEdit} variant="outline" className="flex-1">
                Annulla
              </Button>
            </div>
          </div>
        </div>
      )}

    <Card>
      <CardHeader>
        <CardTitle>Transazioni</CardTitle>
        <CardDescription>Lista delle transazioni registrate</CardDescription>
        
        {/* Barra di ricerca - solo per amministratori */}
        {userRole === 'administrator' && (
          <div className="relative mt-4">
            <Input
              type="text"
              placeholder="Cerca in spese e spese extra... (min 3 caratteri)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <Receipt className="h-4 w-4" />
            </div>
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              >
                ×
              </Button>
            )}
          </div>
        )}
        
        {userRole === 'administrator' && searchTerm.length >= 3 && (
          <p className="text-sm text-gray-500 mt-2">
            {isGlobalSearch 
              ? `Ricerca globale: ${displayTransactions.length} spese trovate (transazioni + spese extra)`
              : `Trovate ${displayTransactions.length} transazioni`
            }
            {searchLoading && <span className="ml-2">🔍</span>}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {!displayTransactions || displayTransactions.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{searchTerm.length >= 3 ? 'Nessuna spesa trovata (transazioni o spese extra)' : 'Nessuna transazione trovata'}</p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-4 max-h-96 overflow-y-auto">
            {displayTransactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-2 sm:p-4 border rounded-lg">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    {getTransactionIcon(transaction.tipo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                      {getTransactionBadge(transaction.tipo)}
                      <span className="font-medium text-sm sm:text-base">{formatEuro(transaction.importo)}</span>
                    </div>
                    {transaction.descrizione && (
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {searchTerm.length >= 3 ? (
                          <span dangerouslySetInnerHTML={{
                            __html: transaction.descrizione.replace(
                              new RegExp(`(${searchTerm})`, 'gi'),
                              '<mark class="bg-yellow-200">$1</mark>'
                            )
                          }} />
                        ) : (
                          transaction.descrizione
                        )}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">{formatDate(transaction.data)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditTransaction(transaction)}
                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 flex-shrink-0"
                  >
                    <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteTransaction(transaction.id)}
                    className="text-red-600 hover:text-red-800 hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
    </div>
  );
};

const CashControlPanel = () => {
  const [dailyTarget, setDailyTarget] = useState('');
  const [cashControl, setCashControl] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCashControl = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cash-control`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCashControl(response.data);
    } catch (error) {
      console.error('Error loading cash control:', error);
    }
  };

  const handleSetTarget = async (e) => {
    e.preventDefault();
    if (!dailyTarget) {
      toast.error('Inserisci l\'incasso del giorno');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/daily-target`, {
        totale_teorico: parseFloat(dailyTarget)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setDailyTarget('');
      toast.success('Incasso del giorno impostato');
      loadCashControl();
    } catch (error) {
      toast.error('Errore nell\'impostare l\'incasso del giorno');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCashControl();
  }, []);

  return (
    <div className="space-y-6">
      {/* Form per inserire incasso del giorno */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Totale Giornaliero Teorico
          </CardTitle>
          <CardDescription>
            Inserisci il totale che dovrebbe essere incassato oggi
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSetTarget} className="flex gap-4">
            <div className="flex-1">
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={dailyTarget}
                onChange={(e) => setDailyTarget(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={loading}>
              {loading ? 'Salvando...' : 'Imposta Totale'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Partitario controllo cassa */}
      {cashControl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {cashControl.differenza >= 0 ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              Controllo Cassa - Partitario
            </CardTitle>
            <CardDescription>
              Verifica corrispondenza tra incasso del giorno e incassi effettivi
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Incasso del giorno */}
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="font-medium">Incasso del Giorno:</span>
                <span className="text-lg font-bold">{formatEuro(cashControl.totale_teorico)}</span>
              </div>

              {/* Dettaglio modalità pagamento */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-gray-700">Modalità di Pagamento Registrate:</h4>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex justify-between items-center p-2 bg-emerald-50 rounded">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm">POS</span>
                    </div>
                    <span className="font-medium text-emerald-800">{formatEuro(cashControl.totale_pos)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center p-2 bg-amber-50 rounded">
                    <div className="flex items-center gap-2">
                      <Coins className="h-4 w-4 text-amber-600" />
                      <span className="text-sm">Contanti</span>
                    </div>
                    <span className="font-medium text-amber-800">{formatEuro(cashControl.totale_contanti)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center p-2 bg-cyan-50 rounded">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-4 w-4 text-cyan-600" />
                      <span className="text-sm">Satispay</span>
                    </div>
                    <span className="font-medium text-cyan-800">{formatEuro(cashControl.totale_satispay)}</span>
                  </div>
                </div>
              </div>

              {/* Totale effettivo */}
              <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                <span className="font-medium">Totale Effettivo:</span>
                <span className="text-lg font-bold text-purple-800">{formatEuro(cashControl.totale_effettivo)}</span>
              </div>

              {/* Differenza - ECCEDENZA/PERDITA */}
              <div className={`flex justify-between items-center p-3 rounded-lg ${
                cashControl.differenza >= 0 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <span className="font-medium">
                  {cashControl.differenza >= 0 ? 'ECCEDENZA:' : 'PERDITA:'}
                </span>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${
                    cashControl.differenza >= 0 ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {formatEuro(Math.abs(cashControl.differenza))}
                  </span>
                  {cashControl.differenza >= 0 ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                </div>
              </div>

              {/* Stato */}
              <div className={`text-center p-3 rounded-lg font-medium ${
                cashControl.differenza >= 0 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {cashControl.differenza >= 0 
                  ? (cashControl.differenza === 0 ? '✅ Cassa in Pareggio' : '✅ Cassa ECCEDENZA') 
                  : '⚠️ Cassa PERDITA'
                }
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const DailyTargetCard = ({ selectedDate, onTargetSet }) => {
  const [dailyTotal, setDailyTotal] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDailyTotal = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/daily-total`, {
        params: { data: selectedDate },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setDailyTotal(response.data.totale);
      } else {
        setDailyTotal(null);
      }
    } catch (error) {
      setDailyTotal(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDailyTotal();
  }, [selectedDate]);

  return (
    <Card className="border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-3 text-emerald-800">
          <div className="p-2 rounded-lg bg-emerald-100">
            <Euro className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <div className="text-lg font-bold">Incasso del Giorno</div>
            <div className="text-sm text-emerald-600 font-normal">{formatDate(selectedDate)}</div>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {loading ? (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
            <div className="text-sm text-gray-500 mt-2">Caricamento...</div>
          </div>
        ) : dailyTotal !== null ? (
          <div className="text-center bg-white rounded-lg p-6 border border-emerald-200">
            <div className="text-4xl font-bold text-emerald-700 mb-2">
              €{dailyTotal.toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
            </div>
            <div className="text-sm text-emerald-600">Totale incassi del giorno</div>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="text-gray-400 text-4xl mb-2">€0,00</div>
            <div className="text-sm text-gray-500">Nessun dato disponibile per questo giorno</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};



const CashAvailableCard = ({ summary, selectedDate }) => {
  const [bankDeposits, setBankDeposits] = useState([]);
  const [extraExpenses, setExtraExpenses] = useState([]);
  const [cumulativeTotals, setCumulativeTotals] = useState({ 
    totalDeposits: 0, 
    totalExpenses: 0, 
    totalContantiCumulativi: 0
  });
  const [initialCash, setInitialCash] = useState(2060.0); // Default value
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [expenseData, setExpenseData] = useState({ importo: '', descrizione: '' });
  const [loading, setLoading] = useState(false);

  if (!summary) return null;

  const loadBankDeposits = async () => {
    try {
      // Carica solo i versamenti del giorno corrente per la visualizzazione
      const response = await axios.get(`${API}/bank-deposits`, {
        params: { data: selectedDate }
      });
      setBankDeposits(response.data);
    } catch (error) {
      console.error('Error loading bank deposits:', error);
    }
  };

  const loadExtraExpenses = async () => {
    try {
      const token = localStorage.getItem('token');
      // Carica solo le spese extra del giorno corrente per la visualizzazione
      const response = await axios.get(`${API}/extra-expenses`, {
        params: { data: selectedDate },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setExtraExpenses(response.data);
    } catch (error) {
      console.error('Error loading extra expenses:', error);
    }
  };

  const loadInitialCash = async () => {
    try {
      console.log('🔍 Loading initial cash from API...');
      const token = localStorage.getItem('token');
      // Cache busting per forzare reload in produzione
      const cacheBuster = `?t=${Date.now()}`;
      const response = await axios.get(`${API}/initial-cash${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      console.log('🔍 Initial cash API response:', response.data);
      setInitialCash(response.data.importo);
      console.log('🔍 Initial cash set to:', response.data.importo);
    } catch (error) {
      console.error('❌ Error loading initial cash:', error);
      console.log('🔍 Using default value:', initialCash);
      // Keep default value on error
    }
  };

  const loadCumulativeTotals = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Carica i totali cumulativi per il calcolo dei contanti disponibili
      const [depositsResponse, expensesResponse, transactionsResponse] = await Promise.all([
        axios.get(`${API}/bank-deposits`, {
          params: { cumulative: true, up_to_date: selectedDate },
          headers
        }),
        axios.get(`${API}/extra-expenses`, {
          params: { cumulative: true, up_to_date: selectedDate },
          headers
        }),
        // Carica TUTTE le transazioni fino alla data selezionata per calcolare contanti e spese cumulativi
        axios.get(`${API}/transactions`, {
          params: { data_inizio: "1900-01-01", data_fine: selectedDate },
          headers
        })
      ]);
      
      // Calcola totali cumulativi di contanti dalle transazioni (spese NON incluse nei contanti disponibili)
      let totalContantiCumulativi = 0;
      
      transactionsResponse.data.forEach(transaction => {
        if (transaction.tipo === 'contanti') {
          totalContantiCumulativi += transaction.importo;
        }
      });
      
      return {
        totalDeposits: depositsResponse.data.reduce((sum, d) => sum + d.importo, 0),
        totalExpenses: expensesResponse.data.reduce((sum, e) => sum + e.importo, 0),
        totalContantiCumulativi: totalContantiCumulativi
      };
    } catch (error) {
      console.error('Error loading cumulative totals:', error);
      return { totalDeposits: 0, totalExpenses: 0, totalContantiCumulativi: 0 };
    }
  };

  const handleAddDeposit = async (e) => {
    e.preventDefault();
    if (!depositAmount) {
      toast.error('Inserisci l\'importo del versamento');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/bank-deposits`, {
        importo: parseFloat(depositAmount),
        data: selectedDate
      });
      
      toast.success('Versamento registrato');
      setDepositAmount('');
      setShowDepositForm(false);
      await loadBankDeposits();
      const totals = await loadCumulativeTotals();
      setCumulativeTotals(totals);
    } catch (error) {
      toast.error('Errore nel registrare il versamento');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseData.importo || !expenseData.descrizione.trim()) {
      toast.error('Inserisci importo e descrizione');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/extra-expenses`, {
        importo: parseFloat(expenseData.importo),
        descrizione: expenseData.descrizione,
        data: selectedDate
      });
      
      toast.success('Spesa extra registrata');
      setExpenseData({ importo: '', descrizione: '' });
      setShowExpenseForm(false);
      await loadExtraExpenses();
      const totals = await loadCumulativeTotals();
      setCumulativeTotals(totals);
    } catch (error) {
      toast.error('Errore nel registrare la spesa extra');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDeposit = async (depositId) => {
    try {
      await axios.delete(`${API}/bank-deposits/${depositId}`);
      toast.success('Versamento eliminato');
      await loadBankDeposits();
      const totals = await loadCumulativeTotals();
      setCumulativeTotals(totals);
    } catch (error) {
      toast.error('Errore nell\'eliminare il versamento');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API}/extra-expenses/${expenseId}`);
      toast.success('Spesa extra eliminata');
      await loadExtraExpenses();
      const totals = await loadCumulativeTotals();
      setCumulativeTotals(totals);
    } catch (error) {
      toast.error('Errore nell\'eliminare la spesa extra');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await loadInitialCash();
      await Promise.all([
        loadBankDeposits(),
        loadExtraExpenses()
      ]);
      const totals = await loadCumulativeTotals();
      setCumulativeTotals(totals);
    };
    loadData();
  }, [selectedDate]);

  // Calcoli
  const contantiIniziali = initialCash; // Valore dinamico dal database
  const totaleBankDepositsToday = bankDeposits.reduce((sum, deposit) => sum + deposit.importo, 0);
  const totaleExtraExpensesToday = extraExpenses.reduce((sum, expense) => sum + expense.importo, 0);
  // Calcolo corretto: Contanti Iniziali + TUTTI i contanti incassati - versamenti banca - spese extra (NON le spese normali)
  const contantiDisponibili = contantiIniziali + 
                              (cumulativeTotals.totalContantiCumulativi || 0) - 
                              cumulativeTotals.totalDeposits - 
                              cumulativeTotals.totalExpenses;

  return (
    <Card className="border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-green-800">
          <Coins className="h-5 w-5" />
          Contanti Disponibili in Cassa
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Totale principale */}
          <div className="text-center">
            <p className="text-3xl sm:text-4xl font-bold text-green-800">
              {formatEuro(contantiDisponibili)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Totale fisico che dovrebbe essere in cassa</p>
          </div>

          {/* Dettaglio calcolo */}
          <div className="text-xs space-y-1 bg-white/50 p-2 rounded">
            <div className="flex justify-between">
              <span>Incassi contanti totali:</span>
              <span className="font-medium text-green-700">+{formatEuro(cumulativeTotals.totalContantiCumulativi || 0)}</span>
            </div>
            {cumulativeTotals.totalDeposits > 0 && (
              <div className="flex justify-between">
                <span>Versamenti totali:</span>
                <span className="font-medium text-red-700">-{formatEuro(cumulativeTotals.totalDeposits)}</span>
              </div>
            )}
            {cumulativeTotals.totalExpenses > 0 && (
              <div className="flex justify-between">
                <span>Spese extra totali:</span>
                <span className="font-medium text-red-700">-{formatEuro(cumulativeTotals.totalExpenses)}</span>
              </div>
            )}
            <div className="border-t pt-1 mt-2">
              <div className="flex justify-between font-medium text-xs">
                <span>Base fissa + Incassi - Uscite:</span>
                <span>{formatEuro(initialCash)} + {formatEuro(summary.totale_contanti)} - {formatEuro(cumulativeTotals.totalDeposits + cumulativeTotals.totalExpenses)}</span>
              </div>
            </div>
          </div>

          {/* Versamenti in banca */}
          <div className="border-t pt-3">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-green-800">Versamenti Banca (Oggi)</h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDepositForm(!showDepositForm)}
                className="text-xs h-6"
              >
                +
              </Button>
            </div>

            {showDepositForm && (
              <form onSubmit={handleAddDeposit} className="flex gap-2 mb-2">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Importo..."
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  className="h-8 text-xs"
                />
                <Button type="submit" size="sm" disabled={loading} className="h-8 text-xs">
                  {loading ? '...' : 'OK'}
                </Button>
              </form>
            )}

            {bankDeposits.length > 0 && (
              <div className="max-h-20 overflow-y-auto space-y-1">
                {bankDeposits.map((deposit) => (
                  <div key={deposit.id} className="flex justify-between items-center text-xs bg-white/30 p-1 rounded">
                    <span>{formatEuro(deposit.importo)}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteDeposit(deposit.id)}
                      className="h-4 w-4 p-0 text-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Spese extra */}
          <div className="border-t pt-3">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-green-800">Spese Extra (Oggi)</h4>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowExpenseForm(!showExpenseForm)}
                className="text-xs h-6"
              >
                +
              </Button>
            </div>

            {showExpenseForm && (
              <form onSubmit={handleAddExpense} className="space-y-2 mb-2">
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Importo..."
                  value={expenseData.importo}
                  onChange={(e) => setExpenseData({...expenseData, importo: e.target.value})}
                  className="h-8 text-xs"
                />
                <Input
                  placeholder="Descrizione..."
                  value={expenseData.descrizione}
                  onChange={(e) => setExpenseData({...expenseData, descrizione: e.target.value})}
                  className="h-8 text-xs"
                />
                <Button type="submit" size="sm" disabled={loading} className="h-8 text-xs w-full">
                  {loading ? 'Salvando...' : 'Aggiungi Spesa'}
                </Button>
              </form>
            )}

            {extraExpenses.length > 0 && (
              <div className="max-h-20 overflow-y-auto space-y-1">
                {extraExpenses.map((expense) => (
                  <div key={expense.id} className="flex justify-between items-center text-xs bg-white/30 p-1 rounded">
                    <div className="flex-1">
                      <div className="font-medium">{formatEuro(expense.importo)}</div>
                      <div className="text-gray-600 truncate">{expense.descrizione}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteExpense(expense.id)}
                      className="h-4 w-4 p-0 text-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Componente per controllo cassa integrato
const IntegratedCashControl = ({ selectedDate, onRefresh }) => {
  const [cashControl, setCashControl] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCashControl = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cash-control`, {
        params: { data: selectedDate },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCashControl(response.data);
    } catch (error) {
      console.error('Error loading cash control:', error);
      setCashControl(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCashControl();
  }, [selectedDate, onRefresh]);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">Caricamento partitario...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!cashControl || cashControl.totale_teorico === 0) {
    return (
      <Card className="border-2 border-gray-200 bg-gray-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-gray-700">
            <CheckCircle className="h-5 w-5" />
            Partitario Cassa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 text-center py-4">
            Nessun dato di vendita disponibile per questo giorno
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-2 ${
      cashControl.differenza >= 0 
        ? 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50' 
        : 'border-red-200 bg-gradient-to-r from-red-50 to-rose-50'
    }`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          {cashControl.differenza >= 0 ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <span className={cashControl.differenza >= 0 ? 'text-green-800' : 'text-red-800'}>
            Partitario Cassa
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Totali principali */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span>Teorico:</span>
              <span className="font-medium">{formatEuro(cashControl.totale_teorico)}</span>
            </div>
            <div className="flex justify-between">
              <span>Effettivo:</span>
              <span className="font-medium">{formatEuro(cashControl.totale_effettivo)}</span>
            </div>
          </div>

          {/* Dettaglio per modalità */}
          <div className="space-y-2">
            <h4 className="text-xs font-medium text-gray-700 uppercase">Dettaglio Modalità:</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-emerald-100 p-2 rounded text-center">
                <div className="font-medium text-emerald-800 flex items-center justify-center gap-1">
                  <CreditCard className="h-3 w-3" />
                  POS
                </div>
                <div className="text-emerald-700 font-bold">{formatEuro(cashControl.totale_pos)}</div>
              </div>
              
              <div className="bg-amber-100 p-2 rounded text-center">
                <div className="font-medium text-amber-800 flex items-center justify-center gap-1">
                  <Coins className="h-3 w-3" />
                  Contanti
                </div>
                <div className="text-amber-700 font-bold">{formatEuro(cashControl.totale_contanti)}</div>
              </div>
              
              <div className="bg-cyan-100 p-2 rounded text-center">
                <div className="font-medium text-cyan-800 flex items-center justify-center gap-1">
                  <Smartphone className="h-3 w-3" />
                  Satispay
                </div>
                <div className="text-cyan-700 font-bold">{formatEuro(cashControl.totale_satispay)}</div>
              </div>

              <div className="bg-green-100 p-2 rounded text-center">
                <div className="font-medium text-green-800 flex items-center justify-center gap-1">
                  <Gift className="h-3 w-3" />
                  B. Day
                </div>
                <div className="text-green-700 font-bold">{formatEuro(cashControl.totale_buoni_day)}</div>
              </div>

              <div className="bg-lime-100 p-2 rounded text-center">
                <div className="font-medium text-lime-800 flex items-center justify-center gap-1">
                  <Gift className="h-3 w-3" />
                  B. Edenred
                </div>
                <div className="text-lime-700 font-bold">{formatEuro(cashControl.totale_buoni_edenred)}</div>
              </div>

              <div className="bg-red-100 p-2 rounded text-center">
                <div className="font-medium text-red-800 flex items-center justify-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Spese
                </div>
                <div className="text-red-700 font-bold">{formatEuro(cashControl.totale_spese)}</div>
              </div>
            </div>
          </div>

          {/* Risultato finale */}
          <div className={`text-center p-3 rounded-lg font-medium border-2 ${
            cashControl.differenza >= 0 
              ? 'bg-green-100 text-green-800 border-green-300' 
              : 'bg-red-100 text-red-800 border-red-300'
          }`}>
            <div className="text-sm">
              {cashControl.differenza >= 0 ? 'ECCEDENZA:' : 'PERDITA:'} {formatEuro(Math.abs(cashControl.differenza))}
            </div>
            <div className="text-xs mt-1">
              {cashControl.differenza >= 0 ? '✅ Cassa ECCEDENZA' : '⚠️ Cassa PERDITA'}
            </div>
            <div className="text-xs mt-1 opacity-75">
              Totale Effettivo: {formatEuro(cashControl.totale_effettivo)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Simplified dashboard for Operators
const OperatorDashboard = () => {
  const [selectedDate, setSelectedDate] = useState(getTodayInRomeTimezone());
  const [selectedOperator, setSelectedOperator] = useState('Vittorio');
  const [dailyTarget, setDailyTarget] = useState(null);
  const [cashControl, setCashControl] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [targetLoading, setTargetLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const [lockLoading, setLockLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [summary, setSummary] = useState(null);
  const [bankDeposits, setBankDeposits] = useState([]);
  const [extraExpenses, setExtraExpenses] = useState([]);

  const operators = ['Vittorio', 'Fabio', 'Operatore 3'];

  // Controlla se il giorno è stato salvato
  const checkDayLock = async () => {
    try {
      const response = await axios.get(`${API}/day-lock/check`, {
        params: { data: selectedDate }
      });
      setIsLocked(response.data.is_locked);
      setLockInfo(response.data.lock_info || null);
    } catch (error) {
      console.error('Error checking day lock:', error);
    }
  };

  // Carica il target giornaliero
  const loadDailyTarget = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/daily-target`, {
        params: { data: selectedDate },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('💰 Daily target loaded:', response.data);
      setDailyTarget(response.data);
    } catch (error) {
      console.error('Error loading daily target:', error);
    }
  }, [selectedDate]);

  // Carica il cash control per il partitario
  const loadCashControl = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cash-control`, {
        params: { data: selectedDate },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCashControl(response.data);
    } catch (error) {
      console.error('Error loading cash control:', error);
    }
  }, [selectedDate]);

  // Carica le transazioni del giorno per l'operatore
  const loadTransactions = useCallback(async () => {
    try {
      console.log('🔄 Loading transactions for date:', selectedDate);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/transactions`, {
        params: { data_inizio: selectedDate, data_fine: selectedDate },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Transactions loaded:', response.data.length, 'items');
      setTransactions(response.data);
    } catch (error) {
      console.error('❌ Error loading transactions:', error.response?.status, error.response?.data);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Torna al login...');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        // PWA-friendly redirect
        window.location.href = window.location.pathname;
      }
    }
  }, [selectedDate]);

  const handleSetTarget = async (e) => {
    e.preventDefault();
    if (!targetInput) {
      toast.error('Inserisci l\'incasso del giorno');
      return;
    }

    if (isLocked) {
      toast.error('Non puoi modificare un giorno già salvato');
      return;
    }

    setTargetLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('💰 Setting daily target:', parseFloat(targetInput), 'for date:', selectedDate);
      const response = await axios.post(`${API}/daily-target`, {
        totale_teorico: parseFloat(targetInput),
        data: selectedDate
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('💰 Daily target set successfully, response:', response.data);
      console.log('💰 Daily target set successfully, reloading data...');
      toast.success('Incasso del giorno impostato');
      setTargetInput('');
      setShowTargetForm(false);
      await loadDailyTarget();
      await loadCashControl();
      console.log('💰 Data reloaded after setting target');
    } catch (error) {
      console.error('💰 Error setting daily target:', error.response?.data || error.message);
      toast.error('Errore nell\'impostare l\'incasso del giorno: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTargetLoading(false);
    }
  };

  const handleTransactionAdded = useCallback(async () => {
    if (isLocked) {
      toast.error('Non puoi aggiungere transazioni a un giorno già salvato');
      return;
    }
    toast.success('Transazione registrata con successo!');
    await loadTransactions();
    await loadCashControl();
  }, [isLocked, loadTransactions, loadCashControl]);

  // Funzione per eliminare transazione
  const handleDeleteTransaction = async (transactionId) => {
    if (isLocked) {
      toast.error('Non puoi eliminare transazioni da un giorno già salvato');
      return;
    }

    const confirmDelete = window.confirm('Sei sicuro di voler eliminare questa transazione?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/transactions/${transactionId}`);
      toast.success('Transazione eliminata con successo!');
      await loadTransactions();
      await loadCashControl();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Errore nell\'eliminare la transazione');
    }
  };

  // Funzione per iniziare l'editing
  const startEditTransaction = (transaction) => {
    if (isLocked) {
      toast.error('Non puoi modificare transazioni di un giorno già salvato');
      return;
    }
    setEditingTransaction(transaction.id);
    setEditForm({
      tipo: transaction.tipo,
      importo: transaction.importo.toString(),
      descrizione: transaction.descrizione || ''
    });
  };

  // Funzione per salvare l'edit
  const handleSaveEdit = async () => {
    if (!editForm.importo || parseFloat(editForm.importo) <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    if (editForm.tipo === 'spese' && !editForm.descrizione.trim()) {
      toast.error('Descrizione obbligatoria per le spese');
      return;
    }

    try {
      await axios.put(`${API}/transactions/${editingTransaction}`, {
        tipo: editForm.tipo,
        importo: parseFloat(editForm.importo),
        descrizione: editForm.descrizione || '',
        modalita_pagamento_spesa: editForm.tipo === 'spese' ? 'contanti' : null
      });
      
      toast.success('Transazione modificata con successo!');
      setEditingTransaction(null);
      setEditForm({ tipo: '', importo: '', descrizione: '' });
      await loadTransactions();
      await loadCashControl();
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error('Errore nel modificare la transazione');
    }
  };

  // Funzione per annullare l'edit
  const cancelEdit = () => {
    setEditingTransaction(null);
    setEditForm({ tipo: '', importo: '', descrizione: '' });
  };

  const handleSaveDay = async () => {
    if (!selectedOperator) {
      toast.error('Seleziona un operatore');
      return;
    }

    if (isLocked) {
      toast.error('Questo giorno è già stato salvato');
      return;
    }

    const confirmSave = window.confirm(
      `Stai per salvare definitivamente le transazioni del ${formatDate(selectedDate)}.\n\n` +
      `Operatore: ${selectedOperator}\n` +
      `Transazioni registrate: ${transactions.length}\n\n` +
      `Dopo il salvataggio non potrai più modificare nulla per questo giorno.\n\n` +
      `Vuoi continuare?`
    );

    if (!confirmSave) return;

    setSaveLoading(true);
    try {
      await axios.post(`${API}/day-lock`, {
        data: selectedDate,
        operator: selectedOperator
      });
      
      toast.success('Giorno salvato con successo! Invio messaggio WhatsApp...');
      
      // Invia automaticamente il messaggio WhatsApp per l'operatore
      setTimeout(() => {
        try {
          console.log('Inizio tentativo invio WhatsApp automatico...');
          console.log('selectedOperator:', selectedOperator);
          console.log('selectedDate:', selectedDate);
          console.log('dailyTarget:', dailyTarget);
          console.log('transactions:', transactions?.length || 0);
          
          // Messaggio WhatsApp completo con transazioni dettagliate e partitario
          const message = generateWhatsAppMessage();
          
          const phoneNumber = "393453595804";
          const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
          
          console.log('WhatsApp URL:', whatsappURL.substring(0, 100) + '...');
          
          // Rileva se è dispositivo mobile con metodo multi-livello (2025 best practices)
          const isMobile = detectMobileDevice();
          console.log('Dispositivo mobile rilevato:', isMobile);
          
          if (isMobile) {
            // Su mobile usa location.href per aprire WhatsApp direttamente
            console.log('Apertura WhatsApp su mobile con location.href');
            window.location.href = whatsappURL;
          } else {
            // Su desktop usa window.open
            console.log('Apertura WhatsApp su desktop con window.open');
            window.open(whatsappURL, '_blank');
          }
          console.log('✅ WhatsApp aperto con successo!');
          
          toast.success('Messaggio WhatsApp inviato automaticamente!');
        } catch (error) {
          console.error('❌ Errore invio WhatsApp:', error);
          toast.error('Errore nell\'invio automatico WhatsApp: ' + error.message);
        }
      }, 1500);
      
      await checkDayLock();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore nel salvare il giorno');
      }
    } finally {
      setSaveLoading(false);
    }
  };

  // Functions for transaction editing
  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setEditForm({
      tipo: transaction.tipo,
      importo: transaction.importo,
      descrizione: transaction.descrizione || '',
      modalita_pagamento_spesa: transaction.modalita_pagamento_spesa || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingTransaction(null);
    setEditForm({});
  };

  const handleRefreshOperator = useCallback(async () => {
    try {
      // Caricamento parallelo di tutti i dati
      await Promise.all([
        loadDailyTarget(),
        loadCashControl(),
        loadTransactions(),
        checkDayLock()
      ]);
      
      toast.success('Dati aggiornati');
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error('Errore durante l\'aggiornamento');
    }
  }, [loadDailyTarget, loadCashControl, loadTransactions, checkDayLock]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadDailyTarget(),
        loadCashControl(),
        loadTransactions(),
        checkDayLock()
      ]);
    };
    loadData();
  }, [selectedDate]);

  // Aggiorna l'orario ogni secondo per l'operatore
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Funzione per rilevazione mobile robusta (2025 best practices)
  const detectMobileDevice = () => {
    try {
      // 1. Metodo moderno: navigator.userAgentData (solo se mobile=true)
      if (navigator.userAgentData?.mobile === true) {
        console.log('Usando navigator.userAgentData.mobile=true');
        return true;
      }

      // 2. Metodo viewport: dimensione schermo come indicatore primario
      const viewportMobile = window.matchMedia("(max-width: 768px)").matches;
      
      // 3. Metodo touch: supporto touch come indicatore mobile
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // 4. Fallback: User agent regex (più conservativo)
      const userAgentMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(navigator.userAgent);
      
      console.log('Rilevazione mobile dettagliata:');
      console.log('- Viewport mobile (≤768px + coarse pointer):', viewportMobile);
      console.log('- Touch support:', hasTouchSupport);
      console.log('- User agent mobile:', userAgentMobile);
      console.log('- Viewport size:', window.innerWidth + 'x' + window.innerHeight);
      
      // Decisione finale: se viewport è mobile O (user agent è mobile E ha touch)
      const finalResult = viewportMobile || (userAgentMobile && hasTouchSupport);
      console.log('- Decisione finale mobile:', finalResult);
      
      return finalResult;
    } catch (error) {
      console.error('Errore rilevazione mobile, usando fallback:', error);
      return /Mobi|Android/i.test(navigator.userAgent);
    }
  };

  // Funzione per formattare l'orario (per operatore)
  const formatCurrentDateTime = () => {
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Rome'
    };
    return new Intl.DateTimeFormat('it-IT', options).format(currentTime).replace(',', ' -');
  };

  // Funzione per creare il messaggio WhatsApp per l'operatore
  const generateWhatsAppMessage = () => {
    const dateFormatted = formatDate(selectedDate);
    let message = `🏪 *CHIUSURA GIORNALIERA*\n`;
    message += `📅 Data: ${dateFormatted}\n`;
    message += `👤 Operatore: ${selectedOperator}\n\n`;

    // Incasso del giorno
    if (dailyTarget) {
      message += `🎯 *INCASSO DEL GIORNO: €${parseFloat(dailyTarget.totale_teorico).toFixed(2)}*\n\n`;
    }

    // Dettaglio transazioni
    if (transactions && transactions.length > 0) {
      message += `📋 *DETTAGLIO TRANSAZIONI (${transactions.length})*\n`;
      
      const totaliPerTipo = {};
      transactions.forEach((transaction) => {
        const tipo = transaction.tipo.toUpperCase();
        const importo = parseFloat(transaction.importo);
        if (!totaliPerTipo[tipo]) {
          totaliPerTipo[tipo] = 0;
        }
        totaliPerTipo[tipo] += importo;
      });

      // Helper function per ottenere l'emoticon giusta
      const getTransactionEmoji = (tipo) => {
        switch(tipo) {
          case 'POS': return '💳';
          case 'CONTANTI': return '💵';
          case 'SATISPAY': return '📱';
          case 'BUONI_DAY': return '🎫';
          case 'BUONI_EDENRED': return '🎫';
          case 'SPESE': return '💸';
          default: return '💰';
        }
      };

      Object.entries(totaliPerTipo).forEach(([tipo, totale]) => {
        const emoji = getTransactionEmoji(tipo);
        message += `${emoji} ${tipo}: €${totale.toFixed(2)}\n`;
      });

      message += `\n📄 *ELENCO DETTAGLIATO:*\n`;
      transactions.forEach((transaction, index) => {
        const tipo = transaction.tipo.toUpperCase();
        const emoji = getTransactionEmoji(tipo);
        const importo = `€${parseFloat(transaction.importo).toFixed(2)}`;
        const descrizione = transaction.descrizione ? ` - ${transaction.descrizione}` : '';
        message += `${index + 1}. ${emoji} ${tipo}: ${importo}${descrizione}\n`;
      });
      message += `\n`;
    }

    // Riepilogo partitario se disponibile
    if (cashControl) {
      message += `💰 *PARTITARIO CASSA*\n`;
      message += `📊 Totale Teorico: €${parseFloat(cashControl.totale_teorico).toFixed(2)}\n`;
      message += `💰 Totale Effettivo: €${parseFloat(cashControl.totale_effettivo).toFixed(2)}\n`;
      message += `💰 Differenza: €${parseFloat(cashControl.differenza).toFixed(2)}\n`;
      message += `📈 Stato: ${cashControl.pareggia ? '✅ Pareggio' : '❌ Differenza'}\n\n`;
    }

    message += `📱 Inviato dall'operatore - Sistema gestione cassa 🏪`;
    return message;
  };

  // Funzione per gestire il cambio data (per operatore)
  const handleDateChange = (newDate) => {
    console.log('Operatore - Cambio data da:', selectedDate, 'a:', newDate);
    setSelectedDate(newDate);
  };

  // Funzione per inviare messaggio WhatsApp (per operatore)
  const sendWhatsAppMessage = () => {
    const message = generateWhatsAppMessage();
    const phoneNumber = "393453595804";
    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    console.log('Operatore - Apertura WhatsApp manuale');
    
    // Rileva se è dispositivo mobile con metodo multi-livello
    const isMobile = detectMobileDevice();
    console.log('Operatore manuale - Dispositivo mobile rilevato:', isMobile);
    
    if (isMobile) {
      // Su mobile usa location.href per aprire WhatsApp direttamente
      console.log('Apertura WhatsApp su mobile con location.href');
      window.location.href = whatsappURL;
    } else {
      // Su desktop usa window.open
      console.log('Apertura WhatsApp su desktop con window.open');
      window.open(whatsappURL, '_blank');
    }
  };

  // Validazione data: massimo 1 giorno precedente (usando timezone di Roma)
  const maxDateString = getYesterdayInRomeTimezone();

  return (
    <div>
      {/* Modal di modifica transazione */}
      {editingTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Modifica Transazione</h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-tipo">Tipo</Label>
                <Select value={editForm.tipo} onValueChange={(value) => setEditForm({...editForm, tipo: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pos">POS</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>  
                    <SelectItem value="satispay">Satispay</SelectItem>
                    <SelectItem value="buoni_day">Buoni Day</SelectItem>
                    <SelectItem value="buoni_edenred">Buoni Edenred</SelectItem>
                    <SelectItem value="spese">Spese</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="edit-importo">Importo (€)</Label>
                <Input
                  id="edit-importo"
                  type="number"
                  step="0.01"
                  value={editForm.importo}
                  onChange={(e) => setEditForm({...editForm, importo: parseFloat(e.target.value) || 0})}
                />
              </div>

              <div>
                <Label htmlFor="edit-descrizione">Descrizione</Label>
                <Input
                  id="edit-descrizione"
                  type="text"
                  value={editForm.descrizione}
                  onChange={(e) => setEditForm({...editForm, descrizione: e.target.value})}
                  placeholder={editForm.tipo === 'spese' ? 'Descrizione obbligatoria per le spese' : 'Descrizione (opzionale)'}
                />
              </div>

              {editForm.tipo === 'spese' && (
                <div>
                  <Label htmlFor="edit-modalita">Modalità Pagamento Spesa</Label>
                  <Select value={editForm.modalita_pagamento_spesa} onValueChange={(value) => setEditForm({...editForm, modalita_pagamento_spesa: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona modalità" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pos">POS</SelectItem>
                      <SelectItem value="contanti">Contanti</SelectItem>
                      <SelectItem value="satispay">Satispay</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <Button onClick={handleSaveEdit} className="flex-1">
                Salva
              </Button>
              <Button onClick={handleCancelEdit} variant="outline" className="flex-1">
                Annulla
              </Button>
            </div>
          </div>
        </div>
      )}

    <div className="space-y-6 sm:space-y-8 px-2 sm:px-4">
      {/* Header moderno Operatore */}
      <div className="flex flex-col gap-6 mb-8">
        {/* Prima riga: Logo e titolo */}
        <div className="flex items-center gap-4">
          <img 
            src="https://customer-assets.emergentagent.com/job_ristocassa/artifacts/j7p7bjoh_Tavola%20disegno%2038.png" 
            alt="Logo Azienda" 
            className="h-16 w-16 object-contain rounded-xl bg-black p-2 shadow-lg"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inserimento Transazioni</h1>
            <p className="text-base text-gray-600">Registra le transazioni del ristorante</p>
          </div>
        </div>

        {/* Seconda riga: Controlli moderni */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-2xl border border-orange-100 shadow-sm">
          {/* Data e orario */}
          <div className="flex flex-row items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3 bg-white px-3 sm:px-4 py-2 rounded-xl shadow-sm border flex-1 min-w-0">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 flex-shrink-0" />
              <div className="flex flex-col min-w-0">
                <Label className="text-xs text-gray-500 font-medium">Data</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  max={maxDateString}
                  onChange={(e) => handleDateChange(e.target.value)}
                  className="border-0 p-0 h-auto font-medium text-gray-900 bg-transparent focus:ring-0 text-sm sm:text-base w-28 sm:w-32"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3 bg-white px-3 sm:px-4 py-2 rounded-xl shadow-sm border flex-shrink-0">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
              <div className="flex flex-col">
                <Label className="text-xs text-gray-500 font-medium">Orario</Label>
                <span className="font-medium text-gray-900 text-sm sm:text-base">{formatCurrentDateTime().split(' - ')[1]}</span>
              </div>
            </div>
          </div>

          {/* Bottone azione */}
          <div className="flex justify-end w-full sm:w-auto">
            <Button
              onClick={handleRefreshOperator}
              variant="outline"
              className="flex items-center gap-2 px-6 py-2 rounded-xl bg-white border-gray-200 hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <RotateCcw className="h-4 w-4" />
              Aggiorna
            </Button>
          </div>
        </div>

        {/* Status del giorno */}
        {isLocked && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center gap-2 text-red-800">
              <Lock className="h-5 w-5" />
              <span className="font-medium">Giorno già salvato definitivamente</span>
            </div>
            <p className="text-sm text-red-600 mt-1">Non è possibile modificare transazioni o impostazioni.</p>
          </div>
        )}
      </div>

      {/* Selezione Operatore e Data */}
      <div className="w-full lg:max-w-4xl lg:mx-auto space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base sm:text-lg">Informazioni Turno</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Operatore</Label>
                <Select value={selectedOperator} onValueChange={setSelectedOperator} disabled={isLocked}>
                  <SelectTrigger className="h-10 sm:h-auto">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map((operator) => (
                      <SelectItem key={operator} value={operator}>
                        {operator}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm font-medium">Data</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  max={maxDateString}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-10 sm:h-auto"
                  disabled={isLocked}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Layout Desktop Ottimizzato - 3 Colonne */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          
          {/* Colonna 1: Incasso del Giorno e Partitario (Desktop) / Impilate (Mobile) */}
          <div className="xl:col-span-1 space-y-12">
            {/* Incasso del Giorno */}
            {!isLocked && (
              <Card className="border-2 border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-teal-800 text-lg">
                    <Target className="h-5 w-5" />
                    Incasso del Giorno
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {dailyTarget ? (
                    <div className="text-center">
                      <div className="text-2xl xl:text-3xl font-bold text-teal-700 mb-2">
                        {formatEuro(dailyTarget.totale_teorico)}
                      </div>
                      <div className="text-sm text-teal-600">
                        Incasso previsto per {formatDate(selectedDate)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="text-gray-400 text-2xl mb-2">--,--</div>
                      <div className="text-sm text-gray-500">Nessun incasso impostato</div>
                    </div>
                  )}

                  <Button 
                    type="button"
                    onClick={() => setShowTargetForm(!showTargetForm)}
                    className="w-full mt-4 bg-teal-600 hover:bg-teal-700 text-white"
                    disabled={isLocked}
                  >
                    {isLocked ? '🔒 Giorno Salvato' : dailyTarget ? 'Modifica Incasso' : 'Imposta Incasso del Giorno'}
                  </Button>

                  {/* Form Modal Compatto */}
                  {showTargetForm && (
                    <div className="mt-4 bg-teal-50 rounded-lg p-4 border border-teal-200">
                      <div className="text-teal-800 font-semibold text-sm mb-3">Imposta Incasso</div>
                      <form onSubmit={handleSetTarget} className="space-y-3">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 font-medium">€</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0,00"
                            value={targetInput}
                            onChange={(e) => setTargetInput(e.target.value)}
                            className="w-full pl-8 border-teal-300 focus:border-teal-500 focus:ring-teal-500"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            type="submit" 
                            disabled={targetLoading || isLocked}
                            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-xs"
                          >
                            {targetLoading ? '⏳' : '💾 Salva'}
                          </Button>
                          <Button 
                            type="button"
                            onClick={() => setShowTargetForm(false)}
                            variant="outline"
                            className="flex-1 border-teal-300 text-teal-700 hover:bg-teal-50 text-xs"
                          >
                            ❌
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Partitario Cassa Compatto */}
            {dailyTarget && !isLocked && (
              <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-violet-50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-purple-800 text-lg">
                    <CheckCircle className="h-5 w-5" />
                    Partitario Cassa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-gray-600 mb-3 text-center">
                    Verifica corrispondenza incasso
                  </div>
                  
                  {cashControl ? (
                    <div className="space-y-3 text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white p-2 rounded text-center">
                          <div className="text-xs text-gray-500">Previsto</div>
                          <div className="font-bold text-purple-700">{formatEuro(cashControl.totale_teorico)}</div>
                        </div>
                        <div className="bg-white p-2 rounded text-center">
                          <div className="text-xs text-gray-500">Effettivo</div>
                          <div className="font-bold text-green-700">{formatEuro(cashControl.totale_effettivo)}</div>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded text-center">
                        <div className="text-xs text-gray-500 mb-1">
                          {cashControl.differenza >= 0 ? 'ECCEDENZA' : 'PERDITA'}
                        </div>
                        <div className={`font-bold text-lg ${cashControl.differenza >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatEuro(Math.abs(cashControl.differenza))}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          cashControl.pareggia 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {cashControl.pareggia ? '✅ Pareggio' : '⚠️ Differenza'}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 text-sm">
                      Caricamento...
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Colonna 2: Registrazione Transazioni */}
          <div className="xl:col-span-1">
            <ErrorBoundary key={`batch-form-${selectedDate}`}>
              <SimpleBatchForm 
                selectedDate={selectedDate}
                isLocked={isLocked}
                onBatchSaved={handleTransactionAdded}
              />
            </ErrorBoundary>
          </div>

          {/* Colonna 3: Lista Transazioni + Azioni */}
          <div className="xl:col-span-1 space-y-6">
            {/* Transazioni del Giorno */}
            {transactions.length > 0 ? (
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Transazioni del Giorno
                  </CardTitle>
                  <div className="text-sm text-gray-600">{formatDate(selectedDate)} • {transactions.length} transazioni</div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-80 xl:max-h-96 overflow-y-auto">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="border rounded-lg hover:bg-gray-50 transition-colors">
                        {editingTransaction === transaction.id ? (
                          // Form di editing
                          <div className="p-3 bg-blue-50 border-blue-200">
                            <div className="space-y-3">
                              <select 
                                value={editForm.tipo} 
                                onChange={(e) => setEditForm(prev => ({ ...prev, tipo: e.target.value }))}
                                className="w-full p-2 border rounded text-sm"
                              >
                                <option value="pos">💳 POS</option>
                                <option value="contanti">💰 Contanti</option>
                                <option value="satispay">📱 Satispay</option>
                                <option value="buoni_day">🎟 Buoni Day</option>
                                <option value="buoni_edenred">🎫 Buoni Edenred</option>
                                <option value="spese">💸 Spese</option>
                              </select>
                              
                              <input
                                type="number"
                                step="0.01"
                                value={editForm.importo}
                                onChange={(e) => setEditForm(prev => ({ ...prev, importo: e.target.value }))}
                                className="w-full p-2 border rounded text-sm"
                                placeholder="Importo €"
                              />
                              
                              <input
                                type="text"
                                value={editForm.descrizione}
                                onChange={(e) => setEditForm(prev => ({ ...prev, descrizione: e.target.value }))}
                                className="w-full p-2 border rounded text-sm"
                                placeholder={editForm.tipo === 'spese' ? 'Descrizione (obbligatoria)' : 'Descrizione (opzionale)'}
                              />
                              
                              <div className="flex gap-2">
                                <button
                                  onClick={handleSaveEdit}
                                  className="flex-1 bg-green-600 text-white p-2 rounded text-sm hover:bg-green-700"
                                >
                                  ✅ Salva
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="flex-1 bg-gray-300 text-gray-700 p-2 rounded text-sm hover:bg-gray-400"
                                >
                                  ❌ Annulla
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Visualizzazione normale
                          <div className="flex justify-between items-start p-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{transaction.tipo.replace('_', ' ')}</div>
                              {transaction.descrizione && (
                                <div className="text-gray-600 text-xs mt-1 truncate">{transaction.descrizione}</div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-sm">
                                {formatEuro(transaction.importo)}
                              </div>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => startEditTransaction(transaction)}
                                  className="p-1 text-blue-600 hover:bg-blue-100 rounded text-xs"
                                  title="Modifica transazione"
                                >
                                  <Edit className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => handleDeleteTransaction(transaction.id)}
                                  className="p-1 text-red-600 hover:bg-red-100 rounded text-xs"
                                  title="Elimina transazione"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {/* Riepilogo veloce */}
                  <div className="mt-4 pt-4 border-t bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-600 mb-1">Totale transazioni:</div>
                    <div className="text-lg font-bold text-gray-900">
                      {formatEuro(transactions.reduce((sum, t) => sum + parseFloat(t.importo), 0))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Transazioni del Giorno
                  </CardTitle>
                  <div className="text-sm text-gray-600">{formatDate(selectedDate)}</div>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📝</div>
                    <div className="text-sm">Nessuna transazione registrata per oggi</div>
                    <div className="text-xs text-gray-400 mt-1">Usa il form per aggiungere transazioni</div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Save Day Button */}
            <Card className={`border-2 ${isLocked ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'}`}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  {isLocked ? (
                    <Lock className="h-5 w-5 text-red-600" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-orange-600" />
                  )}
                  <span className={isLocked ? 'text-red-800' : 'text-orange-800'}>
                    {isLocked ? 'Giorno Salvato' : 'Salva Giornata'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!isLocked ? (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600">
                      Salva definitivamente la giornata e invia riepilogo WhatsApp
                    </div>
                    
                    <Button 
                      onClick={handleSaveDay}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                      disabled={!dailyTarget || saveLoading}
                    >
                      {saveLoading ? (
                        <span className="flex items-center gap-2">
                          <span>⏳ Salvataggio...</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Save className="h-4 w-4" />
                          💾 Salva Definitivamente
                        </span>
                      )}
                    </Button>
                    
                    {!dailyTarget && (
                      <div className="text-xs text-orange-600 bg-orange-100 p-2 rounded">
                        ⚠️ Imposta prima l'incasso del giorno per poter salvare
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-red-600">
                    <div className="text-2xl mb-2">✅</div>
                    <div className="text-sm">Giornata salvata il {formatDate(selectedDate)}</div>
                    <div className="text-xs text-red-500 mt-2">
                      Non è possibile modificare transazioni già salvate
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

    </div>
    </div>
  );
};

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(getTodayInRomeTimezone());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [operatorSettings, setOperatorSettings] = useState({ block_past_edits: false });
  const [dayLocks, setDayLocks] = useState([]);
  const [showDayLocks, setShowDayLocks] = useState(false);
  const [showOperatorManager, setShowOperatorManager] = useState(false);
  const [showDailySheetUpload, setShowDailySheetUpload] = useState(false);
  const [dailySheetUploading, setDailySheetUploading] = useState(false);
  const [showPosClosureModal, setShowPosClosureModal] = useState(false);
  const [posClosureUploading, setPosClosureUploading] = useState(false);
  const [lastSheetData, setLastSheetData] = useState(null);
  const [editableSheetDate, setEditableSheetDate] = useState(''); // Data modificabile
  const [posClosuresLoaded, setPosClosuresLoaded] = useState([]); // Lista chiusure POS caricate
  const [operators, setOperators] = useState(['Vittorio', 'Fabio', 'Operatore 3']);
  const [newOperatorName, setNewOperatorName] = useState('');
  const [editingOperator, setEditingOperator] = useState(null);
  const [editOperatorName, setEditOperatorName] = useState('');
  const [bankDeposits, setBankDeposits] = useState([]);
  const [extraExpenses, setExtraExpenses] = useState([]);
  const [dailyTotals, setDailyTotals] = useState([]);

  // Carica totali giornalieri
  const loadDailyTotals = async () => {
    try {
      const res = await fetch(`${API}/dashboard/daily-totals?days=10`);
      const data = await res.json();
      if (data.success) {
        setDailyTotals(data.daily_totals || []);
      }
    } catch (error) {
      console.error('Errore caricamento totali giornalieri:', error);
    }
  };

  useEffect(() => {
    loadDailyTotals();
  }, []);

  // Funzione per caricare foglio giornaliero con AI
  const handleDailySheetUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verifica che sia un'immagine
    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine (JPG, PNG, etc.)');
      return;
    }

    setDailySheetUploading(true);
    setShowDailySheetUpload(false);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-daily-sheet`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const { extracted, transactions_created, data } = response.data;
        
        // Salva i dati per il modal
        setLastSheetData({
          data: data,
          extracted: extracted,
          transactions_created: transactions_created
        });
        setEditableSheetDate(data); // Imposta la data modificabile
        setPosClosuresLoaded([]); // Reset chiusure POS
        
        // Aggiorna la data selezionata alla data del foglio
        setSelectedDate(data);
        await loadData(data);
        
        // Mostra modal per chiusura POS
        setShowPosClosureModal(true);
      } else {
        toast.error(`Errore: ${response.data.error || 'Impossibile analizzare il foglio'}`);
      }
    } catch (error) {
      console.error('Error uploading daily sheet:', error);
      toast.error('Errore nel caricamento: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDailySheetUploading(false);
      // Reset input file
      event.target.value = '';
    }
  };

  // Funzione per caricare chiusura POS con AI
  const handlePosClosureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine (JPG, PNG, etc.)');
      return;
    }

    setPosClosureUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      // Usa la data modificabile (che potrebbe essere stata corretta dall'utente)
      if (editableSheetDate) {
        formData.append('data', editableSheetDate);
      } else if (lastSheetData?.data) {
        formData.append('data', lastSheetData.data);
      }

      const response = await axios.post(`${API}/upload-pos-closure`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const { pos_details, transactions_created, data: posData } = response.data;
        
        // Aggiungi alla lista delle chiusure caricate
        setPosClosuresLoaded(prev => [...prev, {
          data: posData,
          pos_details: pos_details,
          count: transactions_created.length
        }]);
        
        let message = `✅ Chiusura POS importata!\n\n`;
        pos_details.forEach(p => {
          message += `💳 ${p.tipo}: €${p.importo.toFixed(2)}`;
          if (p.mance > 0) message += ` (mance: €${p.mance.toFixed(2)})`;
          message += `\n`;
        });
        message += `\n${transactions_created.length} transazioni create`;
        
        toast.success(message, { duration: 5000 });
        
        // NON chiudere il modal - permettere di caricare altre chiusure
        await loadData(editableSheetDate || lastSheetData?.data || selectedDate);
      } else {
        toast.error(`Errore: ${response.data.error || 'Impossibile analizzare la chiusura POS'}`);
      }
    } catch (error) {
      console.error('Error uploading POS closure:', error);
      toast.error('Errore nel caricamento: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPosClosureUploading(false);
      event.target.value = '';
    }
  };

  // Chiudi modal (dopo aver caricato chiusure o saltato)
  const handleSkipPosClosure = async () => {
    if (lastSheetData) {
      const { extracted, transactions_created, data } = lastSheetData;
      const finalDate = editableSheetDate || data;
      
      // Se la data è stata modificata, aggiorna le transazioni nel backend
      if (editableSheetDate && editableSheetDate !== data) {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/transactions/update-date`, {
            old_date: data,
            new_date: editableSheetDate
          }, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          toast.success(`📅 Data aggiornata da ${new Date(data).toLocaleDateString('it-IT')} a ${new Date(editableSheetDate).toLocaleDateString('it-IT')}`);
        } catch (error) {
          console.error('Errore aggiornamento data:', error);
          toast.error('Errore nell\'aggiornamento della data');
        }
      }
      
      let message = `✅ Foglio del ${new Date(finalDate).toLocaleDateString('it-IT')} importato!\n\n`;
      if (extracted.contanti) message += `💵 Contanti: €${extracted.contanti.toFixed(2)}\n`;
      if (extracted.pos && posClosuresLoaded.length === 0) message += `💳 POS: €${extracted.pos.toFixed(2)}\n`;
      if (extracted.spese?.length > 0) {
        message += `📝 Spese: ${extracted.spese.length}\n`;
      }
      if (posClosuresLoaded.length > 0) {
        message += `💳 Chiusure POS caricate: ${posClosuresLoaded.length}\n`;
      }
      message += `\n${transactions_created.length} transazioni create`;
      toast.success(message, { duration: 5000 });
      
      // Ricarica i dati con la data corretta
      setSelectedDate(finalDate);
      await loadData(finalDate);
    }
    setShowPosClosureModal(false);
    setLastSheetData(null);
    setEditableSheetDate('');
    setPosClosuresLoaded([]);
  };

  const loadData = async (date = selectedDate) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const summaryUrl = date === getTodayInRomeTimezone() 
        ? `${API}/summary/today`
        : `${API}/summary/today?data=${date}`;
      
      const transactionsUrl = date === getTodayInRomeTimezone()
        ? `${API}/transactions/today`
        : `${API}/transactions?data_inizio=${date}&data_fine=${date}`;

      const [summaryResponse, transactionsResponse] = await Promise.all([
        axios.get(summaryUrl, { headers }),
        axios.get(transactionsUrl, { headers })
      ]);
      
      setSummary(summaryResponse.data);
      setTransactions(transactionsResponse.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOperatorSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/operator-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOperatorSettings(response.data);
    } catch (error) {
      console.error('Error loading operator settings:', error);
    }
  };

  const handleOperatorSettingsChange = async (blockPastEdits) => {
    try {
      const response = await axios.put(`${API}/operator-settings`, {
        block_past_edits: blockPastEdits
      });
      setOperatorSettings(response.data);
      toast.success(blockPastEdits ? 'Modifiche ai giorni precedenti bloccate per gli operatori' : 'Modifiche ai giorni precedenti sbloccate per gli operatori');
    } catch (error) {
      toast.error('Errore nell\'aggiornare le impostazioni operatore');
    }
  };

  const handleToggleOperatorRestriction = async () => {
    const newValue = !operatorSettings.block_past_edits;
    await handleOperatorSettingsChange(newValue);
  };

  const loadDayLocks = async () => {
    try {
      console.log('🔍 Caricamento day locks da:', `${API}/day-locks`);
      const response = await axios.get(`${API}/day-locks`);
      console.log('✅ Day locks ricevuti:', response.data);
      console.log('📊 Numero day locks:', response.data?.length || 0);
      
      if (response.data && response.data.length > 0) {
        console.log('📋 Primo day lock esempio:', response.data[0]);
      }
      
      setDayLocks(response.data || []);
      
    } catch (error) {
      console.error('❌ Errore caricamento day locks:', error);
      console.error('❌ Status:', error.response?.status);
      console.error('❌ Data:', error.response?.data);
      toast.error('Errore nel caricamento dei salvataggi');
      setDayLocks([]); // Imposta array vuoto in caso di errore
    }
  };

  const handleDeleteDayLockFromList = async (lockId, lockDate) => {
    const confirmDelete = window.confirm(`Vuoi eliminare il salvataggio del ${formatDate(lockDate)}?\n\nQuesto permetterà all'operatore di modificare nuovamente le transazioni di quel giorno.`);
    
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/day-lock/${lockId}`);
      toast.success('Salvataggio eliminato. L\'operatore può ora modificare quel giorno.');
      loadDayLocks();
    } catch (error) {
      toast.error('Errore nell\'eliminare il salvataggio');
    }
  };

  // Funzioni per gestione operatori
  const handleAddOperator = () => {
    if (!newOperatorName.trim()) {
      toast.error('Inserisci un nome per l\'operatore');
      return;
    }
    
    if (operators.includes(newOperatorName.trim())) {
      toast.error('Operatore già esistente');
      return;
    }

    setOperators([...operators, newOperatorName.trim()]);
    setNewOperatorName('');
    toast.success(`Operatore "${newOperatorName.trim()}" aggiunto con successo`);
  };

  const handleEditOperator = (oldName) => {
    setEditingOperator(oldName);
    setEditOperatorName(oldName);
  };

  const handleSaveEditOperator = () => {
    if (!editOperatorName.trim()) {
      toast.error('Il nome operatore non può essere vuoto');
      return;
    }

    if (editOperatorName.trim() !== editingOperator && operators.includes(editOperatorName.trim())) {
      toast.error('Operatore già esistente');
      return;
    }

    const updatedOperators = operators.map(op => 
      op === editingOperator ? editOperatorName.trim() : op
    );
    setOperators(updatedOperators);
    setEditingOperator(null);
    setEditOperatorName('');
    toast.success('Operatore modificato con successo');
  };

  const handleDeleteOperator = (operatorName) => {
    const confirmDelete = window.confirm(`Vuoi eliminare l'operatore "${operatorName}"?\n\nQuesto rimuoverà l'operatore dal sistema.`);
    
    if (!confirmDelete) return;

    if (operators.length <= 1) {
      toast.error('Non puoi eliminare tutti gli operatori');
      return;
    }

    setOperators(operators.filter(op => op !== operatorName));
    toast.success(`Operatore "${operatorName}" eliminato con successo`);
  };

  const handleCancelEditOperator = () => {
    setEditingOperator(null);
    setEditOperatorName('');
  };

  const handleRefreshPage = useCallback(async () => {
    try {
      setLoading(true);
      
      // Caricamento parallelo veloce
      await Promise.all([
        loadData(selectedDate),
        loadOperatorSettings(),
        loadDayLocks()
      ]);
      
      toast.success('Dati aggiornati');
    } catch (error) {
      console.error('Page refresh error:', error);
      toast.error('Errore durante l\'aggiornamento');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, loadData, loadOperatorSettings, loadDayLocks]);

  useEffect(() => {
    loadData(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    loadOperatorSettings();
    loadDayLocks();
  }, []);

  // Aggiorna l'orario ogni secondo
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Funzione per formattare l'orario
  const formatCurrentDateTime = () => {
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Rome'
    };
    return new Intl.DateTimeFormat('it-IT', options).format(currentTime).replace(',', ' -');
  };

  // Funzione per creare messaggio WhatsApp semplificato per Administrator
  const generateWhatsAppMessageAdmin = () => {
    const dateFormatted = formatDate(selectedDate);
    let message = `🏪 RIEPILOGO AMMINISTRATORE\n`;
    message += `📅 Data: ${dateFormatted}\n`;
    
    // Per l'Administrator, usa l'operatore dal giorno salvato se disponibile
    const operatorName = dayLocks.find(lock => lock.data === selectedDate)?.operator || 'Amministratore';
    message += `👤 Operatore: ${operatorName}\n\n`;

    // Riepilogo semplificato per Administrator con solo informazioni base
    if (transactions && transactions.length > 0) {
      message += `📋 Transazioni: ${transactions.length}\n`;
      const totale = transactions.reduce((sum, t) => sum + parseFloat(t.importo), 0);
      message += `💰 Totale Giornata: ${formatEuro(totale)}\n\n`;
    } else {
      message += `📋 Nessuna transazione per questa data\n\n`;
    }

    message += `📱 Sistema Gestione Cassa - Report Amministratore`;
    return message;
  };

  // Funzione per rilevazione mobile robusta (2025 best practices) - Dashboard scope
  const detectMobileDevice = () => {
    try {
      // 1. Metodo moderno: navigator.userAgentData (solo se mobile=true)
      if (navigator.userAgentData?.mobile === true) {
        console.log('Usando navigator.userAgentData.mobile=true');
        return true;
      }

      // 2. Metodo viewport: dimensione schermo come indicatore primario
      const viewportMobile = window.matchMedia("(max-width: 768px)").matches;
      
      // 3. Metodo touch: supporto touch come indicatore mobile
      const hasTouchSupport = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // 4. Fallback: User agent regex (più conservativo)
      const userAgentMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|Tablet/i.test(navigator.userAgent);
      
      console.log('Rilevazione mobile dettagliata:');
      console.log('- Viewport mobile (≤768px + coarse pointer):', viewportMobile);
      console.log('- Touch support:', hasTouchSupport);
      console.log('- User agent mobile:', userAgentMobile);
      console.log('- Viewport size:', window.innerWidth + 'x' + window.innerHeight);
      
      // Decisione finale: se viewport è mobile O (user agent è mobile E ha touch)
      const finalResult = viewportMobile || (userAgentMobile && hasTouchSupport);
      console.log('- Decisione finale mobile:', finalResult);
      
      return finalResult;
    } catch (error) {
      console.error('Errore rilevazione mobile, usando fallback:', error);
      return /Mobi|Android/i.test(navigator.userAgent);
    }
  };

  // Funzione per inviare messaggio WhatsApp per Administrator
  const sendWhatsAppMessage = () => {
    const message = generateWhatsAppMessageAdmin();
    const phoneNumber = "393453595804";
    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    console.log('Administrator - Apertura WhatsApp manuale');
    console.log('Administrator - WhatsApp URL generato:', whatsappURL.substring(0, 100) + '...');
    
    // Rileva se è dispositivo mobile con metodo multi-livello
    const isMobile = detectMobileDevice();
    console.log('Administrator - Dispositivo mobile rilevato:', isMobile);
    
    if (isMobile) {
      // Su mobile usa location.href per aprire WhatsApp direttamente
      console.log('Apertura WhatsApp su mobile con location.href');
      window.location.href = whatsappURL;
    } else {
      // Su desktop usa window.open
      console.log('Apertura WhatsApp su desktop con window.open');
      window.open(whatsappURL, '_blank');
    }
  };

  // Funzione per salvare la giornata (Administrator)
  const handleSaveDay = async () => {
    if (!transactions || transactions.length === 0) {
      toast.error('Non ci sono transazioni da salvare per questa data');
      return;
    }

    // Controlla se la giornata è già salvata
    const isLocked = dayLocks.some(lock => lock.data === selectedDate);
    if (isLocked) {
      toast.error('Questa giornata è già stata salvata definitivamente');
      return;
    }

    const operatorName = dayLocks.find(lock => lock.data === selectedDate)?.operator || 'Amministratore';
    
    const confirmSave = window.confirm(
      `Stai per salvare definitivamente le transazioni del ${formatDate(selectedDate)}.\n\n` +
      `Data: ${formatDate(selectedDate)}\n` +
      `Operatore: ${operatorName}\n` +
      `Transazioni registrate: ${transactions.length}\n\n` +
      `Dopo il salvataggio non potrai più modificare nulla per questo giorno.\n\n` +
      `Vuoi continuare?`
    );

    if (!confirmSave) return;

    setLoading(true);
    try {
      await axios.post(`${API}/day-lock`, {
        data: selectedDate,
        operator: operatorName
      });
      
      toast.success('Giorno salvato con successo! Invio messaggio WhatsApp...');
      
      // Invia automaticamente il messaggio WhatsApp per l'administrator
      setTimeout(() => {
        try {
          console.log('Administrator - Inizio tentativo invio WhatsApp automatico...');
          console.log('selectedDate:', selectedDate);
          console.log('operatorName:', operatorName);
          console.log('transactions:', transactions?.length || 0);
          
          const message = generateWhatsAppMessageAdmin();
          const phoneNumber = "393453595804";
          const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
          
          console.log('Administrator - WhatsApp URL:', whatsappURL.substring(0, 100) + '...');
          
          const isMobile = detectMobileDevice();
          console.log('Administrator - Dispositivo mobile rilevato:', isMobile);
          
          if (isMobile) {
            window.location.href = whatsappURL;
          } else {
            window.open(whatsappURL, '_blank');
          }
        } catch (error) {
          console.error('Administrator - Errore invio WhatsApp:', error);
          toast.error('Errore nell\'invio del messaggio WhatsApp');
        }
      }, 1000);
      
      // Ricarica i dati
      await loadData(selectedDate);
      
    } catch (error) {
      console.error('Errore nel salvare la giornata:', error);
      toast.error('Errore nel salvare la giornata: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Stato per editing transazioni (Administrator)
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editForm, setEditForm] = useState({ tipo: '', importo: '', descrizione: '' });

  // Funzione per eliminare transazione (Administrator)
  const handleDeleteTransaction = async (transactionId) => {
    const isLocked = dayLocks.some(lock => lock.data === selectedDate);
    
    // Administrator può eliminare transazioni anche da giorni salvati
    let confirmMessage = 'Sei sicuro di voler eliminare questa transazione?';
    if (isLocked) {
      confirmMessage = '⚠️ ATTENZIONE: Stai per eliminare una transazione da un giorno già salvato.\n\nQuesta operazione richiede particolare attenzione.\n\nSei sicuro di voler continuare?';
    }

    const confirmDelete = window.confirm(confirmMessage);
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/transactions/${transactionId}`);
      toast.success('Transazione eliminata con successo!');
      await loadData(selectedDate);
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error('Errore nell\'eliminare la transazione');
    }
  };

  // Funzione per iniziare l'editing (Administrator)
  const startEditTransaction = (transaction) => {
    const isLocked = dayLocks.some(lock => lock.data === selectedDate);
    
    // Administrator può modificare transazioni anche da giorni salvati
    if (isLocked) {
      const confirmEdit = window.confirm('⚠️ ATTENZIONE: Stai per modificare una transazione di un giorno già salvato.\n\nQuesta operazione richiede particolare attenzione.\n\nVuoi continuare?');
      if (!confirmEdit) return;
    }
    
    setEditingTransaction(transaction.id);
    setEditForm({
      tipo: transaction.tipo,
      importo: transaction.importo.toString(),
      descrizione: transaction.descrizione || ''
    });
  };

  // Funzione per salvare l'edit (Administrator)
  const handleSaveEdit = async () => {
    if (!editForm.importo || parseFloat(editForm.importo) <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }
    if (editForm.tipo === 'spese' && !editForm.descrizione.trim()) {
      toast.error('Descrizione obbligatoria per le spese');
      return;
    }

    try {
      await axios.put(`${API}/transactions/${editingTransaction}`, {
        tipo: editForm.tipo,
        importo: parseFloat(editForm.importo),
        descrizione: editForm.descrizione || '',
        modalita_pagamento_spesa: editForm.tipo === 'spese' ? 'contanti' : null
      });
      
      toast.success('Transazione modificata con successo!');
      setEditingTransaction(null);
      setEditForm({ tipo: '', importo: '', descrizione: '' });
      await loadData(selectedDate);
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error('Errore nel modificare la transazione');
    }
  };

  // Funzione per annullare l'edit (Administrator)
  const cancelEdit = () => {
    setEditingTransaction(null);
    setEditForm({ tipo: '', importo: '', descrizione: '' });
  };

  // Funzione per eliminare il dettaglio giornaliero (day-lock)
  const handleDeleteDayLock = async (date) => {
    const confirmDelete = window.confirm(
      `⚠️ ATTENZIONE: Stai per eliminare completamente il salvataggio della giornata del ${formatDate(date)}.\n\n` +
      `Questa operazione:\n` +
      `• Rimuoverà il blocco del giorno\n` +
      `• Permetterà nuovamente modifiche alle transazioni\n` +
      `• NON eliminerà le transazioni esistenti\n\n` +
      `Questa azione non può essere annullata.\n\n` +
      `Sei sicuro di voler continuare?`
    );

    if (!confirmDelete) return;

    try {
      // Trova il day-lock per questa data
      const dayLock = dayLocks.find(lock => lock.data === date);
      if (!dayLock) {
        toast.error('Salvataggio giornata non trovato');
        return;
      }

      await axios.delete(`${API}/day-lock/${dayLock.id}`);
      toast.success('Dettaglio giornaliero eliminato con successo! Ora puoi modificare le transazioni.');
      
      // Ricarica i dati per aggiornare lo stato
      await loadData(selectedDate);
      await loadDayLocks();
    } catch (error) {
      console.error('Error deleting day lock:', error);
      toast.error('Errore nell\'eliminare il dettaglio giornaliero: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Funzione per forzare l'aggiornamento dei contanti iniziali
  const forceUpdateInitialCash = async () => {
    const confirmUpdate = window.confirm(
      'Vuoi forzare l\'aggiornamento dei contanti iniziali a €2060?\n\n' +
      'Questa operazione aggiorna solo il valore dei contanti iniziali.'
    );

    if (!confirmUpdate) return;

    setLoading(true);
    try {
      await axios.post(`${API}/force-update-initial-cash`);
      toast.success('💰 Contanti iniziali aggiornati a €2060!');
      
      // Ricarica i dati
      await loadData(selectedDate);
      
      // Forza il reload della pagina per essere sicuri
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error forcing update initial cash:', error);
      toast.error('Errore nell\'aggiornare i contanti: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Funzione per resettare tutti i dati
  const handleResetAllData = async () => {
    const confirmReset = window.confirm(
      `🚨 RESET COMPLETO DEI DATI 🚨\n\n` +
      `ATTENZIONE: Questa operazione eliminerà PERMANENTEMENTE:\n\n` +
      `• TUTTE le transazioni\n` +
      `• TUTTI i salvataggi giornalieri\n` +
      `• TUTTI i target giornalieri\n` +
      `• TUTTI i versamenti bancari\n` +
      `• TUTTE le spese extra\n\n` +
      `E imposterà la disponibilità di cassa a €2.060,00\n\n` +
      `⚠️ QUESTA AZIONE NON PUÒ ESSERE ANNULLATA ⚠️\n\n` +
      `Sei ASSOLUTAMENTE sicuro di voler continuare?`
    );

    if (!confirmReset) return;

    // Seconda conferma
    const secondConfirm = window.confirm(
      `ULTIMA CONFERMA:\n\n` +
      `Stai per cancellare TUTTI i dati del sistema.\n` +
      `Inserisci la password per confermare.`
    );

    if (!secondConfirm) return;

    const password = window.prompt(
      `Inserisci la password di amministratore per completare il reset:`
    );

    if (!password) {
      toast.error('Reset annullato - password non inserita');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/reset-all-data`, {
        password: password
      });

      toast.success(`🎉 Reset completato! Cassa impostata a €2.060,00`);
      
      // Ricarica completamente la pagina per aggiornare tutti i dati
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      console.error('Error resetting data:', error);
      if (error.response?.status === 403) {
        toast.error('Password non corretta');
      } else {
        toast.error('Errore durante il reset: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4 lg:space-y-8">
        {/* Header - iOS Style Dark Mobile */}
        <div className="flex flex-col gap-4 lg:gap-6 mb-4 lg:mb-8">
          {/* Logo e titolo - Compatto su mobile */}
          <div className="flex items-center gap-3 lg:gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_ristocassa/artifacts/j7p7bjoh_Tavola%20disegno%2038.png" 
              alt="Logo Azienda" 
              className="h-12 w-12 lg:h-16 lg:w-16 object-contain rounded-xl bg-black p-1.5 lg:p-2 shadow-lg"
            />
            <div>
              <h1 className="text-xl lg:text-3xl font-bold text-white lg:text-gray-900">Dashboard Cassa</h1>
              <p className="text-sm lg:text-base text-slate-400 lg:text-gray-600">Gestione incassi e spese</p>
            </div>
          </div>

          {/* Controlli - iOS Style Dark su mobile */}
          <div className="flex flex-col gap-3 lg:gap-4 bg-slate-800/90 lg:bg-gradient-to-r lg:from-blue-50 lg:to-blue-50 p-4 lg:p-6 rounded-2xl border border-slate-700/50 lg:border lg:border-blue-100">
            {/* Data e orario - Row su mobile */}
            <div className="flex items-center gap-2 lg:gap-4">
              <div className="flex items-center gap-2 sm:gap-3 bg-slate-700 lg:bg-white px-3 sm:px-4 py-2 rounded-xl shadow-sm border border-slate-600 lg:border-gray-200 flex-1 min-w-0">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 lg:text-blue-600 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <Label className="text-xs text-slate-400 lg:text-gray-500 font-medium">Data</Label>
                  <Input
                    type="date"
                    value={selectedDate}
                    max={getTodayInRomeTimezone()}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="border-0 p-0 h-auto font-medium text-white lg:text-gray-900 bg-transparent focus:ring-0 text-sm sm:text-base"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-2 sm:gap-3 bg-slate-700 lg:bg-white px-3 sm:px-4 py-2 rounded-xl shadow-sm border border-slate-600 lg:border-gray-200 flex-shrink-0">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 lg:text-green-600 flex-shrink-0" />
                <div className="flex flex-col">
                  <Label className="text-xs text-slate-400 lg:text-gray-500 font-medium">Orario</Label>
                  <span className="font-medium text-white lg:text-gray-900 text-sm sm:text-base">{formatCurrentDateTime().split(' - ')[1]}</span>
                </div>
              </div>
            </div>

            {/* Controlli integrati - Desktop e Mobile */}
            <div className="flex flex-col gap-3 w-full lg:w-auto">
              {/* Prima riga: Bottoni principali */}
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <Button
                  onClick={sendWhatsAppMessage}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
                >
                  <MessageCircle className="h-4 w-4" />
                  Condividi Chiusura
                </Button>
                
                <Button
                  onClick={handleRefreshPage}
                  disabled={loading}
                  variant="outline"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-white lg:text-gray-900 hover:bg-slate-600 lg:hover:bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200 text-sm"
                >
                  <RotateCcw className="h-4 w-4" />
                  {loading ? 'Aggiornando...' : 'Aggiorna'}
                </Button>

                {/* Pulsante Carica Foglio Giornaliero */}
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleDailySheetUpload}
                    className="hidden"
                    id="daily-sheet-upload"
                    disabled={dailySheetUploading}
                  />
                  <Button
                    onClick={() => document.getElementById('daily-sheet-upload').click()}
                    disabled={dailySheetUploading}
                    className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-sm w-full sm:w-auto"
                  >
                    {dailySheetUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Analisi AI...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4" />
                        Carica Foglio
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Seconda riga: Gestione Admin - Responsive */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 w-full">
                {/* Gestione Operatori */}
                <Button
                  onClick={() => setShowOperatorManager(!showOperatorManager)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-white lg:text-gray-900 hover:bg-slate-600 lg:hover:bg-gray-50 text-xs font-medium"
                >
                  <Plus className="h-3 w-3" />
                  {showOperatorManager ? 'Chiudi' : 'Gestisci'} Operatori
                </Button>

                {/* Gestione Salvataggi */}
                <Button
                  onClick={() => setShowDayLocks(!showDayLocks)}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 lg:bg-white border-slate-600 lg:border-gray-200 text-white lg:text-gray-900 hover:bg-slate-600 lg:hover:bg-gray-50 text-xs font-medium"
                >
                  <Lock className="h-3 w-3" />
                  {showDayLocks ? 'Chiudi' : 'Visualizza'} Salvataggi
                </Button>

                {/* Modifica Restrizioni - Solo su desktop lg+ */}
                <Button
                  onClick={handleToggleOperatorRestriction}
                  variant="outline"
                  size="sm"
                  className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-lg bg-white border-gray-200 hover:bg-gray-50 text-xs font-medium"
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={operatorSettings.block_past_edits}
                      onChange={(e) => e.stopPropagation()}
                      className="w-3 h-3 rounded"
                    />
                    <span>Blocca Passate</span>
                  </div>
                </Button>
              </div>

              {/* Mobile: Restrizioni in riga separata */}
              <div className="lg:hidden">
                <Button
                  onClick={handleToggleOperatorRestriction}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border-gray-200 hover:bg-gray-50 text-xs font-medium w-full"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={operatorSettings.block_past_edits}
                      onChange={(e) => e.stopPropagation()}
                      className="w-3 h-3 rounded"
                    />
                    <span>Blocca Modifiche Passate</span>
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Lista Salvataggi (se visibile) */}
        {showDayLocks && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Giorni Salvati dagli Operatori</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                console.log('🔍 Rendering dayLocks, length:', dayLocks?.length);
                console.log('🔍 dayLocks data:', dayLocks);
                return null;
              })()}
              {dayLocks.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Nessun giorno salvato dagli operatori ({dayLocks.length} trovati)
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {dayLocks.map((lock) => (
                    <div key={lock.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 border rounded-lg bg-red-50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Lock className="h-4 w-4 text-red-600 flex-shrink-0" />
                          <span className="font-medium text-sm">{formatDate(lock.data)}</span>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <div>Operatore: <span className="font-medium">{lock.operator}</span></div>
                          <div>Salvato da: <span className="font-medium">{lock.locked_by_user}</span></div>
                          <div>Data salvataggio: <span className="font-medium">{new Date(lock.locked_at).toLocaleDateString('it-IT')} {new Date(lock.locked_at).toLocaleTimeString('it-IT')}</span></div>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleDeleteDayLockFromList(lock.id, lock.data)}
                        variant="destructive"
                        size="sm"
                        className="mt-2 sm:mt-0 sm:ml-4 text-xs"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Elimina
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Gestione Operatori (se visibile) */}
        {showOperatorManager && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Gestione Operatori</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-sm text-gray-600 mb-4">
                  Gestisci gli operatori del sistema. Puoi aggiungere nuovi operatori o modificare quelli esistenti.
                </div>
                
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium mb-3">Operatori Attuali:</h4>
                  <div className="space-y-2">
                    {operators.map((operator) => (
                      <div key={operator} className="flex items-center justify-between p-2 bg-white rounded">
                        {editingOperator === operator ? (
                          <div className="flex items-center gap-2 flex-1">
                            <Input
                              value={editOperatorName}
                              onChange={(e) => setEditOperatorName(e.target.value)}
                              className="flex-1"
                              placeholder="Nome operatore"
                            />
                            <Button onClick={handleSaveEditOperator} size="sm" className="text-xs bg-green-600 hover:bg-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Salva
                            </Button>
                            <Button onClick={handleCancelEditOperator} variant="outline" size="sm" className="text-xs">
                              <XCircle className="h-3 w-3 mr-1" />
                              Annulla
                            </Button>
                          </div>
                        ) : (
                          <>
                            <span className="font-medium">{operator}</span>
                            <div className="flex gap-2">
                              <Button 
                                onClick={() => handleEditOperator(operator)}
                                variant="outline" 
                                size="sm" 
                                className="text-xs"
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Modifica
                              </Button>
                              <Button 
                                onClick={() => handleDeleteOperator(operator)}
                                variant="destructive" 
                                size="sm" 
                                className="text-xs"
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                Elimina
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border rounded-lg p-4 bg-blue-50">
                  <h4 className="font-medium mb-3">Aggiungi Nuovo Operatore:</h4>
                  <div className="flex gap-2">
                    <Input
                      value={newOperatorName}
                      onChange={(e) => setNewOperatorName(e.target.value)}
                      placeholder="Nome operatore"
                      className="flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddOperator()}
                    />
                    <Button 
                      onClick={handleAddOperator}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Aggiungi
                    </Button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Il nuovo operatore potrà accedere con username e password predefinita "Ristopia!"
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <DashboardSummary summary={summary} />
      
      {/* Incasso del Giorno nella Dashboard */}
      <DailyTargetCard selectedDate={selectedDate} onTargetSet={() => loadData(selectedDate)} />
      
      {/* Grid per contanti disponibili e partitario */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-8 mt-8">
        <CashAvailableCard summary={summary} selectedDate={selectedDate} />
        <IntegratedCashControl selectedDate={selectedDate} onRefresh={summary} />
      </div>
      
      {/* Pulsante Salvataggio Giornata - Administrator */}
      <div className="flex justify-center mb-8">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 w-full max-w-md shadow-sm">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-800 text-lg font-semibold">
              <Save className="h-5 w-5" />
              Salvataggio Giornata
            </div>
            <p className="text-sm text-green-700">
              Salva definitivamente tutte le transazioni del {formatDate(selectedDate)}
            </p>
            <Button
              onClick={handleSaveDay}
              disabled={loading || !transactions || transactions.length === 0 || dayLocks.some(lock => lock.data === selectedDate)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : dayLocks.some(lock => lock.data === selectedDate) ? (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  🔒 Giorno Già Salvato
                </>
              ) : !transactions || transactions.length === 0 ? (
                <>
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Nessuna Transazione
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  💾 Salva Definitivamente
                </>
              )}
            </Button>
            {dayLocks.some(lock => lock.data === selectedDate) && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  onClick={sendWhatsAppMessage}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm py-1 px-3 rounded"
                >
                  📱 Condividi Chiusura
                </Button>
                <Button
                  onClick={() => handleDeleteDayLock(selectedDate)}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm py-1 px-3 rounded"
                  title="Elimina salvataggio giornata"
                >
                  🗑️ Elimina Dettaglio
                </Button>
                <Button
                  onClick={handleResetAllData}
                  className="bg-red-700 hover:bg-red-800 text-white text-sm py-1 px-3 rounded border-2 border-red-900"
                  title="Reset completo dati"
                >
                  🔄 Reset Completo
                </Button>
                <Button
                  onClick={forceUpdateInitialCash}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm py-1 px-3 rounded"
                  title="Forza aggiornamento contanti a €2060"
                >
                  💰 Fix Contanti
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Registrazione Batch Transaction Form */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-200 rounded-xl p-6 mb-8 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
            ✨ REGISTRAZIONE TRANSAZIONI
          </div>
        </div>
        <h3 className="text-xl font-bold text-blue-900 mb-2">Registrazione Batch - Più Transazioni Insieme</h3>
        <p className="text-blue-700 mb-6">
          Aggiungi più transazioni alla lista e salvale tutte insieme con un solo clic. 
          Ideale per registrare rapidamente più operazioni durante il turno.
        </p>
        <SimpleBatchForm 
          selectedDate={selectedDate}
          isLocked={false}
          onBatchSaved={() => loadData(selectedDate)}
        />
      </div>
      
      <TransactionsList 
        transactions={transactions} 
        onDeleteTransaction={handleDeleteTransaction} 
        onEditTransaction={() => loadData(selectedDate)}
        selectedDate={selectedDate} 
        userRole="administrator" 
      />

      {/* Modal per Chiusura POS */}
      {showPosClosureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-6 w-6" />
                Foglio Importato!
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {lastSheetData && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  {/* Campo data modificabile */}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700">📅 Data:</span>
                    <input
                      type="date"
                      value={editableSheetDate}
                      onChange={(e) => setEditableSheetDate(e.target.value)}
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    />
                  </div>
                  {editableSheetDate !== lastSheetData.data && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Data modificata (originale: {new Date(lastSheetData.data).toLocaleDateString('it-IT')})
                    </p>
                  )}
                  {lastSheetData.extracted.contanti > 0 && (
                    <p className="text-gray-700">💵 Contanti: €{lastSheetData.extracted.contanti.toFixed(2)}</p>
                  )}
                  {lastSheetData.extracted.pos > 0 && posClosuresLoaded.length === 0 && (
                    <p className="text-gray-700">💳 POS Totale: €{lastSheetData.extracted.pos.toFixed(2)}</p>
                  )}
                  {lastSheetData.extracted.spese?.length > 0 && (
                    <p className="text-gray-700">📝 Spese: {lastSheetData.extracted.spese.length}</p>
                  )}
                </div>
              )}

              {/* Lista chiusure POS già caricate */}
              {posClosuresLoaded.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg space-y-2 border border-blue-200">
                  <p className="font-medium text-blue-900">✅ Chiusure POS caricate: {posClosuresLoaded.length}</p>
                  {posClosuresLoaded.map((closure, idx) => (
                    <div key={idx} className="text-sm text-blue-700">
                      {closure.pos_details.map((p, i) => (
                        <span key={i}>{p.tipo}: €{p.importo.toFixed(2)}{i < closure.pos_details.length - 1 ? ' | ' : ''}</span>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-gray-700 mb-4">
                  {posClosuresLoaded.length === 0 
                    ? "Vuoi caricare la chiusura POS per differenziare i pagamenti (Visa, Bancomat, Mastercard)?"
                    : "Vuoi caricare un'altra chiusura POS?"
                  }
                </p>
                
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePosClosureUpload}
                    className="hidden"
                    id="pos-closure-upload"
                    disabled={posClosureUploading}
                  />
                  <Button
                    onClick={() => document.getElementById('pos-closure-upload').click()}
                    disabled={posClosureUploading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {posClosureUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Analisi AI...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {posClosuresLoaded.length === 0 ? 'Carica Chiusura POS' : 'Carica Altra Chiusura'}
                      </>
                    )}
                  </Button>
                  
                  <Button
                    onClick={handleSkipPosClosure}
                    disabled={posClosureUploading}
                    variant="outline"
                    className="w-full"
                  >
                    {posClosuresLoaded.length === 0 ? 'Salta (usa POS totale)' : 'Chiudi'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

const ReportsPage = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [yearlySummary, setYearlySummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('monthly'); // monthly, yearly, day

  const years = [2024, 2025, 2026];
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

  useEffect(() => {
    if (view === 'monthly') {
      loadMonthlySummary();
    } else if (view === 'yearly') {
      loadYearlySummary();
    }
  }, [selectedYear, selectedMonth, view]);

  const loadMonthlySummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/monthly-summary?year=${selectedYear}&month=${selectedMonth}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMonthlySummary(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento dati mensili');
    } finally {
      setLoading(false);
    }
  };

  const loadYearlySummary = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/yearly-summary?year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setYearlySummary(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento dati annuali');
    } finally {
      setLoading(false);
    }
  };

  const loadDayDetail = async (date) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/day-detail?date=${date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDayDetail(response.data);
      setSelectedDate(date);
      setView('day');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Errore nel caricamento dettagli giorno');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }

    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push(
        <button
          key={day}
          onClick={() => loadDayDetail(dateStr)}
          className="p-3 border rounded-lg hover:bg-blue-50 hover:border-blue-500 transition-all text-center"
        >
          <span className="font-semibold text-gray-900">{day}</span>
        </button>
      );
    }

    return days;
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2">
              <Button
                onClick={() => setView('monthly')}
                variant={view === 'monthly' ? 'default' : 'outline'}
                className={view === 'monthly' ? 'bg-orange-600' : ''}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Mensile
              </Button>
              <Button
                onClick={() => setView('yearly')}
                variant={view === 'yearly' ? 'default' : 'outline'}
                className={view === 'yearly' ? 'bg-orange-600' : ''}
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Annuale
              </Button>
            </div>

            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {view === 'monthly' && (
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m, idx) => (
                    <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {view === 'day' && (
              <Button onClick={() => setView('monthly')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Torna al Mese
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      )}

      {/* Monthly View - Calendar */}
      {!loading && view === 'monthly' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>📅 Calendario {months[selectedMonth - 1]} {selectedYear}</CardTitle>
              <CardDescription>Clicca su un giorno per vedere i dettagli</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'].map(day => (
                  <div key={day} className="text-center font-semibold text-gray-600 text-sm p-2">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-2">
                {renderCalendar()}
              </div>
            </CardContent>
          </Card>

          {monthlySummary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-green-600">💰 Riepilogo Mensile</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Incasso Totale Mese</p>
                    <p className="text-2xl font-bold text-green-600">€{monthlySummary.total_revenue?.toFixed(2)}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Transazioni Totali</p>
                    <p className="text-2xl font-bold text-blue-600">{monthlySummary.total_coperti}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Giorni con Dati</p>
                    <p className="text-2xl font-bold text-purple-600">{monthlySummary.days_with_data}</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Media Giornaliera Incasso</p>
                    <p className="text-xl font-bold text-orange-600">€{monthlySummary.avg_daily_revenue?.toFixed(2)}</p>
                  </div>
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-1">Media Giornaliera Transazioni</p>
                    <p className="text-xl font-bold text-pink-600">{monthlySummary.avg_daily_coperti?.toFixed(0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Card Breakdown Finanziario - ALTRO, Fiscale, Fatture - Mobile Optimized */}
          {monthlySummary && (monthlySummary.altro || monthlySummary.fiscale || monthlySummary.fatture) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {/* Card ALTRO */}
              <div 
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-500 via-rose-500 to-pink-500 p-4 sm:p-5 shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-[1.02]"
                data-testid="reports-breakdown-altro"
              >
                {/* Background decoration */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <Coins className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-xs font-medium text-white/80 bg-white/20 px-2 py-1 rounded-full">
                      {monthlySummary.altro?.quantita || 0} trans.
                    </span>
                  </div>
                  <p className="text-white/90 text-sm font-medium mb-1">ALTRO</p>
                  <p className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                    €{monthlySummary.altro?.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                </div>
              </div>

              {/* Card Fiscale */}
              <div 
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 via-blue-500 to-purple-500 p-4 sm:p-5 shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-[1.02]"
                data-testid="reports-breakdown-fiscale"
              >
                {/* Background decoration */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <Receipt className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-xs font-medium text-white/80 bg-white/20 px-2 py-1 rounded-full">
                      {monthlySummary.fiscale?.quantita || 0} scontrini
                    </span>
                  </div>
                  <p className="text-white/90 text-sm font-medium mb-1">FISCALE</p>
                  <p className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                    €{monthlySummary.fiscale?.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                </div>
              </div>

              {/* Card Fatture */}
              <div 
                className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-4 sm:p-5 shadow-lg hover:shadow-xl transform transition-all duration-300 hover:scale-[1.02]"
                data-testid="reports-breakdown-fatture"
              >
                {/* Background decoration */}
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                <div className="absolute -left-8 -bottom-8 w-32 h-32 bg-white/5 rounded-full"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="bg-white/20 backdrop-blur-sm p-2.5 rounded-xl">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <span className="text-xs font-medium text-white/80 bg-white/20 px-2 py-1 rounded-full">
                      {monthlySummary.fatture?.quantita || 0} fatture
                    </span>
                  </div>
                  <p className="text-white/90 text-sm font-medium mb-1">FATTURE</p>
                  <p className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                    €{monthlySummary.fatture?.importo?.toLocaleString('it-IT', {minimumFractionDigits: 2}) || '0,00'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Yearly View */}
      {!loading && view === 'yearly' && yearlySummary && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-purple-600">📊 Riepilogo Annuale {selectedYear}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Incasso Totale Anno</p>
                  <p className="text-2xl font-bold text-purple-600">€{yearlySummary.total_revenue?.toFixed(2)}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Transazioni Totali</p>
                  <p className="text-2xl font-bold text-blue-600">{yearlySummary.total_coperti}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Media Mensile Incasso</p>
                  <p className="text-xl font-bold text-green-600">€{yearlySummary.avg_monthly_revenue?.toFixed(2)}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Media Mensile Transazioni</p>
                  <p className="text-xl font-bold text-orange-600">{yearlySummary.avg_monthly_coperti?.toFixed(0)}</p>
                </div>
              </div>

              <h3 className="font-semibold text-lg mb-3">Breakdown Mensile</h3>
              <div className="space-y-2">
                {Object.entries(yearlySummary.monthly_breakdown || {}).map(([month, data]) => (
                  <div key={month} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-20 font-semibold text-gray-700">
                      {months[parseInt(month) - 1]}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1 text-sm">
                        <span className="text-gray-600">€{data.revenue?.toFixed(2)}</span>
                        <span className="text-gray-500">{data.coperti} transazioni</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full"
                          style={{ width: `${(data.revenue / yearlySummary.total_revenue) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Day Detail View */}
      {!loading && view === 'day' && dayDetail && (
        <>
          {dayDetail.has_data ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-orange-600">
                    📅 Dettagli {new Date(selectedDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-orange-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Transazioni</p>
                      <p className="text-3xl font-bold text-orange-600">{dayDetail.total_coperti}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Incasso Totale</p>
                      <p className="text-3xl font-bold text-green-600">€{dayDetail.total_revenue?.toFixed(2)}</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 mb-1">Scontrino Medio</p>
                      <p className="text-3xl font-bold text-blue-600">€{dayDetail.avg_receipt?.toFixed(2)}</p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h3 className="font-semibold text-lg mb-3">💰 Breakdown Finanziario</h3>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-red-50 p-3 rounded border-l-4 border-red-500">
                        <p className="text-xs font-medium text-gray-600">ALTRO</p>
                        <p className="text-lg font-bold">€{dayDetail.breakdown?.nero?.toFixed(2)}</p>
                      </div>
                      <div className="bg-blue-50 p-3 rounded border-l-4 border-blue-500">
                        <p className="text-xs font-medium text-gray-600">FISCALE</p>
                        <p className="text-lg font-bold">€{dayDetail.breakdown?.fiscale?.toFixed(2)}</p>
                      </div>
                      <div className="bg-green-50 p-3 rounded border-l-4 border-green-500">
                        <p className="text-xs font-medium text-gray-600">FATTURE</p>
                        <p className="text-lg font-bold">€{dayDetail.breakdown?.fatture?.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>🍽️ Articoli Venduti</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dayDetail.articoli?.slice(0, 20).map((art, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{art.descrizione}</p>
                          <p className="text-xs text-gray-500">Cod: {art.codice}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{art.quantita} pz</p>
                          <p className="text-sm text-green-600">€{art.importo?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                    {dayDetail.articoli?.length > 20 && (
                      <p className="text-center text-sm text-gray-500 py-2">
                        ... e altri {dayDetail.articoli.length - 20} articoli
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-900">Nessun dato disponibile</p>
                <p className="text-gray-600">Non ci sono dati per {selectedDate}</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );

};

// ============== REPORT MANCE ==============
const SpeseReportPage = () => {
  const [spese, setSpese] = useState([]);
  const [totals, setTotals] = useState({ totale: 0, totale_foglio: 0, totale_extra: 0 });
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1); // Primo del mese
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSpesa, setNewSpesa] = useState({ data: new Date().toISOString().split('T')[0], descrizione: '', importo: '' });
  const [filter, setFilter] = useState('all'); // all, foglio, extra

  const loadSpese = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/spese/all?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSpese(response.data.spese);
        setTotals({
          totale: response.data.totale,
          totale_foglio: response.data.totale_foglio,
          totale_extra: response.data.totale_extra
        });
      }
    } catch (error) {
      console.error('Error loading spese:', error);
      toast.error('Errore nel caricamento spese');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSpese();
  }, [startDate, endDate]);

  const handleAddSpesa = async () => {
    if (!newSpesa.descrizione || !newSpesa.importo) {
      toast.error('Compila tutti i campi');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/spese/add`, {
        data: newSpesa.data,
        descrizione: newSpesa.descrizione,
        importo: parseFloat(newSpesa.importo)
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Spesa aggiunta!');
      setShowAddModal(false);
      setNewSpesa({ data: new Date().toISOString().split('T')[0], descrizione: '', importo: '' });
      loadSpese();
    } catch (error) {
      toast.error('Errore nell\'aggiunta spesa');
    }
  };

  const handleDeleteSpesa = async (id) => {
    if (!confirm('Eliminare questa spesa?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/spese/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Spesa eliminata');
      loadSpese();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const filteredSpese = filter === 'all' 
    ? spese 
    : spese.filter(s => s.tipo_fonte === filter);

  const formatEuro = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  // Raggruppa per data
  const speseByDate = filteredSpese.reduce((acc, s) => {
    if (!acc[s.data]) acc[s.data] = [];
    acc[s.data].push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-red-500 to-rose-500 p-2.5 sm:p-3 rounded-xl shadow-lg">
            <Receipt className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Registro Spese</h1>
            <p className="text-sm text-gray-600 hidden sm:block">Tutte le spese registrate</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-red-600 hover:bg-red-700"
        >
          <Plus className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">Aggiungi Spesa</span>
          <span className="sm:hidden">Nuova</span>
        </Button>
      </div>

      {/* Filtri */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
            <div className="flex gap-2 flex-1">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 sm:flex-none border rounded-lg px-3 py-2 text-sm"
              />
              <span className="self-center text-gray-400">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 sm:flex-none border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex gap-1 sm:gap-2">
              <Button
                size="sm"
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-gray-800' : ''}
              >
                Tutte
              </Button>
              <Button
                size="sm"
                variant={filter === 'foglio' ? 'default' : 'outline'}
                onClick={() => setFilter('foglio')}
                className={filter === 'foglio' ? 'bg-blue-600' : ''}
              >
                Da Foglio
              </Button>
              <Button
                size="sm"
                variant={filter === 'extra' ? 'default' : 'outline'}
                onClick={() => setFilter('extra')}
                className={filter === 'extra' ? 'bg-orange-600' : ''}
              >
                Extra
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Totali Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-red-500 to-rose-600 text-white">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-xs sm:text-sm opacity-90">Totale Spese</p>
            <p className="text-lg sm:text-2xl font-bold">{formatEuro(totals.totale)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-xs sm:text-sm opacity-90">Da Foglio</p>
            <p className="text-lg sm:text-2xl font-bold">{formatEuro(totals.totale_foglio)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-500 to-amber-600 text-white">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-xs sm:text-sm opacity-90">Extra</p>
            <p className="text-lg sm:text-2xl font-bold">{formatEuro(totals.totale_extra)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista Spese */}
      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
        </div>
      ) : filteredSpese.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Receipt className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p>Nessuna spesa nel periodo selezionato</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(speseByDate).map(([data, speseGiorno]) => (
            <Card key={data}>
              <CardHeader className="pb-2 pt-4">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    {new Date(data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </CardTitle>
                  <span className="text-sm font-bold text-red-600">
                    {formatEuro(speseGiorno.reduce((sum, s) => sum + s.importo, 0))}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="divide-y">
                  {speseGiorno.map((spesa) => (
                    <div key={spesa.id} className="py-2 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          spesa.tipo_fonte === 'foglio' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {spesa.tipo_fonte === 'foglio' ? 'Foglio' : 'Extra'}
                        </span>
                        <span className="text-sm text-gray-700">{spesa.descrizione}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-red-600">{formatEuro(spesa.importo)}</span>
                        <button
                          onClick={() => handleDeleteSpesa(spesa.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Aggiungi Spesa */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5" />
                Nuova Spesa Extra
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                <input
                  type="date"
                  value={newSpesa.data}
                  onChange={(e) => setNewSpesa(prev => ({ ...prev, data: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <input
                  type="text"
                  value={newSpesa.descrizione}
                  onChange={(e) => setNewSpesa(prev => ({ ...prev, descrizione: e.target.value }))}
                  placeholder="Es: Materiale pulizia"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Importo (€)</label>
                <input
                  type="number"
                  step="0.01"
                  value={newSpesa.importo}
                  onChange={(e) => setNewSpesa(prev => ({ ...prev, importo: e.target.value }))}
                  placeholder="0.00"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleAddSpesa}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                >
                  Salva Spesa
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const ManceReportPage = () => {
  const [view, setView] = useState('monthly'); // monthly, yearly, range
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const years = [2024, 2025, 2026];
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (view === 'monthly') {
        response = await axios.get(`${API}/mance/monthly?year=${selectedYear}&month=${selectedMonth}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else if (view === 'yearly') {
        response = await axios.get(`${API}/mance/yearly?year=${selectedYear}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else if (view === 'range' && startDate && endDate) {
        response = await axios.get(`${API}/mance/range?start_date=${startDate}&end_date=${endDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      
      if (response?.data?.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error loading mance data:', error);
      toast.error('Errore nel caricamento dati mance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view !== 'range' || (startDate && endDate)) {
      loadData();
    }
  }, [view, selectedYear, selectedMonth, startDate, endDate]);

  const formatEuro = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="bg-gradient-to-r from-yellow-500 to-amber-500 p-3 rounded-xl shadow-lg">
          <DollarSign className="h-8 w-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Report Mance</h1>
          <p className="text-gray-600">Analisi delle mance per periodo</p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex gap-2">
              <Button
                onClick={() => setView('monthly')}
                variant={view === 'monthly' ? 'default' : 'outline'}
                className={view === 'monthly' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                Mensile
              </Button>
              <Button
                onClick={() => setView('yearly')}
                variant={view === 'yearly' ? 'default' : 'outline'}
                className={view === 'yearly' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                Annuale
              </Button>
              <Button
                onClick={() => setView('range')}
                variant={view === 'range' ? 'default' : 'outline'}
                className={view === 'range' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                Periodo
              </Button>
            </div>

            {view === 'monthly' && (
              <div className="flex gap-2">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border rounded-lg px-3 py-2"
                >
                  {months.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border rounded-lg px-3 py-2"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            {view === 'yearly' && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border rounded-lg px-3 py-2"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            )}

            {view === 'range' && (
              <div className="flex gap-2 items-center">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
                <span className="text-gray-500">→</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Caricamento dati mance...</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!loading && data && (
        <>
          {/* Totale Card */}
          <Card className="bg-gradient-to-r from-yellow-50 to-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-700 font-medium">
                    {view === 'monthly' ? `Totale Mance ${months[selectedMonth - 1]} ${selectedYear}` : 
                     view === 'yearly' ? `Totale Mance ${selectedYear}` : 
                     `Totale Mance Periodo`}
                  </p>
                  <p className="text-4xl font-bold text-amber-700 mt-1">
                    {formatEuro(data.totale_mese || data.totale_anno || data.totale)}
                  </p>
                </div>
                <div className="bg-amber-500 p-4 rounded-full">
                  <DollarSign className="h-10 w-10 text-white" />
                </div>
              </div>
              {data.num_giorni && (
                <p className="text-sm text-amber-600 mt-2">
                  {data.num_giorni} giorni con mance registrate
                </p>
              )}
            </CardContent>
          </Card>

          {/* Per Tipo POS */}
          {data.per_tipo && data.per_tipo.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-amber-700 flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Mance per Tipo POS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.per_tipo.map((tipo, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-700">{tipo.tipo}</span>
                      <span className="font-bold text-amber-600">{formatEuro(tipo.totale)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Per Mese (yearly view) */}
          {view === 'yearly' && data.per_mese && data.per_mese.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-amber-700 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Mance per Mese
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {data.per_mese.map((mese, idx) => (
                    <div key={idx} className="p-3 bg-amber-50 rounded-lg text-center border border-amber-100">
                      <p className="text-sm text-gray-600">{mese.nome}</p>
                      <p className="font-bold text-amber-700">{formatEuro(mese.totale)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dettaglio Giorni */}
          {data.giorni && data.giorni.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-amber-700 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Dettaglio Giornaliero
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {data.giorni.map((giorno, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div>
                        <p className="font-medium text-gray-900">
                          {new Date(giorno.data).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </p>
                        <div className="flex gap-2 mt-1">
                          {giorno.dettagli.map((d, i) => (
                            <span key={i} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                              {d.tipo}: {formatEuro(d.importo)}
                            </span>
                          ))}
                        </div>
                      </div>
                      <span className="font-bold text-amber-600">{formatEuro(giorno.totale)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {(!data.giorni || data.giorni.length === 0) && (!data.per_mese || data.per_mese.length === 0) && (
            <Card>
              <CardContent className="py-12 text-center">
                <DollarSign className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-900">Nessuna mancia registrata</p>
                <p className="text-gray-600">Non ci sono dati mance per il periodo selezionato</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// Componente Dettaglio Fornitore con Prodotti e Storico Acquisti
const FornitoreDettaglio = ({ fornitore, selectedAnno, formatCurrency, formatDate }) => {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedProdotto, setExpandedProdotto] = useState({});
  const [searchProdotto, setSearchProdotto] = useState('');
  
  React.useEffect(() => {
    loadProdottiFornitore();
  }, [fornitore.fornitore, selectedAnno]);
  
  const loadProdottiFornitore = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const encodedFornitore = encodeURIComponent(fornitore.fornitore);
      const response = await axios.get(
        `${API}/aruba/statistiche/fornitore/${encodedFornitore}/prodotti?anno=${selectedAnno}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setProdotti(response.data.prodotti || []);
    } catch (error) {
      console.error('Error loading prodotti fornitore:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getTrendIcon = (acquisti) => {
    if (!acquisti || acquisti.length < 2) return null;
    const ultimo = acquisti[0]?.prezzo_netto || 0;
    const penultimo = acquisti[1]?.prezzo_netto || 0;
    if (ultimo > penultimo) return { icon: '📈', color: 'text-red-400', label: 'In aumento' };
    if (ultimo < penultimo) return { icon: '📉', color: 'text-green-400', label: 'In calo' };
    return { icon: '➡️', color: 'text-slate-400', label: 'Stabile' };
  };
  
  const filteredProdotti = prodotti.filter(p => 
    !searchProdotto || p.prodotto?.toLowerCase().includes(searchProdotto.toLowerCase())
  );
  
  return (
    <div className="border-t border-slate-700/50 lg:border-gray-200 bg-slate-900/50 lg:bg-gray-50">
      {/* Info Fornitore */}
      <div className="p-4 border-b border-slate-700/30 lg:border-gray-200">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Imponibile</p>
            <p className="font-bold text-white lg:text-gray-900">{formatCurrency(fornitore.totale_imponibile)}</p>
          </div>
          <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">IVA</p>
            <p className="font-bold text-orange-400 lg:text-orange-600">{formatCurrency(fornitore.totale_iva)}</p>
          </div>
          <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Prima Fattura</p>
            <p className="font-bold text-white lg:text-gray-900">{formatDate(fornitore.prima_fattura)}</p>
          </div>
          <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide">Ultima Fattura</p>
            <p className="font-bold text-white lg:text-gray-900">{formatDate(fornitore.ultima_fattura)}</p>
          </div>
        </div>
      </div>
      
      {/* Prodotti del Fornitore */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-white lg:text-gray-900 flex items-center gap-2">
            <Package className="h-4 w-4 text-purple-400" />
            Prodotti Acquistati ({prodotti.length})
          </h4>
          <Input
            placeholder="Cerca..."
            value={searchProdotto}
            onChange={(e) => setSearchProdotto(e.target.value)}
            className="w-40 h-8 text-xs bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200"
          />
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : filteredProdotti.length === 0 ? (
          <p className="text-center text-slate-500 py-4 text-sm">Nessun prodotto trovato</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {filteredProdotti.map((prod, pIdx) => {
              const trend = getTrendIcon(prod.acquisti);
              const hasSconto = prod.sconto_medio > 0;
              const variazione = prod.prezzo_max && prod.prezzo_min && prod.prezzo_min > 0
                ? ((prod.prezzo_max - prod.prezzo_min) / prod.prezzo_min * 100).toFixed(0)
                : 0;
              
              return (
                <div key={pIdx} className="bg-slate-800/70 lg:bg-white rounded-xl border border-slate-700/50 lg:border-gray-200 overflow-hidden">
                  <div 
                    className="p-3 cursor-pointer hover:bg-slate-700/50 lg:hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedProdotto(prev => ({ ...prev, [prod.prodotto]: !prev[prod.prodotto] }))}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-white lg:text-gray-900 text-sm truncate">{prod.prodotto}</span>
                          {hasSconto && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">
                              -{prod.sconto_medio?.toFixed(1)}%
                            </span>
                          )}
                          {trend && (
                            <span className={`text-[10px] ${trend.color}`} title={trend.label}>{trend.icon}</span>
                          )}
                        </div>
                        <div className="flex gap-2 mt-1 text-[10px] text-slate-400">
                          <span>{prod.numero_acquisti} acquisti</span>
                          <span>•</span>
                          <span>Qta: {prod.quantita_totale} {prod.unita_misura || 'pz'}</span>
                          {variazione > 15 && (
                            <>
                              <span>•</span>
                              <span className="text-orange-400">±{variazione}% variazione</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-purple-400 lg:text-purple-600">{formatCurrency(prod.importo_totale)}</p>
                        <p className="text-[10px] text-slate-500">Media: {formatCurrency(prod.prezzo_medio)}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Storico Acquisti Espanso */}
                  {expandedProdotto[prod.prodotto] && prod.acquisti && (
                    <div className="border-t border-slate-700/30 lg:border-gray-200 bg-slate-900/50 lg:bg-gray-100 p-3">
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="text-center p-2 bg-slate-800/50 lg:bg-white rounded-lg border border-slate-700/30 lg:border-gray-200">
                          <p className="text-[9px] text-slate-500 uppercase">Min</p>
                          <p className="font-bold text-green-400 lg:text-green-600 text-sm">{formatCurrency(prod.prezzo_min)}</p>
                        </div>
                        <div className="text-center p-2 bg-slate-800/50 lg:bg-white rounded-lg border border-slate-700/30 lg:border-gray-200">
                          <p className="text-[9px] text-slate-500 uppercase">Media</p>
                          <p className="font-bold text-white lg:text-gray-900 text-sm">{formatCurrency(prod.prezzo_medio)}</p>
                        </div>
                        <div className="text-center p-2 bg-slate-800/50 lg:bg-white rounded-lg border border-slate-700/30 lg:border-gray-200">
                          <p className="text-[9px] text-slate-500 uppercase">Max</p>
                          <p className="font-bold text-red-400 lg:text-red-600 text-sm">{formatCurrency(prod.prezzo_max)}</p>
                        </div>
                      </div>
                      
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2 font-semibold">
                        Storico Acquisti ({prod.acquisti.length})
                      </p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {prod.acquisti.map((acq, aIdx) => {
                          const prevAcq = prod.acquisti[aIdx + 1];
                          const priceDiff = prevAcq ? ((acq.prezzo_netto - prevAcq.prezzo_netto) / prevAcq.prezzo_netto * 100) : 0;
                          
                          return (
                            <div key={aIdx} className="flex items-center justify-between p-2 bg-slate-800/50 lg:bg-white rounded-lg border border-slate-700/30 lg:border-gray-200 text-xs">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-400 lg:text-gray-500 w-20">{formatDate(acq.data)}</span>
                                <span className="text-slate-500 lg:text-gray-400 hidden sm:inline">Ft. {acq.numero_fattura}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-slate-300 lg:text-gray-600">
                                  {acq.quantita} {prod.unita_misura || 'pz'}
                                </span>
                                <div className="text-right">
                                  <span className="font-medium text-white lg:text-gray-900">{formatCurrency(acq.prezzo_netto)}</span>
                                  {acq.sconto > 0 && (
                                    <span className="text-green-400 ml-1 text-[10px]">(-{acq.sconto.toFixed(0)}%)</span>
                                  )}
                                </div>
                                {priceDiff !== 0 && Math.abs(priceDiff) > 1 && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    priceDiff > 0 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                                  }`}>
                                    {priceDiff > 0 ? '+' : ''}{priceDiff.toFixed(0)}%
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Fatture Elettroniche Page - Aruba Integration
const FattureElettronichePage = () => {
  const [fatture, setFatture] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [selectedFattura, setSelectedFattura] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [view, setView] = useState('anni'); // anni, list, fornitori, prodotti, categorie
  const [statisticheFornitori, setStatisticheFornitori] = useState([]);
  const [statisticheProdotti, setStatisticheProdotti] = useState([]);
  const [statisticheAnni, setStatisticheAnni] = useState([]);
  const [statisticheCategorie, setStatisticheCategorie] = useState([]);
  const [selectedAnno, setSelectedAnno] = useState(new Date().getFullYear());
  const [filters, setFilters] = useState({
    fornitore: '',
    categoria: '',
    sottocategoria: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFornitori, setExpandedFornitori] = useState({});
  const [expandedProdotti, setExpandedProdotti] = useState({});
  const [expandedCategorie, setExpandedCategorie] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Stato per categorizzazione AI in background
  const [catStatus, setCatStatus] = useState({ running: false, progress: 0, total: 0, categorizzate: 0, errori: 0 });
  
  // Stato per ricerca articoli
  const [searchArticoli, setSearchArticoli] = useState('');
  const [articoliTrovati, setArticoliTrovati] = useState([]);
  const [loadingArticoli, setLoadingArticoli] = useState(false);
  const [showArticoliSearch, setShowArticoliSearch] = useState(false);
  
  // Ref per abort controller
  const abortControllerRef = React.useRef(null);
  
  // Cache per i dati già caricati - evita ricaricamenti inutili
  const dataCache = React.useRef({});
  
  // Genera chiave cache basata su view e anno
  const getCacheKey = useCallback((viewType, anno) => {
    return `${viewType}_${anno}`;
  }, []);
  
  // Load data on mount
  useEffect(() => {
    loadStatisticheAnni();
  }, []);
  
  // Polling stato categorizzazione quando siamo nella view categorie
  useEffect(() => {
    let interval;
    const checkCatStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/aruba/categorizzazione/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCatStatus(response.data);
        
        // Se è appena finita, ricarica le categorie
        if (!response.data.running && catStatus.running) {
          loadStatisticheCategorie();
        }
      } catch (err) {
        console.log('Errore check stato categorizzazione');
      }
    };
    
    if (view === 'categorie') {
      checkCatStatus(); // Check immediato
      interval = setInterval(checkCatStatus, 3000); // Poll ogni 3 secondi
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [view, catStatus.running]);
  
  // Load data when year or view changes - con abort delle richieste precedenti e cache
  useEffect(() => {
    // Per la view 'anni', i dati sono già caricati all'avvio - niente da fare
    if (view === 'anni') {
      setLoading(false);
      return;
    }
    
    // Controlla se i dati sono già in cache (solo se non ci sono filtri attivi per la vista list)
    const hasActiveFilters = filters.categoria || filters.sottocategoria || filters.fornitore;
    const cacheKey = getCacheKey(view, selectedAnno);
    
    // Non usare cache se ci sono filtri attivi nella vista list
    if (!hasActiveFilters && dataCache.current[cacheKey]) {
      const cached = dataCache.current[cacheKey];
      if (view === 'list') setFatture(cached);
      else if (view === 'fornitori') setStatisticheFornitori(cached);
      else if (view === 'prodotti') setStatisticheProdotti(cached);
      else if (view === 'categorie') setStatisticheCategorie(cached);
      setLoading(false);
      console.log('📊 FatturePage: Dati caricati da cache per', cacheKey);
      return;
    }
    
    // Cancella richieste precedenti
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    const loadData = async () => {
      if (view === 'list') {
        await loadFattureAnno(abortControllerRef.current.signal);
      } else if (view === 'fornitori') {
        await loadStatisticheFornitori(abortControllerRef.current.signal);
      } else if (view === 'prodotti') {
        await loadStatisticheProdotti(abortControllerRef.current.signal);
      } else if (view === 'categorie') {
        await loadStatisticheCategorie(abortControllerRef.current.signal);
      }
    };
    
    loadData();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [view, selectedAnno, getCacheKey, filters.categoria, filters.sottocategoria, filters.fornitore]);
  
  // Categorizza fatture esistenti
  const categorizzaFatture = async () => {
    setCategorizing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/aruba/categorizza-batch`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        // Reload data
        await loadStatisticheAnni();
        if (view === 'categorie') await loadStatisticheCategorie();
        if (view === 'list') await loadFattureAnno();
      } else if (response.data.already_running) {
        // Categorization already in progress - show info toast instead of error
        toast.info(`ℹ️ Categorizzazione già in corso (${response.data.progress}/${response.data.total})`);
        // Update catStatus to show the progress banner
        setCatStatus(prev => ({ 
          ...prev, 
          running: true, 
          progress: response.data.progress,
          total: response.data.total 
        }));
      } else {
        toast.error(`❌ ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error categorizing:', error);
      toast.error('Errore categorizzazione: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCategorizing(false);
    }
  };
  
  const loadStatisticheAnni = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/aruba/statistiche/anni`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStatisticheAnni(response.data.anni || []);
      if (response.data.anni?.length > 0) {
        setSelectedAnno(response.data.anni[0].anno);
      }
    } catch (error) {
      console.error('Error loading statistiche anni:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadFattureAnno = async (signal = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.fornitore) params.append('fornitore', filters.fornitore);
      if (filters.categoria) params.append('categoria', filters.categoria);
      if (filters.sottocategoria) params.append('sottocategoria', filters.sottocategoria);
      
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      if (signal) config.signal = signal;
      
      const response = await axios.get(`${API}/aruba/fatture/anno/${selectedAnno}?${params.toString()}`, config);
      const loadedFatture = response.data.fatture || [];
      setFatture(loadedFatture);
      
      // Salva in cache
      const cacheKey = getCacheKey('list', selectedAnno);
      dataCache.current[cacheKey] = loadedFatture;
      console.log('📊 FatturePage: Dati salvati in cache per', cacheKey);
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        return;
      }
      console.error('Error loading fatture:', error);
      toast.error('Errore caricamento fatture');
    } finally {
      setLoading(false);
    }
  };
  
  const loadStatisticheFornitori = async (signal = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const startDate = `${selectedAnno}-01-01`;
      const endDate = `${selectedAnno}-12-31`;
      
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      if (signal) config.signal = signal;
      
      const response = await axios.get(`${API}/aruba/statistiche/fornitori?data_inizio=${startDate}&data_fine=${endDate}`, config);
      const loadedFornitori = response.data.fornitori || [];
      setStatisticheFornitori(loadedFornitori);
      
      // Salva in cache
      const cacheKey = getCacheKey('fornitori', selectedAnno);
      dataCache.current[cacheKey] = loadedFornitori;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        return;
      }
      console.error('Error loading statistiche fornitori:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadStatisticheProdotti = async (signal = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const startDate = `${selectedAnno}-01-01`;
      const endDate = `${selectedAnno}-12-31`;
      
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      if (signal) config.signal = signal;
      
      const response = await axios.get(`${API}/aruba/statistiche/prodotti?data_inizio=${startDate}&data_fine=${endDate}`, config);
      const loadedProdotti = response.data.prodotti || [];
      setStatisticheProdotti(loadedProdotti);
      
      // Salva in cache
      const cacheKey = getCacheKey('prodotti', selectedAnno);
      dataCache.current[cacheKey] = loadedProdotti;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        return;
      }
      console.error('Error loading statistiche prodotti:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadStatisticheCategorie = async (signal = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      if (signal) config.signal = signal;
      
      const response = await axios.get(`${API}/aruba/statistiche/categorie?anno=${selectedAnno}`, config);
      const loadedCategorie = response.data.categorie || [];
      setStatisticheCategorie(loadedCategorie);
      
      // Salva in cache
      const cacheKey = getCacheKey('categorie', selectedAnno);
      dataCache.current[cacheKey] = loadedCategorie;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError') {
        return;
      }
      console.error('Error loading statistiche categorie:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Upload fatture XML/ZIP
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await axios.post(`${API}/aruba/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        await loadStatisticheAnni();
        if (view === 'list') await loadFattureAnno();
        if (view === 'fornitori') await loadStatisticheFornitori();
        if (view === 'prodotti') await loadStatisticheProdotti();
        if (view === 'categorie') await loadStatisticheCategorie();
      } else {
        toast.error(`❌ ${response.data.message}`);
      }
      
      // Show summary of errors (not each one individually)
      if (response.data.errori && response.data.errori.length > 0 && response.data.errori.length <= 3) {
        // Show individual errors only if few
        response.data.errori.forEach(err => toast.warning(err));
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Errore upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Drag & Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };
  
  const viewFatturaDetail = async (fatturaId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/aruba/fatture/${fatturaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedFattura(response.data);
      setShowDetail(true);
    } catch (error) {
      toast.error('Errore caricamento dettagli fattura');
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT');
    } catch {
      return dateStr;
    }
  };
  
  // Cerca articoli nelle fatture
  const cercaArticoli = async (query) => {
    if (!query || query.length < 2) {
      setArticoliTrovati([]);
      return;
    }
    
    setLoadingArticoli(true);
    try {
      const token = localStorage.getItem('token');
      // Cerca in tutti gli anni per trovare tutti i risultati
      const response = await axios.get(`${API}/aruba/fatture/cerca-articoli`, {
        params: { q: query, limit: 50 },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setArticoliTrovati(response.data.articoli || []);
    } catch (error) {
      console.error('Errore ricerca articoli:', error);
      toast.error('Errore nella ricerca');
    } finally {
      setLoadingArticoli(false);
    }
  };
  
  // Filter fatture by search term
  const filteredFatture = fatture.filter(f => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      f.fornitore?.denominazione?.toLowerCase().includes(search) ||
      f.numero_fattura?.toLowerCase().includes(search) ||
      f.fornitore?.partita_iva?.includes(search)
    );
  });
  
  // Toggle expand for fornitori
  const toggleFornitore = (fornitore) => {
    setExpandedFornitori(prev => ({
      ...prev,
      [fornitore]: !prev[fornitore]
    }));
  };
  
  // Toggle expand for prodotti
  const toggleProdotto = (prodotto) => {
    setExpandedProdotti(prev => ({
      ...prev,
      [prodotto]: !prev[prodotto]
    }));
  };
  
  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6 overflow-x-hidden">
      {/* Header - Dark Mobile */}
      <div className="flex flex-col gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-white lg:text-gray-900 flex items-center gap-2">
              <FileSearch className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7 text-blue-400 lg:text-blue-600" />
              Fatture Elettroniche
            </h1>
            <a 
              href="https://fatturapa.aruba.it" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-blue-400 lg:text-blue-500 hover:text-blue-300 lg:hover:text-blue-600 underline"
            >
              Aruba FT
            </a>
          </div>
          <p className="text-xs sm:text-sm text-slate-400 lg:text-gray-600 mt-1">
            Importa e analizza le fatture passive
          </p>
        </div>
        
        {/* Buttons - Stack on mobile */}
        <div className="grid grid-cols-2 sm:flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => handleFileUpload(e.target.files)}
            accept=".xml,.p7m,.zip"
            multiple
            className="hidden"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-green-600 hover:bg-green-700 text-sm px-3 py-2"
            size="sm"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                <span className="hidden sm:inline">Caricando...</span>
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-1" />
                <span>Carica</span>
              </>
            )}
          </Button>
          <Button
            onClick={categorizzaFatture}
            disabled={categorizing}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-3 py-2"
          >
            {categorizing ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                <span className="hidden sm:inline">AI...</span>
              </>
            ) : (
              <>
                <PieChart className="h-4 w-4 mr-1" />
                <span>AI Cat.</span>
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Barra Ricerca Articoli - Dark Mobile */}
      <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl shadow-sm border border-slate-700/50 lg:border-gray-100 p-3 lg:p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
            <Input
              placeholder="Cerca articoli..."
              value={searchArticoli}
              onChange={(e) => setSearchArticoli(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  cercaArticoli(searchArticoli);
                }
              }}
              className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400 h-10"
            />
          </div>
          <Button
            onClick={() => cercaArticoli(searchArticoli)}
            disabled={loadingArticoli || searchArticoli.length < 2}
            className="bg-blue-600 hover:bg-blue-700 px-3"
            size="sm"
          >
            {loadingArticoli ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
          {articoliTrovati.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setArticoliTrovati([]); setSearchArticoli(''); }}
              className="border-slate-600 lg:border-gray-200 text-slate-300 lg:text-gray-900 px-3"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Risultati Ricerca - Dark Mobile */}
        {articoliTrovati.length > 0 && (
          <div className="mt-3 border-t border-slate-700/50 lg:border-gray-100 pt-3">
            <p className="text-xs font-medium text-slate-400 lg:text-gray-700 mb-2">
              {articoliTrovati.length} risultati per &quot;{searchArticoli}&quot;
            </p>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {articoliTrovati.map((art, idx) => (
                <div key={idx} className="bg-slate-700/50 lg:bg-gray-50 rounded-xl lg:rounded-lg p-3 border border-slate-600/50 lg:border-gray-100 hover:border-blue-500/50 lg:hover:border-blue-200 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-medium text-white lg:text-gray-900">{art.descrizione}</p>
                      <p className="text-xs text-slate-400 lg:text-gray-500 mt-1">
                        <span className="font-medium">{art.fornitore}</span> • 
                        Fattura {art.numero_fattura} del {formatDate(art.data_fattura)}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      {art.sconto_percentuale ? (
                        <>
                          <p className="text-xs text-slate-500 lg:text-gray-400 line-through">{formatCurrency(art.prezzo_unitario)}</p>
                          <p className="font-bold text-green-400 lg:text-green-600">{formatCurrency(art.prezzo_unitario_netto || art.prezzo_unitario)}</p>
                          <p className="text-xs text-green-400 lg:text-green-600">-{art.sconto_percentuale}%</p>
                        </>
                      ) : (
                        <p className="font-bold text-blue-400 lg:text-blue-600">{formatCurrency(art.prezzo_unitario)}</p>
                      )}
                      <p className="text-xs text-slate-400 lg:text-gray-500">
                        {art.quantita} {art.unita_misura || 'pz'} = {formatCurrency(art.prezzo_totale)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Upload Area - Compact on mobile */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl lg:rounded-xl p-4 lg:p-6 text-center transition-all ${
          dragOver 
            ? 'border-green-500 bg-green-500/10' 
            : 'border-slate-600 lg:border-gray-300 bg-slate-800/50 lg:bg-gray-50 hover:border-slate-500 lg:hover:border-gray-400'
        }`}
      >
        <Upload className={`h-8 w-8 lg:h-10 lg:w-10 mx-auto mb-2 ${dragOver ? 'text-green-500' : 'text-slate-400 lg:text-gray-400'}`} />
        <p className="text-xs lg:text-sm font-medium text-slate-200 lg:text-gray-700">
          Trascina qui i file XML
        </p>
        <p className="text-[10px] lg:text-xs text-slate-400 lg:text-gray-500 mt-1">
          .xml, .p7m, .zip • Upload multiplo
        </p>
      </div>
      
      {/* Year Selector - Compact */}
      {statisticheAnni.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs lg:text-sm text-slate-400 lg:text-gray-600 font-medium">Anno:</span>
          {statisticheAnni.map(anno => (
            <button
              key={anno.anno}
              onClick={() => setSelectedAnno(anno.anno)}
              className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-bold transition-all ${
                selectedAnno === anno.anno
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-slate-700 lg:bg-white text-slate-200 lg:text-gray-700 border border-slate-600 lg:border-gray-200'
              }`}
            >
              {anno.anno}
              <span className={`ml-1 ${selectedAnno === anno.anno ? 'text-blue-100' : 'text-slate-400 lg:text-gray-400'}`}>
                ({anno.totale_fatture})
              </span>
            </button>
          ))}
        </div>
      )}
      
      {/* View Tabs - Scrollable on mobile */}
      <div className="flex gap-1 bg-slate-800 lg:bg-gray-100 p-1 rounded-xl lg:rounded-lg overflow-x-auto">
        <button
          onClick={() => setView('anni')}
          data-testid="tab-anni"
          className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-all whitespace-nowrap ${
            view === 'anni' ? 'bg-slate-700 lg:bg-white shadow text-blue-400 lg:text-blue-600' : 'text-slate-400 lg:text-gray-600'
          }`}
        >
          <Calendar className="h-3 w-3 lg:h-4 lg:w-4 inline mr-1" />
          Anni
        </button>
        <button
          onClick={() => setView('list')}
          data-testid="tab-lista"
          className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-all whitespace-nowrap ${
            view === 'list' ? 'bg-slate-700 lg:bg-white shadow text-blue-400 lg:text-blue-600' : 'text-slate-400 lg:text-gray-600'
          }`}
        >
          <FileText className="h-3 w-3 lg:h-4 lg:w-4 inline mr-1" />
          Lista
        </button>
        <button
          onClick={() => setView('categorie')}
          data-testid="tab-categorie"
          className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-all whitespace-nowrap ${
            view === 'categorie' ? 'bg-slate-700 lg:bg-white shadow text-blue-400 lg:text-blue-600' : 'text-slate-400 lg:text-gray-600'
          }`}
        >
          <PieChart className="h-3 w-3 lg:h-4 lg:w-4 inline mr-1" />
          Cat.
        </button>
        <button
          onClick={() => setView('fornitori')}
          data-testid="tab-fornitori"
          className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-all whitespace-nowrap ${
            view === 'fornitori' ? 'bg-slate-700 lg:bg-white shadow text-blue-400 lg:text-blue-600' : 'text-slate-400 lg:text-gray-600'
          }`}
        >
          <Truck className="h-3 w-3 lg:h-4 lg:w-4 inline mr-1" />
          Forn.
        </button>
        <button
          onClick={() => setView('prodotti')}
          className={`px-3 py-1.5 rounded-lg text-xs lg:text-sm font-medium transition-all whitespace-nowrap ${
            view === 'prodotti' ? 'bg-slate-700 lg:bg-white shadow text-blue-400 lg:text-blue-600' : 'text-slate-400 lg:text-gray-600'
          }`}
        >
          <Package className="h-3 w-3 lg:h-4 lg:w-4 inline mr-1" />
          Prod.
        </button>
      </div>
      
      {/* Content based on view */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400 lg:text-blue-600" />
        </div>
      ) : (
        <>
          {/* ANNI/RIEPILOGO VIEW - Dark Mobile */}
          {view === 'anni' && (
            <div className="space-y-3">
              {statisticheAnni.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-8 text-center">
                  <FileSearch className="h-10 w-10 text-slate-500 lg:text-gray-300 mx-auto mb-3" />
                  <p className="text-slate-200 lg:text-gray-600 font-medium text-sm">Nessuna fattura importata</p>
                  <p className="text-xs text-slate-400 lg:text-gray-500 mt-1">
                    Carica i file XML o ZIP
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                  {statisticheAnni.map(anno => (
                    <div 
                      key={anno.anno} 
                      className={`bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border cursor-pointer transition-all hover:shadow-lg p-4 ${
                        selectedAnno === anno.anno ? 'ring-2 ring-blue-500 border-blue-500/50' : 'border-slate-700/50 lg:border-gray-200'
                      }`}
                      onClick={() => {
                        setSelectedAnno(anno.anno);
                        setView('list');
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-2xl font-bold text-white lg:text-gray-900">{anno.anno}</span>
                        <span className="text-xs bg-blue-500/20 lg:bg-blue-100 text-blue-400 lg:text-blue-700 px-2 py-1 rounded-full">
                          {anno.totale_fatture} fatture
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-400 lg:text-gray-500">Totale</span>
                          <span className="font-bold text-emerald-300 lg:text-green-600">
                            {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(anno.totale_importo)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-400 lg:text-gray-500">Imponibile</span>
                          <span className="font-medium text-slate-200 lg:text-gray-700">
                            {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(anno.totale_imponibile)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-slate-400 lg:text-gray-500">IVA</span>
                          <span className="font-medium text-orange-300 lg:text-orange-600">
                            {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(anno.totale_iva)}
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-slate-700/50 lg:border-gray-200">
                          <span className="text-sm text-slate-400 lg:text-gray-500">Fornitori</span>
                          <span className="font-medium text-slate-200 lg:text-gray-700">{anno.num_fornitori}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* CATEGORIE VIEW - Dark Mobile Ottimizzato */}
          {view === 'categorie' && (
            <div className="space-y-4 overflow-x-hidden">
              {/* Banner stato categorizzazione in corso */}
              {catStatus.running && (
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-4 border border-blue-400/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 text-white animate-spin" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-white">Categorizzazione AI in corso...</p>
                      <p className="text-xs text-blue-200">Non chiudere la pagina</p>
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.post(`${API}/aruba/categorizzazione/stop`, {}, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          toast.info('Categorizzazione fermata');
                        } catch (err) {
                          toast.error('Errore nel fermare la categorizzazione');
                        }
                      }}
                      className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-medium transition-colors"
                    >
                      Stop
                    </button>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="relative h-3 bg-white/20 rounded-full overflow-hidden mb-2">
                    <div 
                      className="absolute left-0 top-0 h-full bg-white rounded-full transition-all duration-500"
                      style={{ width: `${catStatus.total > 0 ? (catStatus.progress / catStatus.total * 100) : 0}%` }}
                    />
                  </div>
                  
                  {/* Stats dettagliati */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-white/10 rounded-lg p-2">
                      <p className="text-lg font-bold text-white">{catStatus.progress}</p>
                      <p className="text-[10px] text-blue-200">Processate</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <p className="text-lg font-bold text-emerald-300">{catStatus.categorizzate}</p>
                      <p className="text-[10px] text-blue-200">Categorizzate</p>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <p className="text-lg font-bold text-white">{catStatus.total - catStatus.progress}</p>
                      <p className="text-[10px] text-blue-200">Rimanenti</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Pulsante avvia categorizzazione se ci sono fatture non categorizzate */}
              {!catStatus.running && statisticheCategorie.some(c => c.categoria === 'Non categorizzato' && c.totale_fatture > 0) && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <span className="text-lg">⚠️</span>
                      </div>
                      <div>
                        <p className="font-semibold text-amber-300">
                          {statisticheCategorie.find(c => c.categoria === 'Non categorizzato')?.totale_fatture || 0} fatture da categorizzare
                        </p>
                        <p className="text-xs text-amber-400/70">Clicca per avviare la categorizzazione AI</p>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        // Previeni doppio click se già in esecuzione
                        if (catStatus.running) {
                          toast.info('Categorizzazione già in corso...');
                          return;
                        }
                        try {
                          const token = localStorage.getItem('token');
                          const response = await axios.post(
                            `${API}/aruba/categorizza-batch`, 
                            {},
                            { headers: { 'Authorization': `Bearer ${token}` } }
                          );
                          
                          // Controlla se il backend ha segnalato che è già in corso
                          if (response.data.already_running) {
                            toast.info(`Categorizzazione già in corso (${response.data.progress}/${response.data.total})`);
                            setCatStatus(prev => ({ 
                              ...prev, 
                              running: true, 
                              progress: response.data.progress,
                              total: response.data.total 
                            }));
                            return;
                          }
                          
                          toast.success(`Avviata categorizzazione di ${response.data.totale_da_categorizzare} fatture`);
                          // Aggiorna stato
                          setCatStatus(prev => ({ ...prev, running: true, total: response.data.totale_da_categorizzare }));
                        } catch (err) {
                          // Gestisci errori specifici
                          const errorMsg = err.response?.data?.detail || err.message;
                          if (errorMsg.includes('già in corso')) {
                            toast.info('Categorizzazione già in corso');
                          } else {
                            toast.error('Errore nell\'avvio della categorizzazione');
                          }
                        }
                      }}
                      disabled={catStatus.running}
                      className={`px-4 py-2 ${catStatus.running ? 'bg-amber-500/50 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-400'} text-black font-bold rounded-xl text-sm transition-colors flex items-center gap-2`}
                    >
                      <Sparkles className="h-4 w-4" />
                      {catStatus.running ? 'In corso...' : 'Categorizza'}
                    </button>
                  </div>
                </div>
              )}
              
              {statisticheCategorie.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <PieChart className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-300 lg:text-gray-600">Nessuna categoria trovata</p>
                  <p className="text-xs text-slate-500 mt-2">Usa "AI Cat." per categorizzare le fatture</p>
                </div>
              ) : (
                <>
                  {/* KPI Summary */}
                  {(() => {
                    const totaleImporto = statisticheCategorie.reduce((sum, c) => sum + c.totale_importo, 0);
                    const totaleFatture = statisticheCategorie.reduce((sum, c) => sum + c.totale_fatture, 0);
                    return (
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-slate-800/90 lg:bg-white rounded-xl border border-slate-700/50 lg:border-gray-200 p-3 text-center">
                          <p className="text-[10px] text-slate-400 lg:text-gray-500 uppercase tracking-wide">Categorie</p>
                          <p className="text-lg font-bold text-white lg:text-gray-900">{statisticheCategorie.length}</p>
                        </div>
                        <div className="bg-slate-800/90 lg:bg-white rounded-xl border border-slate-700/50 lg:border-gray-200 p-3 text-center">
                          <p className="text-[10px] text-slate-400 lg:text-gray-500 uppercase tracking-wide">Fatture</p>
                          <p className="text-lg font-bold text-blue-300 lg:text-blue-600">{totaleFatture}</p>
                        </div>
                        <div className="bg-slate-800/90 lg:bg-white rounded-xl border border-slate-700/50 lg:border-gray-200 p-3 text-center">
                          <p className="text-[10px] text-slate-400 lg:text-gray-500 uppercase tracking-wide">Totale</p>
                          <p className="text-sm font-bold text-emerald-300 lg:text-green-600">
                            {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(totaleImporto)}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Categorie List con barre progresso */}
                  <div className="space-y-2">
                    {(() => {
                      const maxImporto = Math.max(...statisticheCategorie.map(c => c.totale_importo));
                      const totaleImporto = statisticheCategorie.reduce((sum, c) => sum + c.totale_importo, 0);
                      
                      const getCatColor = (cat) => {
                        const colors = {
                          'Food & Materie Prime': { bg: 'bg-emerald-500', text: 'text-emerald-300 lg:text-emerald-600', border: 'border-emerald-500/30', icon: '🍽️' },
                          'Alimentari': { bg: 'bg-emerald-500', text: 'text-emerald-300 lg:text-emerald-600', border: 'border-emerald-500/30', icon: '🍽️' },
                          'Bevande': { bg: 'bg-blue-500', text: 'text-blue-300 lg:text-blue-600', border: 'border-blue-500/30', icon: '🍷' },
                          'Lavanderia & Biancheria': { bg: 'bg-blue-500', text: 'text-blue-300 lg:text-blue-600', border: 'border-blue-500/30', icon: '🧺' },
                          'Pulizia & Igiene': { bg: 'bg-cyan-500', text: 'text-cyan-300 lg:text-cyan-600', border: 'border-cyan-500/30', icon: '🧹' },
                          'Pulizia': { bg: 'bg-cyan-500', text: 'text-cyan-300 lg:text-cyan-600', border: 'border-cyan-500/30', icon: '🧹' },
                          'Attrezzature Cucina': { bg: 'bg-amber-500', text: 'text-amber-300 lg:text-amber-600', border: 'border-amber-500/30', icon: '🍳' },
                          'Forniture Sala': { bg: 'bg-orange-500', text: 'text-orange-300 lg:text-orange-600', border: 'border-orange-500/30', icon: '🍽️' },
                          'Attrezzature & Forniture': { bg: 'bg-amber-500', text: 'text-amber-300 lg:text-amber-600', border: 'border-amber-500/30', icon: '🔧' },
                          'Attrezzature': { bg: 'bg-amber-500', text: 'text-amber-300 lg:text-amber-600', border: 'border-amber-500/30', icon: '🔧' },
                          'Servizi Professionali': { bg: 'bg-purple-500', text: 'text-purple-300 lg:text-purple-600', border: 'border-purple-500/30', icon: '👔' },
                          'Servizi': { bg: 'bg-purple-500', text: 'text-purple-300 lg:text-purple-600', border: 'border-purple-500/30', icon: '👔' },
                          'Utenze': { bg: 'bg-yellow-500', text: 'text-yellow-300 lg:text-yellow-600', border: 'border-yellow-500/30', icon: '💡' },
                          'Utenze & Affitti': { bg: 'bg-yellow-500', text: 'text-yellow-300 lg:text-yellow-600', border: 'border-yellow-500/30', icon: '💡' },
                          'Affitto & Locazione': { bg: 'bg-slate-500', text: 'text-slate-300 lg:text-slate-600', border: 'border-slate-500/30', icon: '🏠' },
                          'Marketing & Omaggi': { bg: 'bg-pink-500', text: 'text-pink-300 lg:text-pink-600', border: 'border-pink-500/30', icon: '🎁' },
                          'Marketing & Comunicazione': { bg: 'bg-pink-500', text: 'text-pink-300 lg:text-pink-600', border: 'border-pink-500/30', icon: '📢' },
                          'Marketing': { bg: 'bg-pink-500', text: 'text-pink-300 lg:text-pink-600', border: 'border-pink-500/30', icon: '📢' },
                          'Packaging & Asporto': { bg: 'bg-teal-500', text: 'text-teal-300 lg:text-teal-600', border: 'border-teal-500/30', icon: '📦' },
                          'Ambiente & Decorazioni': { bg: 'bg-rose-500', text: 'text-rose-300 lg:text-rose-600', border: 'border-rose-500/30', icon: '🕯️' },
                          'Cancelleria & Ufficio': { bg: 'bg-gray-500', text: 'text-gray-300 lg:text-gray-600', border: 'border-gray-500/30', icon: '📝' },
                          'Noleggio & Abbonamenti': { bg: 'bg-violet-500', text: 'text-violet-300 lg:text-violet-600', border: 'border-violet-500/30', icon: '📅' },
                          'Manutenzione': { bg: 'bg-red-500', text: 'text-red-300 lg:text-red-600', border: 'border-red-500/30', icon: '🔩' },
                          'Altro': { bg: 'bg-slate-400', text: 'text-slate-300 lg:text-gray-500', border: 'border-slate-400/30', icon: '📋' }
                        };
                        return colors[cat] || { bg: 'bg-slate-500', text: 'text-slate-300 lg:text-gray-600', border: 'border-slate-500/30', icon: '📋' };
                      };
                      
                      // Ordina per importo decrescente
                      const sortedCategorie = [...statisticheCategorie].sort((a, b) => b.totale_importo - a.totale_importo);
                      
                      return sortedCategorie.map((cat, idx) => {
                        const colors = getCatColor(cat.categoria);
                        const percentualeMax = (cat.totale_importo / maxImporto * 100).toFixed(0);
                        const percentualeTotale = (cat.totale_importo / totaleImporto * 100).toFixed(1);
                        
                        return (
                          <div 
                            key={idx} 
                            className={`bg-slate-800/90 lg:bg-white rounded-2xl border ${colors.border} lg:border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg`}
                            data-testid={`categoria-card-${cat.categoria}`}
                          >
                            <div
                              className="p-4 cursor-pointer hover:bg-slate-700/30 lg:hover:bg-gray-50 transition-colors"
                              onClick={() => setExpandedCategorie(prev => ({...prev, [cat.categoria]: !prev[cat.categoria]}))}
                            >
                              {/* Header con icona */}
                              <div className="flex justify-between items-center mb-3">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className={`w-10 h-10 rounded-xl ${colors.bg}/20 flex items-center justify-center text-lg`}>
                                    {colors.icon}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="font-bold text-white lg:text-gray-900 block truncate">{cat.categoria || 'Non categorizzato'}</span>
                                    <span className="text-xs text-slate-400 lg:text-gray-500">{cat.totale_fatture} fatture · {percentualeTotale}% del totale</span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                  <span className={`font-bold text-lg ${colors.text}`}>
                                    {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(cat.totale_importo)}
                                  </span>
                                  {expandedCategorie[cat.categoria] ? (
                                    <ChevronUp className="h-5 w-5 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-slate-400" />
                                  )}
                                </div>
                              </div>
                              
                              {/* Progress bar più grande */}
                              <div className="relative h-2 bg-slate-700/50 lg:bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`absolute left-0 top-0 h-full ${colors.bg} rounded-full transition-all duration-500`}
                                  style={{ width: `${percentualeMax}%` }}
                                />
                              </div>
                            </div>
                            
                            {/* Expanded sottocategorie */}
                            {expandedCategorie[cat.categoria] && (
                              <div className="border-t border-slate-700/50 lg:border-gray-200 bg-slate-900/50 lg:bg-gray-50 p-4">
                                {cat.sottocategorie?.length > 0 ? (
                                  <>
                                    <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-3 font-semibold">Sottocategorie (clicca per filtrare)</p>
                                    <div className="flex flex-wrap gap-2">
                                      {cat.sottocategorie.map((sub, subIdx) => (
                                        <button 
                                          key={subIdx}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setFilters(prev => ({ 
                                              ...prev, 
                                              categoria: cat.categoria,
                                              sottocategoria: sub.nome 
                                            }));
                                            setView('list');
                                          }}
                                          className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all duration-200 cursor-pointer
                                            bg-slate-700/50 lg:bg-white border-slate-600/50 lg:border-gray-200 
                                            text-slate-200 lg:text-gray-700 
                                            hover:${colors.bg}/20 hover:border-current hover:scale-105
                                            ${colors.text}`}
                                        >
                                          {sub.nome} <span className="opacity-60">({sub.count})</span>
                                        </button>
                                      ))}
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-xs text-slate-500 text-center py-2">Nessuna sottocategoria</p>
                                )}
                                
                                {/* Quick action - Vedi fatture categoria */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFilters(prev => ({ ...prev, categoria: cat.categoria }));
                                    setView('list');
                                  }}
                                  className="mt-3 w-full text-center text-xs text-blue-400 hover:text-blue-300 py-1.5 border border-blue-500/30 rounded-lg hover:bg-blue-500/10 transition-colors"
                                >
                                  Vedi {cat.totale_fatture} fatture →
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* LIST VIEW - Dark Mobile */}
          {view === 'list' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-400 lg:text-gray-600">
                  Fatture anno {selectedAnno}
                </div>
                
                {/* Active filters indicator */}
                {(filters.categoria || filters.sottocategoria) && (
                  <button
                    onClick={() => {
                      setFilters({ fornitore: '', categoria: '', sottocategoria: '' });
                      // Clear cache to force reload
                      dataCache.current = {};
                      loadFattureAnno();
                    }}
                    className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 px-2 py-1 bg-blue-500/10 rounded-lg border border-blue-500/30"
                  >
                    <X className="h-3 w-3" />
                    Rimuovi filtri
                  </button>
                )}
              </div>
              
              {/* Active filters badges */}
              {(filters.categoria || filters.sottocategoria) && (
                <div className="flex flex-wrap gap-2">
                  {filters.categoria && (
                    <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded-full border border-purple-500/30">
                      Categoria: {filters.categoria}
                    </span>
                  )}
                  {filters.sottocategoria && (
                    <span className="text-xs px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded-full border border-cyan-500/30">
                      Sottocategoria: {filters.sottocategoria}
                    </span>
                  )}
                </div>
              )}
              
              {/* Search - Dark Mobile */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
                <Input
                  placeholder="Cerca per fornitore, numero fattura..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400"
                />
              </div>
              
              {/* Summary Cards - Dark Mobile */}
              {(() => {
                const annoStats = statisticheAnni.find(a => a.anno === selectedAnno) || {};
                return (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-blue-50 lg:to-blue-100 border border-blue-500/30 lg:border-blue-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-blue-300 lg:text-blue-600 font-medium">Totale Fatture</p>
                      <p className="text-xl font-bold text-blue-300 lg:text-blue-900">{annoStats.totale_fatture || 0}</p>
                    </div>
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-green-50 lg:to-green-100 border border-green-500/30 lg:border-green-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-emerald-300 lg:text-green-600 font-medium">Importo Totale</p>
                      <p className="text-lg font-bold text-emerald-300 lg:text-green-900">
                        {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(annoStats.totale_importo || 0)}
                      </p>
                    </div>
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-purple-50 lg:to-purple-100 border border-purple-500/30 lg:border-purple-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-purple-300 lg:text-purple-600 font-medium">Imponibile</p>
                      <p className="text-lg font-bold text-purple-300 lg:text-purple-900">
                        {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(annoStats.totale_imponibile || 0)}
                      </p>
                    </div>
                    <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100 border border-orange-500/30 lg:border-orange-200 rounded-2xl lg:rounded-xl p-3">
                      <p className="text-xs text-orange-300 lg:text-orange-600 font-medium">IVA</p>
                      <p className="text-lg font-bold text-orange-300 lg:text-orange-900">
                        {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(annoStats.totale_iva || 0)}
                      </p>
                    </div>
                  </div>
                );
              })()}
              
              {/* Fatture List - Dark Mobile */}
              {fatture.filter(f => {
                if (!searchTerm) return true;
                const search = searchTerm.toLowerCase();
                return f.fornitore?.denominazione?.toLowerCase().includes(search) ||
                       f.numero_fattura?.toLowerCase().includes(search);
              }).length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <FileSearch className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-200 lg:text-gray-600 font-medium">Nessuna fattura trovata</p>
                  <p className="text-sm text-slate-400 lg:text-gray-500 mt-1">
                    Carica i file XML o ZIP dal pannello Aruba
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {fatture.filter(f => {
                    if (!searchTerm) return true;
                    const search = searchTerm.toLowerCase();
                    return f.fornitore?.denominazione?.toLowerCase().includes(search) ||
                           f.numero_fattura?.toLowerCase().includes(search);
                  }).map((fattura) => (
                    <div 
                      key={fattura.id} 
                      className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 hover:shadow-md transition-shadow cursor-pointer p-3 lg:p-4"
                      onClick={() => viewFatturaDetail(fattura.id)}
                    >
                      <div className="flex flex-col sm:flex-row justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Building2 className="h-4 w-4 text-slate-400 lg:text-gray-400" />
                            <span className="font-semibold text-white lg:text-gray-900">
                              {fattura.fornitore?.denominazione || 'Fornitore sconosciuto'}
                            </span>
                            {fattura.categoria?.categoria_principale && (
                              <span className="text-xs px-2 py-0.5 bg-slate-700 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-full">
                                {fattura.categoria.categoria_principale}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-400 lg:text-gray-600">
                            <span>N. {fattura.numero_fattura}</span>
                            <span>{fattura.data_fattura}</span>
                            {fattura.fornitore?.partita_iva && (
                              <span className="text-slate-500 lg:text-gray-400 hidden sm:inline">P.IVA: {fattura.fornitore.partita_iva}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-bold text-lg text-white lg:text-gray-900">
                              {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(fattura.importo_totale || 0)}
                            </p>
                            <p className="text-xs text-slate-400 lg:text-gray-500">
                              {fattura.linee?.length || 0} articoli
                            </p>
                          </div>
                          <Eye className="h-5 w-5 text-slate-400 lg:text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* FORNITORI VIEW - Completamente Rinnovato */}
          {view === 'fornitori' && (
            <div className="space-y-4">
              {/* Header KPI Fornitori */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-blue-50 lg:to-blue-100 border border-blue-500/30 lg:border-blue-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-blue-300 lg:text-blue-600 font-medium">Totale Fornitori</p>
                  <p className="text-xl font-bold text-blue-300 lg:text-blue-900">{statisticheFornitori.length}</p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-emerald-50 lg:to-emerald-100 border border-emerald-500/30 lg:border-emerald-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-emerald-300 lg:text-emerald-600 font-medium">Valore Totale</p>
                  <p className="text-lg font-bold text-emerald-300 lg:text-emerald-900">
                    {formatCurrency(statisticheFornitori.reduce((sum, f) => sum + (f.totale_importo || 0), 0))}
                  </p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-purple-50 lg:to-purple-100 border border-purple-500/30 lg:border-purple-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-purple-300 lg:text-purple-600 font-medium">Totale Fatture</p>
                  <p className="text-xl font-bold text-purple-300 lg:text-purple-900">
                    {statisticheFornitori.reduce((sum, f) => sum + (f.totale_fatture || 0), 0)}
                  </p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100 border border-orange-500/30 lg:border-orange-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-orange-300 lg:text-orange-600 font-medium">Media per Fornitore</p>
                  <p className="text-lg font-bold text-orange-300 lg:text-orange-900">
                    {formatCurrency(statisticheFornitori.length > 0 
                      ? statisticheFornitori.reduce((sum, f) => sum + (f.totale_importo || 0), 0) / statisticheFornitori.length 
                      : 0)}
                  </p>
                </div>
              </div>

              {/* Barra ricerca e ordinamento */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
                  <Input
                    placeholder="Cerca fornitore..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const sorted = [...statisticheFornitori].sort((a, b) => 
                        (a.fornitore || '').localeCompare(b.fornitore || '', 'it')
                      );
                      setStatisticheFornitori(sorted);
                    }}
                    className="border-slate-600 lg:border-gray-200 text-slate-200 lg:text-gray-700 text-xs"
                  >
                    A-Z
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const sorted = [...statisticheFornitori].sort((a, b) => 
                        (b.totale_importo || 0) - (a.totale_importo || 0)
                      );
                      setStatisticheFornitori(sorted);
                    }}
                    className="border-slate-600 lg:border-gray-200 text-slate-200 lg:text-gray-700 text-xs"
                  >
                    €↓
                  </Button>
                </div>
              </div>

              {statisticheFornitori.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <Truck className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-300 lg:text-gray-600">Nessun fornitore trovato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {statisticheFornitori
                    .filter(f => !searchTerm || f.fornitore?.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((fornitore, idx) => {
                      const maxImporto = Math.max(...statisticheFornitori.map(f => f.totale_importo || 0));
                      const percentuale = maxImporto > 0 ? ((fornitore.totale_importo || 0) / maxImporto * 100) : 0;
                      
                      return (
                        <div key={idx} className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                          <div
                            className="p-4 cursor-pointer hover:bg-slate-700/30 lg:hover:bg-gray-50 transition-colors"
                            onClick={() => toggleFornitore(fornitore.fornitore)}
                          >
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                                    <Truck className="h-5 w-5 text-blue-400 lg:text-blue-600" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="font-bold text-white lg:text-gray-900 block truncate">{fornitore.fornitore || 'N/D'}</span>
                                    <div className="flex flex-wrap gap-2 mt-1 text-xs">
                                      <span className="px-2 py-0.5 bg-slate-700/50 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-lg">
                                        {fornitore.totale_fatture} fatture
                                      </span>
                                      {fornitore.partita_iva && (
                                        <span className="px-2 py-0.5 bg-slate-700/50 lg:bg-gray-100 text-slate-400 lg:text-gray-500 rounded-lg hidden sm:inline">
                                          P.IVA: {fornitore.partita_iva}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {/* Progress bar */}
                                <div className="mt-3 relative h-1.5 bg-slate-700/50 lg:bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-500"
                                    style={{ width: `${percentuale}%` }}
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-4 flex-shrink-0">
                                <div className="text-right">
                                  <p className="font-bold text-xl text-emerald-400 lg:text-emerald-600">{formatCurrency(fornitore.totale_importo)}</p>
                                  <p className="text-xs text-slate-400 lg:text-gray-500">
                                    {fornitore.ultima_fattura && `Ultimo: ${formatDate(fornitore.ultima_fattura)}`}
                                  </p>
                                </div>
                                {expandedFornitori[fornitore.fornitore] ? (
                                  <ChevronUp className="h-5 w-5 text-slate-400" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-slate-400" />
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Expanded: Dettagli + Prodotti */}
                          {expandedFornitori[fornitore.fornitore] && (
                            <FornitoreDettaglio 
                              fornitore={fornitore} 
                              selectedAnno={selectedAnno}
                              formatCurrency={formatCurrency}
                              formatDate={formatDate}
                            />
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
          
          {/* PRODOTTI VIEW - Migliorato con prezzi e statistiche */}
          {view === 'prodotti' && (
            <div className="space-y-4">
              {/* Header con statistiche prodotti */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-purple-50 lg:to-purple-100 border border-purple-500/30 lg:border-purple-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-purple-300 lg:text-purple-600 font-medium">Totale Prodotti</p>
                  <p className="text-xl font-bold text-purple-300 lg:text-purple-900">{statisticheProdotti.length}</p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-emerald-50 lg:to-emerald-100 border border-emerald-500/30 lg:border-emerald-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-emerald-300 lg:text-emerald-600 font-medium">Valore Totale</p>
                  <p className="text-lg font-bold text-emerald-300 lg:text-emerald-900">
                    {formatCurrency(statisticheProdotti.reduce((sum, p) => sum + (p.importo_totale || 0), 0))}
                  </p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-blue-50 lg:to-blue-100 border border-blue-500/30 lg:border-blue-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-blue-300 lg:text-blue-600 font-medium">Prodotti con Sconto</p>
                  <p className="text-xl font-bold text-blue-300 lg:text-blue-900">
                    {statisticheProdotti.filter(p => p.sconto_medio > 0).length}
                  </p>
                </div>
                <div className="bg-slate-800/90 lg:bg-gradient-to-br lg:from-orange-50 lg:to-orange-100 border border-orange-500/30 lg:border-orange-200 rounded-2xl lg:rounded-xl p-3">
                  <p className="text-xs text-orange-300 lg:text-orange-600 font-medium">Fornitori Unici</p>
                  <p className="text-xl font-bold text-orange-300 lg:text-orange-900">
                    {new Set(statisticheProdotti.flatMap(p => p.fornitori || [])).size}
                  </p>
                </div>
              </div>

              {/* Barra ricerca prodotti */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 lg:text-gray-400" />
                <Input
                  placeholder="Cerca prodotto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-700 lg:bg-white text-white lg:text-gray-900 border-slate-600 lg:border-gray-200 placeholder-slate-400"
                />
              </div>

              {statisticheProdotti.length === 0 ? (
                <div className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 py-12 text-center">
                  <Package className="h-12 w-12 text-slate-500 lg:text-gray-300 mx-auto mb-4" />
                  <p className="text-slate-300 lg:text-gray-600">Nessun prodotto trovato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {statisticheProdotti
                    .filter(p => !searchTerm || p.prodotto?.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((prodotto, idx) => {
                      const hasSconto = prodotto.sconto_medio && prodotto.sconto_medio > 0;
                      const prezzoVariazione = prodotto.prezzo_max && prodotto.prezzo_min 
                        ? ((prodotto.prezzo_max - prodotto.prezzo_min) / prodotto.prezzo_min * 100).toFixed(0)
                        : 0;
                      
                      return (
                        <div key={idx} className="bg-slate-800/90 lg:bg-white rounded-2xl lg:rounded-xl border border-slate-700/50 lg:border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                          <div
                            className="p-4 cursor-pointer hover:bg-slate-700/50 lg:hover:bg-gray-50 transition-colors"
                            onClick={() => toggleProdotto(prodotto.prodotto)}
                          >
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Package className="h-5 w-5 text-purple-400 lg:text-purple-600 flex-shrink-0" />
                                  <span className="font-semibold text-white lg:text-gray-900 truncate">{prodotto.prodotto || 'N/D'}</span>
                                  {hasSconto && (
                                    <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 lg:text-green-600 rounded-full whitespace-nowrap">
                                      -{prodotto.sconto_medio?.toFixed(1)}% medio
                                    </span>
                                  )}
                                </div>
                                {prodotto.codice && (
                                  <p className="text-sm text-slate-400 lg:text-gray-500 mt-1">Cod: {prodotto.codice}</p>
                                )}
                                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                                  <span className="px-2 py-1 bg-slate-700/50 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-lg">
                                    {prodotto.numero_acquisti} acquisti
                                  </span>
                                  <span className="px-2 py-1 bg-slate-700/50 lg:bg-gray-100 text-slate-300 lg:text-gray-600 rounded-lg">
                                    Qta: {prodotto.quantita_totale?.toFixed(1)} {prodotto.unita_misura || 'pz'}
                                  </span>
                                  {prodotto.fornitori?.length > 0 && (
                                    <span className="px-2 py-1 bg-blue-500/20 text-blue-300 lg:text-blue-600 rounded-lg">
                                      {prodotto.fornitori.length} fornitore{prodotto.fornitori.length > 1 ? 'i' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-4 flex-shrink-0">
                                <div className="text-right">
                                  <p className="font-bold text-xl text-purple-400 lg:text-purple-600">{formatCurrency(prodotto.importo_totale)}</p>
                                  <div className="flex items-center justify-end gap-1 text-xs text-slate-400 lg:text-gray-500">
                                    <span>Media: {formatCurrency(prodotto.prezzo_medio)}</span>
                                    {prezzoVariazione > 10 && (
                                      <span className="text-orange-400">±{prezzoVariazione}%</span>
                                    )}
                                  </div>
                                </div>
                                {expandedProdotti[prodotto.prodotto] ? (
                                  <ChevronUp className="h-5 w-5 text-slate-400 lg:text-gray-400" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-slate-400 lg:text-gray-400" />
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {expandedProdotti[prodotto.prodotto] && (
                            <div className="border-t border-slate-700/50 lg:border-gray-200 bg-slate-900/50 lg:bg-gray-50 p-4">
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                                <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
                                  <p className="text-[10px] text-slate-500 lg:text-gray-500 uppercase tracking-wide">Prezzo Minimo</p>
                                  <p className="font-bold text-lg text-green-400 lg:text-green-600">{formatCurrency(prodotto.prezzo_min || prodotto.prezzo_medio)}</p>
                                </div>
                                <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
                                  <p className="text-[10px] text-slate-500 lg:text-gray-500 uppercase tracking-wide">Prezzo Massimo</p>
                                  <p className="font-bold text-lg text-red-400 lg:text-red-600">{formatCurrency(prodotto.prezzo_max || prodotto.prezzo_medio)}</p>
                                </div>
                                <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
                                  <p className="text-[10px] text-slate-500 lg:text-gray-500 uppercase tracking-wide">Prezzo Medio</p>
                                  <p className="font-bold text-lg text-white lg:text-gray-900">{formatCurrency(prodotto.prezzo_medio)}</p>
                                </div>
                                <div className="bg-slate-800/50 lg:bg-white rounded-xl p-3 border border-slate-700/30 lg:border-gray-200">
                                  <p className="text-[10px] text-slate-500 lg:text-gray-500 uppercase tracking-wide">Sconto Medio</p>
                                  <p className={`font-bold text-lg ${hasSconto ? 'text-green-400 lg:text-green-600' : 'text-slate-400 lg:text-gray-400'}`}>
                                    {hasSconto ? `-${prodotto.sconto_medio?.toFixed(1)}%` : 'Nessuno'}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Barra range prezzo visuale */}
                              {prodotto.prezzo_min !== prodotto.prezzo_max && (
                                <div className="mb-4">
                                  <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">Range Prezzo</p>
                                  <div className="relative h-3 bg-slate-700/50 lg:bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                      className="absolute h-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full"
                                      style={{ width: '100%' }}
                                    />
                                    <div 
                                      className="absolute h-6 w-1 bg-white border-2 border-purple-500 rounded-full top-1/2 -translate-y-1/2 shadow-lg"
                                      style={{ 
                                        left: `${prodotto.prezzo_min && prodotto.prezzo_max 
                                          ? ((prodotto.prezzo_medio - prodotto.prezzo_min) / (prodotto.prezzo_max - prodotto.prezzo_min) * 100) 
                                          : 50}%` 
                                      }}
                                      title={`Prezzo medio: ${formatCurrency(prodotto.prezzo_medio)}`}
                                    />
                                  </div>
                                  <div className="flex justify-between mt-1 text-xs text-slate-500">
                                    <span>{formatCurrency(prodotto.prezzo_min)}</span>
                                    <span className="text-purple-400 font-medium">Media: {formatCurrency(prodotto.prezzo_medio)}</span>
                                    <span>{formatCurrency(prodotto.prezzo_max)}</span>
                                  </div>
                                </div>
                              )}
                              
                              {/* Fornitori */}
                              {prodotto.fornitori?.length > 0 && (
                                <div>
                                  <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">Fornitori</p>
                                  <div className="flex flex-wrap gap-2">
                                    {prodotto.fornitori.map((forn, fIdx) => (
                                      <button
                                        key={fIdx}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setFilters(prev => ({ ...prev, fornitore: forn }));
                                          setView('list');
                                        }}
                                        className="px-3 py-1.5 bg-slate-700/50 lg:bg-white border border-slate-600/50 lg:border-gray-200 rounded-lg text-xs text-slate-200 lg:text-gray-700 hover:border-blue-500 hover:text-blue-400 transition-colors"
                                      >
                                        {forn}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          )}
        </>
      )}
      
      {/* Detail Modal */}
      {showDetail && selectedFattura && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-blue-50 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Dettaglio Fattura</h2>
                <p className="text-sm text-gray-600">
                  N. {selectedFattura.numero_fattura} del {formatDate(selectedFattura.data_fattura)}
                </p>
              </div>
              <button 
                onClick={() => { setShowDetail(false); setSelectedFattura(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Fornitore Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Fornitore
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Denominazione:</span>
                    <span className="ml-2 font-medium">{selectedFattura.fornitore?.denominazione}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">P.IVA:</span>
                    <span className="ml-2 font-medium">{selectedFattura.fornitore?.partita_iva || 'N/D'}</span>
                  </div>
                  {selectedFattura.fornitore?.indirizzo && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Indirizzo:</span>
                      <span className="ml-2 font-medium">
                        {selectedFattura.fornitore.indirizzo}, {selectedFattura.fornitore.cap} {selectedFattura.fornitore.comune} ({selectedFattura.fornitore.provincia})
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Totali */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600">Imponibile</p>
                  <p className="font-bold text-lg text-blue-900">{formatCurrency(selectedFattura.imponibile)}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-orange-600">IVA</p>
                  <p className="font-bold text-lg text-orange-900">{formatCurrency(selectedFattura.iva)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-green-600">Totale</p>
                  <p className="font-bold text-lg text-green-900">{formatCurrency(selectedFattura.importo_totale)}</p>
                </div>
              </div>
              
              {/* Line Items */}
              {selectedFattura.linee && selectedFattura.linee.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Articoli ({selectedFattura.linee.length})
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedFattura.linee.map((linea, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded-lg text-sm">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{linea.descrizione}</p>
                          <p className="text-xs text-gray-500">
                            {linea.quantita} {linea.unita_misura || 'pz'} × {formatCurrency(linea.prezzo_unitario)}
                            {linea.codice_articolo && ` | Cod: ${linea.codice_articolo}`}
                          </p>
                        </div>
                        <p className="font-bold text-gray-900">{formatCurrency(linea.prezzo_totale)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <Button 
                onClick={() => { setShowDetail(false); setSelectedFattura(null); }}
                variant="outline"
              >
                Chiudi
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ PLACEHOLDER PAGES FOR NEW FEATURES ============

const HomePage = ({ onNavigate, user, hasAccess }) => {
  // Menu items della tua app (filtrati per permessi)
  const allMobileMenuItems = [
    { id: 'dashboard', title: 'Dashboard', icon: BarChart3, gradient: 'from-blue-500 to-cyan-400', shadowColor: 'shadow-blue-500/30' },
    { id: 'fatture', title: 'Fatture', icon: FileText, gradient: 'from-violet-500 to-purple-400', shadowColor: 'shadow-violet-500/30' },
    { id: 'fbcost', title: 'F&B Cost', icon: DollarSign, gradient: 'from-emerald-500 to-green-400', shadowColor: 'shadow-emerald-500/30' },
    { id: 'ordini', title: 'Ordini', icon: ShoppingCart, gradient: 'from-orange-500 to-amber-400', shadowColor: 'shadow-orange-500/30' },
    { id: 'bolle', title: 'Bolle/DDT', icon: Package, gradient: 'from-purple-500 to-blue-400', shadowColor: 'shadow-purple-500/30' },
    { id: 'haccp', title: 'HACCP', icon: ShieldCheck, gradient: 'from-teal-500 to-cyan-400', shadowColor: 'shadow-teal-500/30' },
    { id: 'spese', title: 'Spese', icon: Receipt, gradient: 'from-red-500 to-orange-400', shadowColor: 'shadow-red-500/30' },
    { id: 'mance', title: 'Mance', icon: Coins, gradient: 'from-amber-500 to-yellow-400', shadowColor: 'shadow-amber-500/30' },
    { id: 'analytics', title: 'Analytics', icon: PieChart, gradient: 'from-blue-500 to-blue-400', shadowColor: 'shadow-blue-500/30' },
    { id: 'reports', title: 'Report', icon: FileSearch, gradient: 'from-fuchsia-500 to-pink-400', shadowColor: 'shadow-fuchsia-500/30' },
    { id: 'team', title: 'Team', icon: Users, gradient: 'from-sky-500 to-blue-400', shadowColor: 'shadow-sky-500/30', hidden: true },
    { id: 'settings', title: 'Impostazioni', icon: Settings, gradient: 'from-gray-500 to-slate-400', shadowColor: 'shadow-gray-500/30' },
    { id: 'saas-admin', title: 'Admin SaaS', icon: Crown, gradient: 'from-red-500 to-rose-400', shadowColor: 'shadow-red-500/30', superAdminOnly: true },
  ];

  const allDesktopMenuItems = [
    { id: 'dashboard', title: 'Dashboard', desc: 'Panoramica giornaliera', icon: BarChart3, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'analytics', title: 'Analytics', desc: 'Statistiche e grafici avanzati', icon: PieChart, color: 'text-purple-600', bg: 'bg-purple-50' },
    { id: 'reports', title: 'Report', desc: 'Report dettagliati per data', icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'fatture', title: 'Fatture', desc: 'Fatture elettroniche', icon: FileSearch, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'spese', title: 'Spese', desc: 'Gestisci le spese', icon: Receipt, color: 'text-red-600', bg: 'bg-red-50' },
    { id: 'mance', title: 'Mance', desc: 'Report mance', icon: Coins, color: 'text-amber-600', bg: 'bg-amber-50' },
    { id: 'menu', title: 'Menù', desc: 'Gestione menù', icon: ShoppingCart, color: 'text-green-600', bg: 'bg-green-50' },
    { id: 'bolle', title: 'Gestione Bolle', desc: 'DDT e bolle di consegna', icon: Package, color: 'text-purple-600', bg: 'bg-purple-50' },
    { id: 'resi', title: 'Resi Fornitore', desc: 'Note credito in attesa', icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
    { id: 'fbcost', title: 'F&B Cost', desc: 'Costo ricette', icon: DollarSign, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'ordini', title: 'Ordini', desc: 'Gestione ordini fornitori', icon: ShoppingCart, color: 'text-orange-600', bg: 'bg-orange-50' },
    { id: 'haccp', title: 'HACCP', desc: 'Sicurezza alimentare', icon: ShieldCheck, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'saas-admin', title: 'Admin SaaS', desc: 'Gestione tenant e utenti', icon: Crown, color: 'text-red-600', bg: 'bg-red-50', superAdminOnly: true },
  ];

  // Filtra per permessi - gli elementi superAdminOnly sono visibili solo per administrator
  // (la verifica Super Admin vera avviene nella SaaSAdminPage)
  const mobileMenuItems = allMobileMenuItems.filter(item => {
    if (item.superAdminOnly) {
      return user?.role === 'administrator' || user?.is_super_admin === true;
    }
    return hasAccess(item.id);
  });
  const desktopMenuItems = allDesktopMenuItems.filter(item => {
    if (item.superAdminOnly) {
      return user?.role === 'administrator' || user?.is_super_admin === true;
    }
    return hasAccess(item.id);
  });

  return (
    <>
      {/* MOBILE VERSION - Full Screen with Chef Background - Extends into safe areas */}
      <div 
        className="lg:hidden fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(/images/login-bg.png)',
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Content container - starts below safe area */}
        <div 
          className="absolute inset-0 flex flex-col px-4 overflow-auto"
          style={{ 
            paddingTop: 'calc(env(safe-area-inset-top, 44px) + 8px)',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 34px) + 70px)'
          }}
        >
          {/* Header - smaller size */}
          <div className="flex items-center justify-between mb-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="relative">
                <img 
                  src="/images/logo-new.png" 
                  alt="Ristorantissimo" 
                  className="w-9 h-9 rounded-lg object-cover"
                />
                <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full border border-black"></div>
              </div>
              <div>
                <p className="text-white/60 text-[10px]">Benvenuto</p>
                <h1 className="text-white text-sm font-semibold">{user?.nome_completo || user?.username || 'Utente'}</h1>
              </div>
            </div>
            
            <button className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <Mail className="h-4 w-4 text-white" />
            </button>
          </div>

          {/* Menu Grid */}
          <div className="flex-1 grid grid-cols-2 gap-3">
            {mobileMenuItems.map((item) => (
              <div
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className="rounded-2xl bg-black/40 backdrop-blur-md border border-white/10 active:scale-[0.97] transition-transform cursor-pointer flex flex-col items-center justify-center"
                data-testid={`menu-${item.id}`}
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-2 shadow-lg ${item.shadowColor}`}>
                  <item.icon className="h-7 w-7 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-white font-semibold text-sm">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DESKTOP VERSION */}
      <div className="hidden lg:block">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {desktopMenuItems.map((item) => (
            <div
              key={item.id}
              data-testid={`nav-card-${item.id}`}
              onClick={() => onNavigate(item.id)}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg hover:border-blue-200 transition-all cursor-pointer group"
            >
              <div className={`w-12 h-12 ${item.bg} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <item.icon className={`h-6 w-6 ${item.color}`} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const PlaceholderPage = ({ title, icon: Icon, description }) => (
  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
    <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
      <Icon className="h-10 w-10 text-blue-600" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
    <p className="text-gray-500 max-w-md">{description}</p>
    <span className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
      Prossimamente
    </span>
  </div>
);

// ============ MAIN APP COMPONENT ============

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  
  // Traccia le pagine già visitate per mantenerle in memoria
  const visitedPages = React.useRef(new Set());

  // Mappa permessi -> id sezione (usata per sidebar e home)
  const permissionMap = {
    dashboard: ['dashboard', 'analytics', 'reports'],
    cassa: ['spese', 'mance'],
    ordini: ['ordini'],
    ddt: ['bolle', 'ddt'],
    haccp: ['haccp'],
    fb_cost: ['fbcost', 'menu'],
    fatture: ['fatture'],
    team: ['team'],
    locali: ['locali']
  };

  // Funzione per verificare se l'utente ha accesso a una sezione
  const hasAccess = useCallback((sectionId) => {
    // Admin ha sempre accesso a tutto (controlla sia role che is_super_admin)
    if (user?.role === 'administrator' || user?.is_super_admin === true) return true;
    
    // Se non ci sono permessi definiti, blocca l'accesso
    if (!user?.permissions) return false;
    
    // Cerca in quale permesso rientra la sezione
    for (const [permKey, sections] of Object.entries(permissionMap)) {
      if (sections.includes(sectionId)) {
        return user.permissions[permKey] === true;
      }
    }
    
    // Home accessibile a tutti, Impostazioni solo Administrator
    if (sectionId === 'home') return true;
    if (sectionId === 'settings') return user?.role === 'administrator' || user?.is_super_admin === true;
    
    return false;
  }, [user]);
  
  const handlePageChange = useCallback((page) => {
    // Verifica accesso prima di navigare
    if (!hasAccess(page) && page !== 'home') {
      return; // Non navigare se non ha accesso
    }
    // Aggiungi la pagina alle pagine visitate
    visitedPages.current.add(page);
    setCurrentPage(page);
  }, [hasAccess]);

  // Funzione per reset completo dell'app (PWA-friendly)
  const resetApplication = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setResetKey(prev => prev + 1);
    toast.success('Sessione resetata. Rieffettua il login.');
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    console.log('🔐 App startup - Token check:', {
      hasToken: !!token,
      hasUserData: !!savedUser,
      tokenLength: token ? token.length : 0
    });
    
    if (token && savedUser && isTokenValid()) {
      try {
        const userObj = JSON.parse(savedUser);
        setUser(userObj);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        console.log('✅ Authentication restored for user:', userObj.username);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    } else {
      console.log('❌ No valid authentication found or token expired');
      // Pulisci eventuali token corrotti/scaduti
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
    }
    setLoading(false);
  }, [resetKey]); // Dipende da resetKey per re-inizializzare

  // Gestione PWA: controlla token quando l'app torna in foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        console.log('📱 App resumed from background - checking token validity');
        if (!isTokenValid()) {
          console.log('🚨 Token scaduto dopo inattività - reset necessario');
          toast.error('Sessione scaduta durante l\'inattività. Rieffettua il login.');
          resetApplication();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, resetApplication]);

  const handleLogin = (userData) => {
    setUser(userData);
    // Mostra il prompt per le notifiche dopo 2 secondi dal login
    setTimeout(() => {
      setShowNotificationPrompt(true);
    }, 2000);
  };

  const handleLogout = () => {
    // Rimuovi tutti i token dal localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('saas_token');
    localStorage.removeItem('original_saas_token');
    // Rimuovi header Authorization da axios
    delete axios.defaults.headers.common['Authorization'];
    // Reset stato utente
    setUser(null);
    toast.success('Logout effettuato');
  };

  // =====================================================
  // ROUTING PUBBLICO - Prima di qualsiasi check di loading/auth
  // =====================================================
  const path = window.location.pathname;
  
  // Tenant login page (sempre accessibile)
  if (path === '/tenant' || path === '/tenant/') {
    return <TenantApp />;
  }

  // SaaS Admin page (sempre accessibile per login)
  if (path === '/admin' || path === '/admin/' || path === '/saas' || path === '/saas/') {
    return <SaaSAdminPage />;
  }

  // Control Panel page (sempre accessibile)
  if (path === '/controlpanel' || path === '/controlpanel/') {
    return <ControlPanelPage />;
  }

  // Public lotto page (sempre accessibile)
  if (path.startsWith('/lotto/')) {
    const lottoId = path.replace('/lotto/', '');
    return <LottoPubblicoPage lottoId={lottoId} />;
  }

  // Cartella per l'ispettore via QR (sola lettura, link del responsabile)
  if (path.startsWith('/ispettore/')) {
    return <CartellaIspettorePage token={path.replace('/ispettore/', '').replace(/\/$/, '')} />;
  }

  // Registro Anomalie Temperature pubblico via QR del registro temperature (link firmato, sola lettura)
  if (path === '/registro-anomalie' || path === '/registro-anomalie/') {
    return <RegistroAnomaliePage />;
  }

  // Carta dei Vini pubblica via QR code (sempre accessibile)
  if (path.startsWith('/carta-vini/')) {
    const tenantId = path.replace('/carta-vini/', '').replace(/\/$/, '');
    return <PublicCartaViniPage tenantId={tenantId} />;
  }
  // =====================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  // Landing page for root path when not logged in
  if (!user && (path === '/' || path === '')) {
    return (
      <>
        <LandingPage onNavigateToLogin={() => window.location.href = '/tenant'} />
        <Toaster position="top-center" />
      </>
    );
  }

  // Login page for /login or /cassa path (Dashboard Cassa access)
  if (!user && (path === '/login' || path === '/cassa' || path === '/cassa/')) {
    return <LoginPage onLogin={handleLogin} />;
  }

  if (!user) {
    return (
      <>
        <LandingPage onNavigateToLogin={() => window.location.href = '/tenant'} />
        <Toaster position="top-center" />
      </>
    );
  }

  // Role-based rendering
  if (user.role === 'operatore') {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader user={user} onLogout={handleLogout} onShowSettings={null} />
        <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 pb-16 sm:pb-8">
          <OperatorDashboard />
        </div>
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <>
      {/* MOBILE BACKGROUND for non-home pages - Extends into safe areas */}
      {currentPage !== 'home' && (
        <div 
          className="lg:hidden fixed bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/images/login-bg.png)',
            top: 'calc(-1 * env(safe-area-inset-top, 50px))',
            left: 0,
            right: 0,
            bottom: 'calc(-1 * env(safe-area-inset-bottom, 50px))',
            zIndex: 0,
          }}
        >
          {/* Dark Overlay */}
          <div 
            className="absolute bg-black/60"
            style={{ 
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
        </div>
      )}
      
      <div className={`min-h-screen bg-transparent lg:bg-gray-50 flex relative z-10 ${currentPage !== 'home' ? 'has-mobile-nav' : ''}`}>
        {/* Sidebar - Desktop */}
      <div className="hidden lg:flex flex-col w-56 bg-[#0B0C3F] border-r border-cyan-900/30 fixed h-full z-50 shadow-xl">
        {/* Logo */}
        <div className="p-4 flex justify-center border-b border-cyan-900/30">
          <img 
            src="/images/logo-ristorantissimo.png" 
            alt="Ristorantissimo Logo" 
            className="w-64 h-auto object-contain"
          />
        </div>

        {/* Menu Items */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="space-y-1 px-3">
            {[
              { id: 'home', icon: Home, label: 'Home' },
              { id: 'dashboard', icon: BarChart3, label: 'Dashboard' },
              { id: 'analytics', icon: PieChart, label: 'Analytics' },
              { id: 'reports', icon: FileText, label: 'Report' },
              { id: 'fatture', icon: FileSearch, label: 'Fatture' },
              { id: 'spese', icon: Receipt, label: 'Spese' },
              { id: 'mance', icon: Coins, label: 'Mance' },
              { id: 'menu', icon: ShoppingCart, label: 'Menù' },
              { id: 'ordini', icon: Package, label: 'Ordini' },
              { id: 'ddt', icon: ClipboardList, label: 'DDT' },
              { id: 'fbcost', icon: DollarSign, label: 'F&B Cost' },
              { id: 'haccp', icon: ShieldCheck, label: 'HACCP' },
              { id: 'cassetto-fiscale', icon: FileText, label: 'Cassetto Fiscale' },
              { id: 'sdi', icon: Shield, label: 'SdI' },
            ].map((item) => {
              const canAccess = item.id === 'home' || hasAccess(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => canAccess && handlePageChange(item.id)}
                  disabled={!canAccess}
                  className={`w-full flex items-center gap-3 py-3 px-4 rounded-xl transition-colors duration-150 ${
                    !canAccess
                      ? 'opacity-40 cursor-not-allowed text-slate-600'
                      : currentPage === item.id 
                        ? 'bg-cyan-400/15 text-cyan-300 border border-cyan-400/40' 
                        : 'text-slate-300 hover:bg-cyan-400/10 hover:text-cyan-200'
                  }`}
                  title={!canAccess ? 'Non hai accesso a questa sezione' : item.label}
                >
                  <item.icon className={`h-5 w-5 ${currentPage === item.id ? 'text-cyan-400' : ''}`} />
                  <span className="text-sm font-medium tracking-wide">{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Settings & Logout */}
        <div className="border-t border-cyan-900/30 p-3 space-y-2">
          <button
            onClick={() => handlePageChange('settings')}
            className={`w-full flex items-center gap-3 py-3 px-4 rounded-xl transition-colors duration-150 ${
              currentPage === 'settings' 
                ? 'bg-cyan-400/15 text-cyan-300 border border-cyan-400/40' 
                : 'text-slate-300 hover:bg-cyan-400/10 hover:text-cyan-200'
            }`}
          >
            <Settings className={`h-5 w-5 ${currentPage === 'settings' ? 'text-cyan-400' : ''}`} />
            <span className="text-sm font-medium tracking-wide">Impostazioni</span>
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 py-3 px-4 rounded-xl text-slate-400 hover:bg-red-500/15 hover:text-red-400 transition-colors duration-150"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm font-medium tracking-wide">Esci</span>
          </button>
        </div>
      </div>

      {/* Mobile Header - Dark Style - Nascosto su Home */}
      {currentPage !== 'home' && (
        <>
          {/* Mobile Header - transparent, starts BELOW safe area */}
          <div className="lg:hidden fixed left-0 right-0 z-50 px-4 py-2" style={{ 
            top: 'max(0px, calc(env(safe-area-inset-top, 0px) - 24px))',
          }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setCurrentPage('home')} 
                  className="w-10 h-10 bg-black/30 backdrop-blur-sm rounded-xl flex items-center justify-center active:scale-95 transition-transform"
                >
                  <ArrowLeft className="h-5 w-5 text-white" />
                </button>
                <h1 className="text-white text-lg font-bold">
                  {currentPage === 'dashboard' && 'Dashboard'}
                  {currentPage === 'fatture' && 'Fatture'}
                  {currentPage === 'fornitori' && 'Fornitori'}
                  {currentPage === 'ordini' && 'Ordini'}
                  {currentPage === 'bolle' && 'Gestione Bolle'}
                  {currentPage === 'resi' && 'Resi Fornitore'}
                  {currentPage === 'ddt' && 'DDT'}
                  {currentPage === 'fbcost' && 'F&B Cost'}
                  {currentPage === 'haccp' && 'HACCP'}
                  {currentPage === 'sdi' && 'SdI Config'}
                  {currentPage === 'cassetto-fiscale' && 'Cassetto Fiscale'}
                  {currentPage === 'lista-spesa' && 'Lista Spesa'}
                  {currentPage === 'team' && 'Team'}
                  {currentPage === 'locali' && 'Locali'}
                  {currentPage === 'settings' && 'Impostazioni'}
                  {currentPage === 'analytics' && 'Analytics'}
                  {currentPage === 'reports' && 'Report'}
                  {currentPage === 'mance' && 'Mance'}
                  {currentPage === 'spese' && 'Spese'}
                  {currentPage === 'menu' && 'Menù'}
                  {currentPage === 'importazioni' && 'Importazioni'}
                  {currentPage === 'saas-admin' && 'Admin SaaS'}
                </h1>
              </div>
              <button 
                onClick={handleLogout} 
                className="w-10 h-10 bg-black/30 backdrop-blur-sm rounded-xl flex items-center justify-center active:scale-95 transition-transform"
              >
                <LogOut className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className={`lg:ml-56 flex-1 min-w-0 overflow-x-hidden relative z-10 ${currentPage !== 'home' ? 'pt-20 lg:pt-0' : 'pt-0'}`}>
        {/* Top Bar - Desktop */}
        <div className="hidden lg:flex bg-gradient-to-r from-[#0B0C3F] via-[#1F2646] to-[#0B0C3F] px-8 py-5 justify-between items-center shadow-lg border-b border-cyan-900/30">
          <div>
            <h2 className="text-xl font-bold text-white">
              {currentPage === 'home' && 'Home'}
              {currentPage === 'dashboard' && 'Dashboard'}
              {currentPage === 'fatture' && 'Fatture Elettroniche'}
              {currentPage === 'fornitori' && 'Fornitori'}
              {currentPage === 'ordini' && 'Ordini'}
              {currentPage === 'bolle' && 'Gestione Bolle'}
              {currentPage === 'ddt' && 'DDT'}
              {currentPage === 'fbcost' && 'F&B Cost'}
              {currentPage === 'haccp' && 'HACCP'}
              {currentPage === 'listaspesa' && 'Lista della spesa'}
              {currentPage === 'team' && 'Team'}
              {currentPage === 'locali' && 'Locali e reparti'}
              {currentPage === 'settings' && 'Impostazioni'}
              {currentPage === 'analytics' && 'Analytics'}
              {currentPage === 'reports' && 'Report'}
              {currentPage === 'mance' && 'Mance'}
              {currentPage === 'spese' && 'Spese'}
              {currentPage === 'menu' && 'Menù'}
              {currentPage === 'importazioni' && 'Importazioni'}
              {currentPage === 'saas-admin' && 'Admin SaaS'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/80">
              {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
            <span className="px-3 py-1.5 bg-white/20 backdrop-blur text-white rounded-full text-sm font-medium">
              Benvenuto, {user.nome_completo || user.username}
            </span>
          </div>
        </div>

        {/* Content Area - background scrolls with content on mobile */}
        <div className={`${currentPage === 'home' ? '' : 'px-4 pt-2 pb-24 lg:p-8 lg:pb-8 bg-transparent lg:bg-transparent min-h-screen lg:min-h-0 overflow-x-hidden'}`}>
          {/* Home sempre visibile come default */}
          {currentPage === 'home' && <HomePage onNavigate={handlePageChange} user={user} hasAccess={hasAccess} />}
          
          {/* Componenti pesanti: usa display:none per mantenerli in memoria dopo prima visita */}
          {(currentPage === 'dashboard' || visitedPages.current.has('dashboard')) && (
            <div style={{ display: currentPage === 'dashboard' ? 'block' : 'none' }}>
              <Dashboard />
            </div>
          )}
          {(currentPage === 'analytics' || visitedPages.current.has('analytics')) && (
            <div style={{ display: currentPage === 'analytics' ? 'block' : 'none' }}>
              <AnalyticsPage />
            </div>
          )}
          {(currentPage === 'reports' || visitedPages.current.has('reports')) && (
            <div style={{ display: currentPage === 'reports' ? 'block' : 'none' }}>
              <ReportsPage />
            </div>
          )}
          {(currentPage === 'fatture' || visitedPages.current.has('fatture')) && (
            <div style={{ display: currentPage === 'fatture' ? 'block' : 'none' }}>
              <FattureElettronichePage />
            </div>
          )}
          {(currentPage === 'spese' || visitedPages.current.has('spese')) && (
            <div style={{ display: currentPage === 'spese' ? 'block' : 'none' }}>
              <SpeseReportPage />
            </div>
          )}
          {(currentPage === 'mance' || visitedPages.current.has('mance')) && (
            <div style={{ display: currentPage === 'mance' ? 'block' : 'none' }}>
              <ManceReportPage />
            </div>
          )}
          {(currentPage === 'menu' || visitedPages.current.has('menu')) && (
            <div style={{ display: currentPage === 'menu' ? 'block' : 'none' }}>
              <MenuPage />
            </div>
          )}
          {(currentPage === 'importazioni' || visitedPages.current.has('importazioni')) && (
            <div style={{ display: currentPage === 'importazioni' ? 'block' : 'none' }}>
              <ImportazioniPage />
            </div>
          )}
          {(currentPage === 'settings' || visitedPages.current.has('settings')) && (
            <div style={{ display: currentPage === 'settings' ? 'block' : 'none' }}>
              <SettingsPage onBack={() => handlePageChange('home')} />
            </div>
          )}
          
          {/* Pagine placeholder leggere: usa conditional rendering */}
          {currentPage === 'ddt' && <DDTPage />}
          {currentPage === 'fbcost' && <FBCostPage />}
          {currentPage === 'haccp' && <HACCPPage />}
          {currentPage === 'sdi' && <SDIConfigPage />}
          {currentPage === 'cassetto-fiscale' && <CassettoFiscalePage />}
          {currentPage === 'ordini' && <OrdiniPage />}
          {currentPage === 'bolle' && <GestioneBollePage />}
          {currentPage === 'resi' && <ResiFornitorePage />}
          {currentPage === 'fornitori' && <PlaceholderPage title="Fornitori" icon={Truck} description="Gestisci i fornitori." />}
          {currentPage === 'lista-spesa' && <PlaceholderPage title="Lista della spesa" icon={ListTodo} description="Lista della spesa." />}
          {currentPage === 'team' && <TeamPage />}
          {currentPage === 'locali' && <PlaceholderPage title="Locali" icon={Store} description="Gestisci i locali." />}
          {currentPage === 'saas-admin' && <SaaSAdminPage />}
        </div>
      </div>

      {/* Mobile Bottom Navigation - Glassmorphism at very bottom */}
      {currentPage !== 'home' && (
        <div className="lg:hidden fixed left-0 right-0 bottom-0 z-50 px-3 pb-1">
          <div className="flex items-center justify-around py-3 rounded-2xl bg-white/10 backdrop-blur-2xl border border-white/20">
            {[
              { id: 'home', icon: Home, label: 'Home' },
              { id: 'dashboard', icon: BarChart3, label: 'Stats' },
              { id: 'fatture', icon: FileText, label: 'Fatture' },
              { id: 'fbcost', icon: DollarSign, label: 'F&B' },
              { id: 'settings', icon: Settings, label: 'Altro' },
            ].map((item) => {
              const canAccess = item.id === 'home' || item.id === 'settings' || hasAccess(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => canAccess && setCurrentPage(item.id)}
                  disabled={!canAccess}
                  className={`flex flex-col items-center px-3 py-1 rounded-xl ${
                    !canAccess
                      ? 'opacity-40 cursor-not-allowed text-slate-600'
                      : currentPage === item.id 
                        ? 'bg-white/20 text-white' 
                        : 'text-slate-400'
                  }`}
                >
                  <item.icon className="h-5 w-5" strokeWidth={currentPage === item.id ? 2 : 1.5} />
                  <span className="text-[10px] font-medium mt-0.5">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Toaster position="top-center" />
      
      {/* Prompt per attivare le notifiche push */}
      <NotificationPrompt 
        show={showNotificationPrompt && user} 
        onClose={() => setShowNotificationPrompt(false)} 
      />
    </div>
    </>
  );
}

export default App;